/**
 * GENERATED by tools/build-art.py from tools/assets.toml (and the paintings
 * from content/valley.toml). Do not edit --
 * change the manifest and re-run the build, and this file follows.
 *
 * Code asks for art by a STABLE name -- a balance stage, a sheet id --
 * and this table says which versioned file that currently is. That is
 * what lets a painting or a sheet be replaced without touching code.
 *
 * Every sheet was re-packed by the build onto square cells of `cell`
 * pixels, so frame (column, row) is always exactly at
 *
 *     background-size:     columns * 100%   rows * 100%
 *     background-position: column / (columns - 1)   row / (rows - 1)
 *
 * with nothing measured at runtime.
 */

export interface PaintingSet {
  day: string;
  night: string;
  portraitDay: string;
  portraitNight: string;
}

export interface SheetInfo {
  /** Base name; the app serves `.avif` and `.webp` of it. */
  file: string;
  columns: number;
  rows: number;
  sequenceRows: number;
  frameCounts: Readonly<Record<string, number>>;
  /** Pixels per square cell in the built sheet. */
  cell: number;
  /** What each row is, in order. */
  names: readonly string[];
}

/** Balance stages, emptiest first, with the paintings each one shows (the default scene). */
export const PAINTINGS = {
  "barren": {
    day: "valley-barren-v13",
    night: "valley-night-barren-v13",
    portraitDay: "valley-portrait-barren-v13",
    portraitNight: "valley-portrait-night-barren-v13",
  },
  "recovering": {
    day: "valley-recovering-v13",
    night: "valley-night-recovering-v13",
    portraitDay: "valley-portrait-recovering-v13",
    portraitNight: "valley-portrait-night-recovering-v13",
  },
  "growing": {
    day: "valley-growing-v13",
    night: "valley-night-growing-v13",
    portraitDay: "valley-portrait-growing-v13",
    portraitNight: "valley-portrait-night-growing-v13",
  },
  "thriving": {
    day: "valley-thriving-v13",
    night: "valley-night-thriving-v13",
    portraitDay: "valley-portrait-thriving-v13",
    portraitNight: "valley-portrait-night-thriving-v13",
  },
  "plentiful": {
    day: "valley-plentiful-v18",
    night: "valley-night-plentiful-v13",
    portraitDay: "valley-portrait-plentiful-v18",
    portraitNight: "valley-portrait-night-plentiful-v13",
  },
} as const satisfies Record<string, PaintingSet>;

export type StageName = keyof typeof PAINTINGS;

/** Every background ([[scene]] in content/valley.toml), each with all its stages. */
export const SCENE_PAINTINGS: Readonly<Record<string, Readonly<Record<StageName, PaintingSet>>>> = {
  "meadow": PAINTINGS,
  "cottage": {
    "barren": {
      day: "cottage-barren-v2",
      night: "cottage-night-barren-v2",
      portraitDay: "cottage-portrait-barren-v2",
      portraitNight: "cottage-portrait-night-barren-v2",
    },
    "recovering": {
      day: "cottage-recovering-v2",
      night: "cottage-night-recovering-v2",
      portraitDay: "cottage-portrait-recovering-v2",
      portraitNight: "cottage-portrait-night-recovering-v2",
    },
    "growing": {
      day: "cottage-growing-v2",
      night: "cottage-night-growing-v2",
      portraitDay: "cottage-portrait-growing-v2",
      portraitNight: "cottage-portrait-night-growing-v2",
    },
    "thriving": {
      day: "cottage-thriving-v2",
      night: "cottage-night-thriving-v2",
      portraitDay: "cottage-portrait-thriving-v2",
      portraitNight: "cottage-portrait-night-thriving-v2",
    },
    "plentiful": {
      day: "cottage-plentiful-v2",
      night: "cottage-night-plentiful-v2",
      portraitDay: "cottage-portrait-plentiful-v2",
      portraitNight: "cottage-portrait-night-plentiful-v2",
    },
  },
};

/** Every sprite sheet, by the id code uses to ask for it. */
export const SHEETS = {
  "celestial": {
    file: "celestial-v21",
    columns: 6,
    rows: 2,
    sequenceRows: 1,
    frameCounts: {},
    cell: 256,
    names: ["sun", "moon"],
  },
  "woodland-actions": {
    file: "woodland-actions-v13",
    columns: 6,
    rows: 8,
    sequenceRows: 2,
    frameCounts: {"grazing": 10},
    cell: 256,
    names: ["deer", "deer-cont", "grazing", "grazing-cont", "fox", "fox-cont", "raccoon", "raccoon-cont"],
  },
  "habitat-actions": {
    file: "habitat-actions-v13",
    columns: 12,
    rows: 4,
    sequenceRows: 1,
    frameCounts: {},
    cell: 256,
    names: ["squirrel", "duck", "frog", "butterfly"],
  },
  "meadow-flight": {
    file: "meadow-flight-v13",
    columns: 12,
    rows: 2,
    sequenceRows: 1,
    frameCounts: {},
    cell: 256,
    names: ["rabbit", "bird"],
  },
  "night-moves": {
    file: "night-moves-v13",
    columns: 12,
    rows: 4,
    sequenceRows: 1,
    frameCounts: {},
    cell: 256,
    names: ["owl", "bat", "hedgehog", "moth"],
  },
  "turn-squirrel": {
    file: "turn-squirrel-v21",
    columns: 4,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["squirrel", "squirrel-cont"],
  },
  "turn-woodland": {
    file: "turn-woodland-v13",
    columns: 8,
    rows: 3,
    sequenceRows: 1,
    frameCounts: {},
    cell: 256,
    names: ["deer", "fox", "raccoon"],
  },
  "turn-habitat": {
    file: "turn-habitat-v13",
    columns: 8,
    rows: 4,
    sequenceRows: 1,
    frameCounts: {},
    cell: 256,
    names: ["squirrel", "duck", "frog", "butterfly"],
  },
  "turn-meadow": {
    file: "turn-meadow-v13",
    columns: 8,
    rows: 2,
    sequenceRows: 1,
    frameCounts: {},
    cell: 256,
    names: ["rabbit", "bird"],
  },
  "turn-night": {
    file: "turn-night-v13",
    columns: 8,
    rows: 4,
    sequenceRows: 1,
    frameCounts: {},
    cell: 256,
    names: ["owl", "bat", "hedgehog", "moth"],
  },
  "idle-woodland": {
    file: "idle-woodland-v6",
    columns: 10,
    rows: 4,
    sequenceRows: 1,
    frameCounts: {},
    cell: 256,
    names: ["deer", "fox", "raccoon", "rabbit"],
  },
  "idle-habitat": {
    file: "idle-habitat-v6",
    columns: 10,
    rows: 4,
    sequenceRows: 1,
    frameCounts: {},
    cell: 256,
    names: ["squirrel", "duck", "frog", "butterfly"],
  },
  "idle-night": {
    file: "idle-night-v6",
    columns: 10,
    rows: 5,
    sequenceRows: 1,
    frameCounts: {},
    cell: 256,
    names: ["bird", "owl", "bat", "hedgehog", "moth"],
  },
  "travel-deer": {
    file: "travel-deer-v20",
    columns: 4,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["deer", "deer-cont"],
  },
  "travel-raccoon": {
    file: "travel-raccoon-v20",
    columns: 4,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["raccoon", "raccoon-cont"],
  },
  "travel-squirrel": {
    file: "travel-squirrel-v19",
    columns: 4,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["squirrel", "squirrel-cont"],
  },
  "travel-hedgehog": {
    file: "travel-hedgehog-v20",
    columns: 4,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["hedgehog", "hedgehog-cont"],
  },
  "idle-deer": {
    file: "idle-deer-v21",
    columns: 5,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["deer", "deer-cont"],
  },
  "turn-deer": {
    file: "turn-deer-v21",
    columns: 4,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["deer", "deer-cont"],
  },
  "idle-fox": {
    file: "idle-fox-v21",
    columns: 5,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["fox", "fox-cont"],
  },
  "turn-fox": {
    file: "turn-fox-v21",
    columns: 4,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["fox", "fox-cont"],
  },
  "idle-raccoon": {
    file: "idle-raccoon-v21",
    columns: 5,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["raccoon", "raccoon-cont"],
  },
  "turn-raccoon": {
    file: "turn-raccoon-v21",
    columns: 4,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["raccoon", "raccoon-cont"],
  },
  "idle-squirrel": {
    file: "idle-squirrel-v21",
    columns: 5,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["squirrel", "squirrel-cont"],
  },
  "idle-duck": {
    file: "idle-duck-v21",
    columns: 5,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["duck", "duck-cont"],
  },
  "turn-duck": {
    file: "turn-duck-v21",
    columns: 4,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["duck", "duck-cont"],
  },
  "idle-frog": {
    file: "idle-frog-v21",
    columns: 5,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["frog", "frog-cont"],
  },
  "turn-frog": {
    file: "turn-frog-v21",
    columns: 4,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["frog", "frog-cont"],
  },
  "idle-butterfly": {
    file: "idle-butterfly-v26",
    columns: 5,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["butterfly", "butterfly-cont"],
  },
  "turn-butterfly": {
    file: "turn-butterfly-v26",
    columns: 8,
    rows: 4,
    sequenceRows: 4,
    frameCounts: {},
    cell: 256,
    names: ["butterfly", "butterfly-cont", "butterfly-cont2", "butterfly-cont3"],
  },
  "idle-rabbit": {
    file: "idle-rabbit-v21",
    columns: 5,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["rabbit", "rabbit-cont"],
  },
  "turn-rabbit": {
    file: "turn-rabbit-v21",
    columns: 4,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["rabbit", "rabbit-cont"],
  },
  "idle-bird": {
    file: "idle-bird-v26",
    columns: 5,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["bird", "bird-cont"],
  },
  "turn-bird": {
    file: "turn-bird-v26",
    columns: 8,
    rows: 4,
    sequenceRows: 4,
    frameCounts: {},
    cell: 256,
    names: ["bird", "bird-cont", "bird-cont2", "bird-cont3"],
  },
  "idle-owl": {
    file: "idle-owl-v26",
    columns: 5,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["owl", "owl-cont"],
  },
  "turn-owl": {
    file: "turn-owl-v26",
    columns: 8,
    rows: 4,
    sequenceRows: 4,
    frameCounts: {},
    cell: 256,
    names: ["owl", "owl-cont", "owl-cont2", "owl-cont3"],
  },
  "idle-bat": {
    file: "idle-bat-v21",
    columns: 5,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["bat", "bat-cont"],
  },
  "turn-bat": {
    file: "turn-bat-v26",
    columns: 8,
    rows: 4,
    sequenceRows: 4,
    frameCounts: {},
    cell: 256,
    names: ["bat", "bat-cont", "bat-cont2", "bat-cont3"],
  },
  "idle-hedgehog": {
    file: "idle-hedgehog-v21",
    columns: 5,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["hedgehog", "hedgehog-cont"],
  },
  "turn-hedgehog": {
    file: "turn-hedgehog-v21",
    columns: 4,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["hedgehog", "hedgehog-cont"],
  },
  "idle-moth": {
    file: "idle-moth-v26",
    columns: 5,
    rows: 2,
    sequenceRows: 2,
    frameCounts: {},
    cell: 256,
    names: ["moth", "moth-cont"],
  },
  "turn-moth": {
    file: "turn-moth-v26",
    columns: 8,
    rows: 4,
    sequenceRows: 4,
    frameCounts: {},
    cell: 256,
    names: ["moth", "moth-cont", "moth-cont2", "moth-cont3"],
  },
} as const satisfies Record<string, SheetInfo>;

export type SheetId = keyof typeof SHEETS;

/** Single pictures, by the id code asks for them with. */
export const PICTURES = {
  "builder-grove": "builder-grove-v6",
} as const;

/**
 * The painted characters, one entry per activity, split into layers by
 * tools/art_characters.py. `meta` is that activity's own activity.json;
 * `frames` holds, per outfit, the layer names of each held frame; `hair`
 * the painted hair per style. Every name is looked up through ASSET_SRC.
 */
export interface CharacterArt {
  meta: Record<string, unknown>;
  width: number;
  height: number;
  frames: Readonly<Record<string, ReadonlyArray<Readonly<Record<string, string | null>>>>>;
  hair: Readonly<Record<string, string>>;
  roles?: Readonly<Record<string, { frames: CharacterArt["frames"] }>>;
  sharedFrames?: readonly string[];
  bounds?: Readonly<Record<string, readonly [number, number, number, number]>>;
  atlases?: Readonly<Record<string, readonly [number, number, number, number, number, number]>>;
}

export const CHARACTER_ART: Readonly<Record<string, CharacterArt>> = {
  "animals": {
    "frames": {
      "0": [
        {
          "brows": "act-animals-tee-shorts-a-brows",
          "garment": "act-animals-tee-shorts-a-garment",
          "props": "act-animals-tee-shorts-a-props",
          "skin": "act-animals-tee-shorts-a-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-shorts-b-brows",
          "garment": "act-animals-tee-shorts-b-garment",
          "props": "act-animals-tee-shorts-b-props",
          "skin": "act-animals-tee-shorts-b-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-shorts-c-brows",
          "garment": "act-animals-tee-shorts-c-garment",
          "props": "act-animals-tee-shorts-c-props",
          "skin": "act-animals-tee-shorts-c-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-shorts-d-brows",
          "garment": "act-animals-tee-shorts-d-garment",
          "props": "act-animals-tee-shorts-d-props",
          "skin": "act-animals-tee-shorts-d-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-shorts-e-brows",
          "garment": "act-animals-tee-shorts-e-garment",
          "props": "act-animals-tee-shorts-e-props",
          "skin": "act-animals-tee-shorts-e-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-shorts-f-brows",
          "garment": "act-animals-tee-shorts-f-garment",
          "props": "act-animals-tee-shorts-f-props",
          "skin": "act-animals-tee-shorts-f-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-shorts-g-brows",
          "garment": "act-animals-tee-shorts-g-garment",
          "props": "act-animals-tee-shorts-g-props",
          "skin": "act-animals-tee-shorts-g-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-shorts-h-brows",
          "garment": "act-animals-tee-shorts-h-garment",
          "props": "act-animals-tee-shorts-h-props",
          "skin": "act-animals-tee-shorts-h-skin",
          "tongue": null
        }
      ],
      "1": [
        {
          "brows": "act-animals-tee-jeans-a-brows",
          "garment": "act-animals-tee-jeans-a-garment",
          "props": "act-animals-tee-jeans-a-props",
          "skin": "act-animals-tee-jeans-a-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-jeans-b-brows",
          "garment": "act-animals-tee-jeans-b-garment",
          "props": "act-animals-tee-jeans-b-props",
          "skin": "act-animals-tee-jeans-b-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-jeans-c-brows",
          "garment": "act-animals-tee-jeans-c-garment",
          "props": "act-animals-tee-jeans-c-props",
          "skin": "act-animals-tee-jeans-c-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-jeans-d-brows",
          "garment": "act-animals-tee-jeans-d-garment",
          "props": "act-animals-tee-jeans-d-props",
          "skin": "act-animals-tee-jeans-d-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-jeans-e-brows",
          "garment": "act-animals-tee-jeans-e-garment",
          "props": "act-animals-tee-jeans-e-props",
          "skin": "act-animals-tee-jeans-e-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-jeans-f-brows",
          "garment": "act-animals-tee-jeans-f-garment",
          "props": "act-animals-tee-jeans-f-props",
          "skin": "act-animals-tee-jeans-f-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-jeans-g-brows",
          "garment": "act-animals-tee-jeans-g-garment",
          "props": "act-animals-tee-jeans-g-props",
          "skin": "act-animals-tee-jeans-g-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-tee-jeans-h-brows",
          "garment": "act-animals-tee-jeans-h-garment",
          "props": "act-animals-tee-jeans-h-props",
          "skin": "act-animals-tee-jeans-h-skin",
          "tongue": null
        }
      ],
      "2": [
        {
          "brows": "act-animals-dress-a-brows",
          "garment": "act-animals-dress-a-garment",
          "props": "act-animals-dress-a-props",
          "skin": "act-animals-dress-a-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-dress-b-brows",
          "garment": "act-animals-dress-b-garment",
          "props": "act-animals-dress-b-props",
          "skin": "act-animals-dress-b-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-dress-c-brows",
          "garment": "act-animals-dress-c-garment",
          "props": "act-animals-dress-c-props",
          "skin": "act-animals-dress-c-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-dress-d-brows",
          "garment": "act-animals-dress-d-garment",
          "props": "act-animals-dress-d-props",
          "skin": "act-animals-dress-d-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-dress-e-brows",
          "garment": "act-animals-dress-e-garment",
          "props": "act-animals-dress-e-props",
          "skin": "act-animals-dress-e-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-dress-f-brows",
          "garment": "act-animals-dress-f-garment",
          "props": "act-animals-dress-f-props",
          "skin": "act-animals-dress-f-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-dress-g-brows",
          "garment": "act-animals-dress-g-garment",
          "props": "act-animals-dress-g-props",
          "skin": "act-animals-dress-g-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-dress-h-brows",
          "garment": "act-animals-dress-h-garment",
          "props": "act-animals-dress-h-props",
          "skin": "act-animals-dress-h-skin",
          "tongue": null
        }
      ],
      "3": [
        {
          "brows": "act-animals-shirt-skirt-a-brows",
          "garment": "act-animals-shirt-skirt-a-garment",
          "props": "act-animals-shirt-skirt-a-props",
          "skin": "act-animals-shirt-skirt-a-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-shirt-skirt-b-brows",
          "garment": "act-animals-shirt-skirt-b-garment",
          "props": "act-animals-shirt-skirt-b-props",
          "skin": "act-animals-shirt-skirt-b-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-shirt-skirt-c-brows",
          "garment": "act-animals-shirt-skirt-c-garment",
          "props": "act-animals-shirt-skirt-c-props",
          "skin": "act-animals-shirt-skirt-c-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-shirt-skirt-d-brows",
          "garment": "act-animals-shirt-skirt-d-garment",
          "props": "act-animals-shirt-skirt-d-props",
          "skin": "act-animals-shirt-skirt-d-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-shirt-skirt-e-brows",
          "garment": "act-animals-shirt-skirt-e-garment",
          "props": "act-animals-shirt-skirt-e-props",
          "skin": "act-animals-shirt-skirt-e-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-shirt-skirt-f-brows",
          "garment": "act-animals-shirt-skirt-f-garment",
          "props": "act-animals-shirt-skirt-f-props",
          "skin": "act-animals-shirt-skirt-f-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-shirt-skirt-g-brows",
          "garment": "act-animals-shirt-skirt-g-garment",
          "props": "act-animals-shirt-skirt-g-props",
          "skin": "act-animals-shirt-skirt-g-skin",
          "tongue": null
        },
        {
          "brows": "act-animals-shirt-skirt-h-brows",
          "garment": "act-animals-shirt-skirt-h-garment",
          "props": "act-animals-shirt-skirt-h-props",
          "skin": "act-animals-shirt-skirt-h-skin",
          "tongue": null
        }
      ]
    },
    "hair": {
      "0": "act-animals-hair-0",
      "1": "act-animals-hair-1",
      "2": "act-animals-hair-2",
      "3": "act-animals-hair-3",
      "4": "act-animals-hair-4",
      "5": "act-animals-hair-5",
      "6": "act-animals-hair-6",
      "7": "act-animals-hair-7",
      "8": "act-animals-hair-8",
      "9": "act-animals-hair-9"
    },
    "height": 683,
    "meta": {
      "availableOutfits": [
        0,
        1,
        2,
        3
      ],
      "bounds": [
        300,
        25,
        830,
        915
      ],
      "camera": "quarter",
      "canvas": [
        1536,
        1024
      ],
      "expression": "content",
      "eyeScale": [
        1.2,
        1.2
      ],
      "eyes": [
        [
          0.18697729954509218,
          0.6809138329455163
        ],
        [
          0.5397892498006746,
          0.6799515213667959
        ]
      ],
      "frames": 8,
      "front": [
        "wildlife",
        "grass"
      ],
      "gaze": [
        -2.1,
        2.4
      ],
      "ground": 0.88,
      "hairBounds": {
        "0": [
          615.1176470588235,
          -5.176470588235304,
          1043.764705882353,
          359.2352941176471
        ],
        "1": [
          653.4117647058823,
          -17.529411764705884,
          1017.8235294117648,
          259.1764705882353
        ],
        "2": [
          643.5294117647059,
          -13.823529411764724,
          1043.764705882353,
          351.82352941176475
        ],
        "3": [
          587.9411764705883,
          -5.176470588235304,
          1094.4117647058824,
          613.7058823529412
        ],
        "4": [
          637.3529411764706,
          -22.470588235294116,
          1143.8235294117646,
          422.2352941176471
        ],
        "5": [
          670.7058823529412,
          -7.64705882352942,
          1005.4705882352941,
          260.4117647058824
        ],
        "6": [
          629.9411764705883,
          -10.117647058823536,
          1059.8235294117646,
          406.1764705882353
        ],
        "7": [
          639.8235294117646,
          4.70588235294116,
          1010.4117647058823,
          464.2352941176471
        ],
        "8": [
          517.5294117647059,
          -12.588235294117652,
          1146.2941176470588,
          656.9411764705883
        ],
        "9": [
          563.2352941176471,
          -13.823529411764724,
          1110.4705882352941,
          414.82352941176475
        ]
      },
      "hairFit": {
        "fromX": 0.5260416666666666,
        "fromY": 0.1904296875,
        "scale": 1.2352941176470589
      },
      "hairReady": false,
      "hairStyles": [
        0,
        1,
        2,
        3,
        4,
        5,
        6,
        7,
        8,
        9
      ],
      "head": {
        "angle": 10,
        "scale": 0.205078125,
        "x": 0.5436197916666666,
        "y": 0.185546875
      },
      "hold": [
        1.6,
        2.6
      ],
      "id": "animals",
      "interaction": "stroke-rabbit",
      "motionRoot": "avatar-motion-v25",
      "outfitFits": {
        "0": {
          "bounds": [
            300,
            25,
            830,
            922
          ],
          "ear": [
            0.6184895833333334,
            0.3955078125,
            0.020182291666666668,
            0.0390625
          ],
          "eyeScales": [
            [
              1.1710243052829417,
              1.0414247774255412
            ],
            [
              1.4765089066611006,
              1.1455672551680953
            ]
          ],
          "eyes": [
            [
              0.16894812585271785,
              0.7432564723922357
            ],
            [
              0.46602742517700807,
              0.7435289180229604
            ]
          ],
          "ground": 0.9130859375,
          "hairBounds": {
            "0": [
              540.1861149315944,
              78.97732786277919,
              1072.6891396723827,
              586.5619687704157
            ],
            "1": [
              568.2788669855003,
              78.77891487035185,
              1008.8246973082936,
              482.5917910397941
            ],
            "2": [
              561.2534977319883,
              71.3412452583587,
              1069.658948739358,
              567.7830855454748
            ],
            "3": [
              516.4522380250442,
              58.06678814178758,
              1226.8223489993086,
              820.5683255143722
            ],
            "4": [
              552.1141281058638,
              22.39409637588176,
              1187.043061410513,
              631.9506220514716
            ],
            "5": [
              587.662593704837,
              92.60599805752724,
              998.3978887646277,
              476.1799902408199
            ],
            "6": [
              550.9916907184922,
              67.98368095777852,
              1106.7282359451635,
              621.1363213044317
            ],
            "7": [
              566.0427171708993,
              101.47463483670717,
              1088.7227917019136,
              667.560162371439
            ],
            "8": [
              451.74965770578757,
              30.101021595937596,
              1290.8590111031751,
              888.8242634033222
            ],
            "9": [
              491.1306795989988,
              43.80053440632099,
              1154.7048586910978,
              657.5803753270842
            ]
          },
          "hairFit": {
            "fromX": 0.5260416666666666,
            "fromY": 0.1904296875,
            "rotation": -24.363586089683306,
            "scale": 1.2042045005921833,
            "targetX": 0.5327159361017897,
            "targetY": 0.33547543519295303
          },
          "head": {
            "angle": -14.363586089683306,
            "scale": 0.19991676279362416,
            "x": 0.5327159361017897,
            "y": 0.33547543519295303
          },
          "pettingContact": [
            582,
            524,
            837,
            678
          ],
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.1710243052829417,
                  1.0414247774255412
                ],
                [
                  1.4765089066611006,
                  1.1455672551680953
                ]
              ],
              "eyes": [
                [
                  0.16894812585271785,
                  0.7432564723922357
                ],
                [
                  0.46602742517700807,
                  0.7435289180229604
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -24.363586089683306,
                "scale": 1.2042045005921833,
                "targetX": 0.5327159361017897,
                "targetY": 0.33547543519295303
              },
              "head": {
                "angle": -14.363586089683306,
                "scale": 0.19991676279362416,
                "x": 0.5327159361017897,
                "y": 0.33547543519295303
              }
            },
            {
              "eyeScales": [
                [
                  1.1710243052829417,
                  1.0414247774255412
                ],
                [
                  1.4765089066611006,
                  1.1455672551680953
                ]
              ],
              "eyes": [
                [
                  0.16894812585271785,
                  0.7432564723922357
                ],
                [
                  0.46602742517700807,
                  0.7435289180229604
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -24.363586089683306,
                "scale": 1.2042045005921833,
                "targetX": 0.5327159361017897,
                "targetY": 0.33547543519295303
              },
              "head": {
                "angle": -14.363586089683306,
                "scale": 0.19991676279362416,
                "x": 0.5327159361017897,
                "y": 0.33547543519295303
              }
            },
            {
              "eyeScales": [
                [
                  1.1710243052829417,
                  1.0414247774255412
                ],
                [
                  1.4765089066611006,
                  1.1455672551680953
                ]
              ],
              "eyes": [
                [
                  0.16894812585271785,
                  0.7432564723922357
                ],
                [
                  0.46602742517700807,
                  0.7435289180229604
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -24.363586089683306,
                "scale": 1.2042045005921833,
                "targetX": 0.5327159361017897,
                "targetY": 0.33547543519295303
              },
              "head": {
                "angle": -14.363586089683306,
                "scale": 0.19991676279362416,
                "x": 0.5327159361017897,
                "y": 0.33547543519295303
              }
            },
            {
              "eyeScales": [
                [
                  1.1710243052829417,
                  1.0414247774255412
                ],
                [
                  1.4765089066611006,
                  1.1455672551680953
                ]
              ],
              "eyes": [
                [
                  0.16894812585271785,
                  0.7432564723922357
                ],
                [
                  0.46602742517700807,
                  0.7435289180229604
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -24.363586089683306,
                "scale": 1.2042045005921833,
                "targetX": 0.5327159361017897,
                "targetY": 0.33547543519295303
              },
              "head": {
                "angle": -14.363586089683306,
                "scale": 0.19991676279362416,
                "x": 0.5327159361017897,
                "y": 0.33547543519295303
              }
            },
            {
              "eyeScales": [
                [
                  1.1710243052829417,
                  1.0414247774255412
                ],
                [
                  1.4765089066611006,
                  1.1455672551680953
                ]
              ],
              "eyes": [
                [
                  0.16894812585271785,
                  0.7432564723922357
                ],
                [
                  0.46602742517700807,
                  0.7435289180229604
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -24.363586089683306,
                "scale": 1.2042045005921833,
                "targetX": 0.5327159361017897,
                "targetY": 0.33547543519295303
              },
              "head": {
                "angle": -14.363586089683306,
                "scale": 0.19991676279362416,
                "x": 0.5327159361017897,
                "y": 0.33547543519295303
              }
            },
            {
              "eyeScales": [
                [
                  1.1710243052829417,
                  1.0414247774255412
                ],
                [
                  1.4765089066611006,
                  1.1455672551680953
                ]
              ],
              "eyes": [
                [
                  0.16894812585271785,
                  0.7432564723922357
                ],
                [
                  0.46602742517700807,
                  0.7435289180229604
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -24.363586089683306,
                "scale": 1.2042045005921833,
                "targetX": 0.5327159361017897,
                "targetY": 0.33547543519295303
              },
              "head": {
                "angle": -14.363586089683306,
                "scale": 0.19991676279362416,
                "x": 0.5327159361017897,
                "y": 0.33547543519295303
              }
            },
            {
              "eyeScales": [
                [
                  1.1710243052829417,
                  1.0414247774255412
                ],
                [
                  1.4765089066611006,
                  1.1455672551680953
                ]
              ],
              "eyes": [
                [
                  0.16894812585271785,
                  0.7432564723922357
                ],
                [
                  0.46602742517700807,
                  0.7435289180229604
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -24.363586089683306,
                "scale": 1.2042045005921833,
                "targetX": 0.5327159361017897,
                "targetY": 0.33547543519295303
              },
              "head": {
                "angle": -14.363586089683306,
                "scale": 0.19991676279362416,
                "x": 0.5327159361017897,
                "y": 0.33547543519295303
              }
            },
            {
              "eyeScales": [
                [
                  1.1710243052829417,
                  1.0414247774255412
                ],
                [
                  1.4765089066611006,
                  1.1455672551680953
                ]
              ],
              "eyes": [
                [
                  0.16894812585271785,
                  0.7432564723922357
                ],
                [
                  0.46602742517700807,
                  0.7435289180229604
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -24.363586089683306,
                "scale": 1.2042045005921833,
                "targetX": 0.5327159361017897,
                "targetY": 0.33547543519295303
              },
              "head": {
                "angle": -14.363586089683306,
                "scale": 0.19991676279362416,
                "x": 0.5327159361017897,
                "y": 0.33547543519295303
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "tee-shorts-0-backward.png",
              "forward": "tee-shorts-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "tee-shorts-1-backward.png",
              "forward": "tee-shorts-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "tee-shorts-2-backward.png",
              "forward": "tee-shorts-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "tee-shorts-3-backward.png",
              "forward": "tee-shorts-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "tee-shorts-4-backward.png",
              "forward": "tee-shorts-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "tee-shorts-5-backward.png",
              "forward": "tee-shorts-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "tee-shorts-6-backward.png",
              "forward": "tee-shorts-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "1": {
          "bounds": [
            300,
            25,
            830,
            922
          ],
          "ear": [
            0.6145833333333334,
            0.400390625,
            0.01953125,
            0.0390625
          ],
          "eyeScales": [
            [
              1.1243885714285713,
              1.0105558441558442
            ],
            [
              1.4310399999999999,
              1.0802493506493507
            ]
          ],
          "eyes": [
            [
              0.16157828302286958,
              0.7584575927262345
            ],
            [
              0.4807290522383917,
              0.7568603211502001
            ]
          ],
          "ground": 0.9130859375,
          "hairBounds": {
            "0": [
              535.6233255146198,
              74.37729505288888,
              1065.6028805514823,
              579.6469992151112
            ],
            "1": [
              563.6348760238407,
              74.22652819633396,
              1001.8631727002958,
              476.26523258589816
            ],
            "2": [
              556.6272823830824,
              66.79721340586468,
              1062.5677944608778,
              561.207715122163
            ],
            "3": [
              512.0008845421762,
              53.52992507024487,
              1216.4955618940282,
              812.9695711472648
            ],
            "4": [
              547.566314377526,
              18.030864232153647,
              1179.2130026011464,
              625.2015959316002
            ],
            "5": [
              582.8870315133191,
              87.97420303608666,
              991.5280462734797,
              470.0518056694051
            ],
            "6": [
              546.3984467915366,
              63.435678124204,
              1098.9183587998027,
              614.324811040631
            ],
            "7": [
              561.3179649056735,
              96.76900134885983,
              1079.6578869242485,
              660.7787892807775
            ],
            "8": [
              447.632517687857,
              25.676869808693368,
              1280.054106309608,
              880.3478116753619
            ],
            "9": [
              486.83370678268085,
              39.3397017214067,
              1146.9072864173668,
              649.9842481389162
            ]
          },
          "hairFit": {
            "fromX": 0.5260416666666666,
            "fromY": 0.1904296875,
            "rotation": -25.80406737379246,
            "scale": 1.1963064295485637,
            "targetX": 0.5277273995535714,
            "targetY": 0.33047110218542364
          },
          "head": {
            "angle": -15.80406737379246,
            "scale": 0.19860555959302328,
            "x": 0.5277273995535714,
            "y": 0.33047110218542364
          },
          "pettingContact": [
            552,
            526,
            829,
            684
          ],
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.1243885714285713,
                  1.0105558441558442
                ],
                [
                  1.4310399999999999,
                  1.0802493506493507
                ]
              ],
              "eyes": [
                [
                  0.16157828302286958,
                  0.7584575927262345
                ],
                [
                  0.4807290522383917,
                  0.7568603211502001
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -25.80406737379246,
                "scale": 1.1963064295485637,
                "targetX": 0.5277273995535714,
                "targetY": 0.33047110218542364
              },
              "head": {
                "angle": -15.80406737379246,
                "scale": 0.19860555959302328,
                "x": 0.5277273995535714,
                "y": 0.33047110218542364
              }
            },
            {
              "eyeScales": [
                [
                  1.1243885714285713,
                  1.0105558441558442
                ],
                [
                  1.4310399999999999,
                  1.0802493506493507
                ]
              ],
              "eyes": [
                [
                  0.16157828302286958,
                  0.7584575927262345
                ],
                [
                  0.4807290522383917,
                  0.7568603211502001
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -25.80406737379246,
                "scale": 1.1963064295485637,
                "targetX": 0.5277273995535714,
                "targetY": 0.33047110218542364
              },
              "head": {
                "angle": -15.80406737379246,
                "scale": 0.19860555959302328,
                "x": 0.5277273995535714,
                "y": 0.33047110218542364
              }
            },
            {
              "eyeScales": [
                [
                  1.1243885714285713,
                  1.0105558441558442
                ],
                [
                  1.4310399999999999,
                  1.0802493506493507
                ]
              ],
              "eyes": [
                [
                  0.16157828302286958,
                  0.7584575927262345
                ],
                [
                  0.4807290522383917,
                  0.7568603211502001
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -25.80406737379246,
                "scale": 1.1963064295485637,
                "targetX": 0.5277273995535714,
                "targetY": 0.33047110218542364
              },
              "head": {
                "angle": -15.80406737379246,
                "scale": 0.19860555959302328,
                "x": 0.5277273995535714,
                "y": 0.33047110218542364
              }
            },
            {
              "eyeScales": [
                [
                  1.1243885714285713,
                  1.0105558441558442
                ],
                [
                  1.4310399999999999,
                  1.0802493506493507
                ]
              ],
              "eyes": [
                [
                  0.16157828302286958,
                  0.7584575927262345
                ],
                [
                  0.4807290522383917,
                  0.7568603211502001
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -25.80406737379246,
                "scale": 1.1963064295485637,
                "targetX": 0.5277273995535714,
                "targetY": 0.33047110218542364
              },
              "head": {
                "angle": -15.80406737379246,
                "scale": 0.19860555959302328,
                "x": 0.5277273995535714,
                "y": 0.33047110218542364
              }
            },
            {
              "eyeScales": [
                [
                  1.1243885714285713,
                  1.0105558441558442
                ],
                [
                  1.4310399999999999,
                  1.0802493506493507
                ]
              ],
              "eyes": [
                [
                  0.16157828302286958,
                  0.7584575927262345
                ],
                [
                  0.4807290522383917,
                  0.7568603211502001
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -25.80406737379246,
                "scale": 1.1963064295485637,
                "targetX": 0.5277273995535714,
                "targetY": 0.33047110218542364
              },
              "head": {
                "angle": -15.80406737379246,
                "scale": 0.19860555959302328,
                "x": 0.5277273995535714,
                "y": 0.33047110218542364
              }
            },
            {
              "eyeScales": [
                [
                  1.1243885714285713,
                  1.0105558441558442
                ],
                [
                  1.4310399999999999,
                  1.0802493506493507
                ]
              ],
              "eyes": [
                [
                  0.16157828302286958,
                  0.7584575927262345
                ],
                [
                  0.4807290522383917,
                  0.7568603211502001
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -25.80406737379246,
                "scale": 1.1963064295485637,
                "targetX": 0.5277273995535714,
                "targetY": 0.33047110218542364
              },
              "head": {
                "angle": -15.80406737379246,
                "scale": 0.19860555959302328,
                "x": 0.5277273995535714,
                "y": 0.33047110218542364
              }
            },
            {
              "eyeScales": [
                [
                  1.1243885714285713,
                  1.0105558441558442
                ],
                [
                  1.4310399999999999,
                  1.0802493506493507
                ]
              ],
              "eyes": [
                [
                  0.16157828302286958,
                  0.7584575927262345
                ],
                [
                  0.4807290522383917,
                  0.7568603211502001
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -25.80406737379246,
                "scale": 1.1963064295485637,
                "targetX": 0.5277273995535714,
                "targetY": 0.33047110218542364
              },
              "head": {
                "angle": -15.80406737379246,
                "scale": 0.19860555959302328,
                "x": 0.5277273995535714,
                "y": 0.33047110218542364
              }
            },
            {
              "eyeScales": [
                [
                  1.1243885714285713,
                  1.0105558441558442
                ],
                [
                  1.4310399999999999,
                  1.0802493506493507
                ]
              ],
              "eyes": [
                [
                  0.16157828302286958,
                  0.7584575927262345
                ],
                [
                  0.4807290522383917,
                  0.7568603211502001
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -25.80406737379246,
                "scale": 1.1963064295485637,
                "targetX": 0.5277273995535714,
                "targetY": 0.33047110218542364
              },
              "head": {
                "angle": -15.80406737379246,
                "scale": 0.19860555959302328,
                "x": 0.5277273995535714,
                "y": 0.33047110218542364
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "tee-jeans-0-backward.png",
              "forward": "tee-jeans-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "tee-jeans-1-backward.png",
              "forward": "tee-jeans-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "tee-jeans-2-backward.png",
              "forward": "tee-jeans-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "tee-jeans-3-backward.png",
              "forward": "tee-jeans-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "tee-jeans-4-backward.png",
              "forward": "tee-jeans-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "tee-jeans-5-backward.png",
              "forward": "tee-jeans-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "tee-jeans-6-backward.png",
              "forward": "tee-jeans-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "2": {
          "bounds": [
            300,
            25,
            830,
            916
          ],
          "ear": [
            0.609375,
            0.4580078125,
            0.017578125,
            0.0341796875
          ],
          "eyeScales": [
            [
              1.1282051282051282,
              0.945054945054945
            ],
            [
              1.4827838827838828,
              1.010989010989011
            ]
          ],
          "eyes": [
            [
              0.19788710247063784,
              0.7799883998947997
            ],
            [
              0.5161731500014523,
              0.7490201423897225
            ]
          ],
          "ground": 0.9072265625,
          "hairBounds": {
            "0": [
              585.4130187165167,
              186.83212137991924,
              1041.4825529927728,
              613.1645700546751
            ],
            "1": [
              612.2025661104558,
              185.1896489840776,
              990.458886130616,
              521.7199689610619
            ],
            "2": [
              605.4465593593507,
              179.97093800636375,
              1039.1513478105414,
              598.6187735184092
            ],
            "3": [
              563.7369692736813,
              170.9022193013396,
              1161.707528770677,
              824.3899638437113
            ],
            "4": [
              597.8924007113034,
              141.6384846726877,
              1140.691490364308,
              656.6564354853288
            ],
            "5": [
              628.9995972126686,
              196.9163433813834,
              980.5139585244303,
              517.449676386385
            ],
            "6": [
              595.7347276842227,
              177.86050060495813,
              1068.9890501739826,
              646.1002469071416
            ],
            "7": [
              608.1216996667754,
              205.16389712688,
              1048.0433953001905,
              689.4049892600631
            ],
            "8": [
              505.32390962466513,
              148.70276870553118,
              1216.4733259083582,
              880.2722558368146
            ],
            "9": [
              541.4036860055189,
              158.99009136628334,
              1111.89572074122,
              673.2686465630738
            ]
          },
          "hairFit": {
            "fromX": 0.5260416666666666,
            "fromY": 0.1904296875,
            "rotation": -18.236596854241785,
            "scale": 1.054367201426025,
            "targetX": 0.5358755195180976,
            "targetY": 0.4046495225694444
          },
          "head": {
            "angle": -8.236596854241785,
            "scale": 0.17504142992424243,
            "x": 0.5358755195180976,
            "y": 0.4046495225694444
          },
          "pettingContact": [
            562,
            566,
            841,
            714
          ],
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.1282051282051282,
                  0.945054945054945
                ],
                [
                  1.4827838827838828,
                  1.010989010989011
                ]
              ],
              "eyes": [
                [
                  0.19788710247063784,
                  0.7799883998947997
                ],
                [
                  0.5161731500014523,
                  0.7490201423897225
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -18.236596854241785,
                "scale": 1.054367201426025,
                "targetX": 0.5358755195180976,
                "targetY": 0.4046495225694444
              },
              "head": {
                "angle": -8.236596854241785,
                "scale": 0.17504142992424243,
                "x": 0.5358755195180976,
                "y": 0.4046495225694444
              }
            },
            {
              "eyeScales": [
                [
                  1.1282051282051282,
                  0.945054945054945
                ],
                [
                  1.4827838827838828,
                  1.010989010989011
                ]
              ],
              "eyes": [
                [
                  0.19788710247063784,
                  0.7799883998947997
                ],
                [
                  0.5161731500014523,
                  0.7490201423897225
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -18.236596854241785,
                "scale": 1.054367201426025,
                "targetX": 0.5358755195180976,
                "targetY": 0.4046495225694444
              },
              "head": {
                "angle": -8.236596854241785,
                "scale": 0.17504142992424243,
                "x": 0.5358755195180976,
                "y": 0.4046495225694444
              }
            },
            {
              "eyeScales": [
                [
                  1.1282051282051282,
                  0.945054945054945
                ],
                [
                  1.4827838827838828,
                  1.010989010989011
                ]
              ],
              "eyes": [
                [
                  0.19788710247063784,
                  0.7799883998947997
                ],
                [
                  0.5161731500014523,
                  0.7490201423897225
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -18.236596854241785,
                "scale": 1.054367201426025,
                "targetX": 0.5358755195180976,
                "targetY": 0.4046495225694444
              },
              "head": {
                "angle": -8.236596854241785,
                "scale": 0.17504142992424243,
                "x": 0.5358755195180976,
                "y": 0.4046495225694444
              }
            },
            {
              "eyeScales": [
                [
                  1.1282051282051282,
                  0.945054945054945
                ],
                [
                  1.4827838827838828,
                  1.010989010989011
                ]
              ],
              "eyes": [
                [
                  0.19788710247063784,
                  0.7799883998947997
                ],
                [
                  0.5161731500014523,
                  0.7490201423897225
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -18.236596854241785,
                "scale": 1.054367201426025,
                "targetX": 0.5358755195180976,
                "targetY": 0.4046495225694444
              },
              "head": {
                "angle": -8.236596854241785,
                "scale": 0.17504142992424243,
                "x": 0.5358755195180976,
                "y": 0.4046495225694444
              }
            },
            {
              "eyeScales": [
                [
                  1.1282051282051282,
                  0.945054945054945
                ],
                [
                  1.4827838827838828,
                  1.010989010989011
                ]
              ],
              "eyes": [
                [
                  0.19788710247063784,
                  0.7799883998947997
                ],
                [
                  0.5161731500014523,
                  0.7490201423897225
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -18.236596854241785,
                "scale": 1.054367201426025,
                "targetX": 0.5358755195180976,
                "targetY": 0.4046495225694444
              },
              "head": {
                "angle": -8.236596854241785,
                "scale": 0.17504142992424243,
                "x": 0.5358755195180976,
                "y": 0.4046495225694444
              }
            },
            {
              "eyeScales": [
                [
                  1.1282051282051282,
                  0.945054945054945
                ],
                [
                  1.4827838827838828,
                  1.010989010989011
                ]
              ],
              "eyes": [
                [
                  0.19788710247063784,
                  0.7799883998947997
                ],
                [
                  0.5161731500014523,
                  0.7490201423897225
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -18.236596854241785,
                "scale": 1.054367201426025,
                "targetX": 0.5358755195180976,
                "targetY": 0.4046495225694444
              },
              "head": {
                "angle": -8.236596854241785,
                "scale": 0.17504142992424243,
                "x": 0.5358755195180976,
                "y": 0.4046495225694444
              }
            },
            {
              "eyeScales": [
                [
                  1.1282051282051282,
                  0.945054945054945
                ],
                [
                  1.4827838827838828,
                  1.010989010989011
                ]
              ],
              "eyes": [
                [
                  0.19788710247063784,
                  0.7799883998947997
                ],
                [
                  0.5161731500014523,
                  0.7490201423897225
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -18.236596854241785,
                "scale": 1.054367201426025,
                "targetX": 0.5358755195180976,
                "targetY": 0.4046495225694444
              },
              "head": {
                "angle": -8.236596854241785,
                "scale": 0.17504142992424243,
                "x": 0.5358755195180976,
                "y": 0.4046495225694444
              }
            },
            {
              "eyeScales": [
                [
                  1.1282051282051282,
                  0.945054945054945
                ],
                [
                  1.4827838827838828,
                  1.010989010989011
                ]
              ],
              "eyes": [
                [
                  0.19788710247063784,
                  0.7799883998947997
                ],
                [
                  0.5161731500014523,
                  0.7490201423897225
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -18.236596854241785,
                "scale": 1.054367201426025,
                "targetX": 0.5358755195180976,
                "targetY": 0.4046495225694444
              },
              "head": {
                "angle": -8.236596854241785,
                "scale": 0.17504142992424243,
                "x": 0.5358755195180976,
                "y": 0.4046495225694444
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "dress-0-backward.png",
              "forward": "dress-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "dress-1-backward.png",
              "forward": "dress-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "dress-2-backward.png",
              "forward": "dress-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "dress-3-backward.png",
              "forward": "dress-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "dress-4-backward.png",
              "forward": "dress-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "dress-5-backward.png",
              "forward": "dress-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "dress-6-backward.png",
              "forward": "dress-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "3": {
          "bounds": [
            300,
            25,
            830,
            915
          ],
          "ear": [
            0.6067708333333334,
            0.45703125,
            0.018229166666666668,
            0.03515625
          ],
          "eyeScales": [
            [
              1.0670553935860059,
              0.9354057021695508
            ],
            [
              1.473552686380675,
              1.0046950134413692
            ]
          ],
          "eyes": [
            [
              0.162922031385125,
              0.773376788338494
            ],
            [
              0.48152269200966397,
              0.7739189747164884
            ]
          ],
          "ground": 0.90625,
          "hairBounds": {
            "0": [
              560.8458856694558,
              157.13423107020267,
              1044.8031507699427,
              614.0084316082841
            ],
            "1": [
              587.920994288297,
              156.1211600582727,
              1002.210528333597,
              517.8178403492124
            ],
            "2": [
              581.1202808545775,
              149.99518658342282,
              1042.1360342289786,
              597.959683012822
            ],
            "3": [
              538.4766384829011,
              139.20051093989093,
              1178.062294493746,
              834.1428530786338
            ],
            "4": [
              572.9247250749933,
              107.426109644076,
              1149.8331423731802,
              658.4019917879272
            ],
            "5": [
              605.7121251586024,
              168.65414612372774,
              991.4012435208673,
              512.8062727588767
            ],
            "6": [
              571.2692155317823,
              147.36847557302409,
              1074.9130803820435,
              647.6944720679371
            ],
            "7": [
              584.7377203179121,
              177.10314987261629,
              1055.1341347652542,
              692.3078064376714
            ],
            "8": [
              477.85283604949984,
              114.71020201285444,
              1236.325400763096,
              894.5008875402455
            ],
            "9": [
              515.0293232579387,
              126.37516494837814,
              1119.7128588305354,
              678.1271384579834
            ]
          },
          "hairFit": {
            "fromX": 0.5260416666666666,
            "fromY": 0.1904296875,
            "rotation": -22.855036544951965,
            "scale": 1.109705882352941,
            "targetX": 0.5286897786458333,
            "targetY": 0.3878853352864583
          },
          "head": {
            "angle": -12.855036544951965,
            "scale": 0.184228515625,
            "x": 0.5286897786458333,
            "y": 0.3878853352864583
          },
          "pettingContact": [
            550,
            562,
            830,
            696
          ],
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.0670553935860059,
                  0.9354057021695508
                ],
                [
                  1.473552686380675,
                  1.0046950134413692
                ]
              ],
              "eyes": [
                [
                  0.162922031385125,
                  0.773376788338494
                ],
                [
                  0.48152269200966397,
                  0.7739189747164884
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -22.855036544951965,
                "scale": 1.109705882352941,
                "targetX": 0.5286897786458333,
                "targetY": 0.3878853352864583
              },
              "head": {
                "angle": -12.855036544951965,
                "scale": 0.184228515625,
                "x": 0.5286897786458333,
                "y": 0.3878853352864583
              }
            },
            {
              "eyeScales": [
                [
                  1.0670553935860059,
                  0.9354057021695508
                ],
                [
                  1.473552686380675,
                  1.0046950134413692
                ]
              ],
              "eyes": [
                [
                  0.162922031385125,
                  0.773376788338494
                ],
                [
                  0.48152269200966397,
                  0.7739189747164884
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -22.855036544951965,
                "scale": 1.109705882352941,
                "targetX": 0.5286897786458333,
                "targetY": 0.3878853352864583
              },
              "head": {
                "angle": -12.855036544951965,
                "scale": 0.184228515625,
                "x": 0.5286897786458333,
                "y": 0.3878853352864583
              }
            },
            {
              "eyeScales": [
                [
                  1.0670553935860059,
                  0.9354057021695508
                ],
                [
                  1.473552686380675,
                  1.0046950134413692
                ]
              ],
              "eyes": [
                [
                  0.162922031385125,
                  0.773376788338494
                ],
                [
                  0.48152269200966397,
                  0.7739189747164884
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -22.855036544951965,
                "scale": 1.109705882352941,
                "targetX": 0.5286897786458333,
                "targetY": 0.3878853352864583
              },
              "head": {
                "angle": -12.855036544951965,
                "scale": 0.184228515625,
                "x": 0.5286897786458333,
                "y": 0.3878853352864583
              }
            },
            {
              "eyeScales": [
                [
                  1.0670553935860059,
                  0.9354057021695508
                ],
                [
                  1.473552686380675,
                  1.0046950134413692
                ]
              ],
              "eyes": [
                [
                  0.162922031385125,
                  0.773376788338494
                ],
                [
                  0.48152269200966397,
                  0.7739189747164884
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -22.855036544951965,
                "scale": 1.109705882352941,
                "targetX": 0.5286897786458333,
                "targetY": 0.3878853352864583
              },
              "head": {
                "angle": -12.855036544951965,
                "scale": 0.184228515625,
                "x": 0.5286897786458333,
                "y": 0.3878853352864583
              }
            },
            {
              "eyeScales": [
                [
                  1.0670553935860059,
                  0.9354057021695508
                ],
                [
                  1.473552686380675,
                  1.0046950134413692
                ]
              ],
              "eyes": [
                [
                  0.162922031385125,
                  0.773376788338494
                ],
                [
                  0.48152269200966397,
                  0.7739189747164884
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -22.855036544951965,
                "scale": 1.109705882352941,
                "targetX": 0.5286897786458333,
                "targetY": 0.3878853352864583
              },
              "head": {
                "angle": -12.855036544951965,
                "scale": 0.184228515625,
                "x": 0.5286897786458333,
                "y": 0.3878853352864583
              }
            },
            {
              "eyeScales": [
                [
                  1.0670553935860059,
                  0.9354057021695508
                ],
                [
                  1.473552686380675,
                  1.0046950134413692
                ]
              ],
              "eyes": [
                [
                  0.162922031385125,
                  0.773376788338494
                ],
                [
                  0.48152269200966397,
                  0.7739189747164884
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -22.855036544951965,
                "scale": 1.109705882352941,
                "targetX": 0.5286897786458333,
                "targetY": 0.3878853352864583
              },
              "head": {
                "angle": -12.855036544951965,
                "scale": 0.184228515625,
                "x": 0.5286897786458333,
                "y": 0.3878853352864583
              }
            },
            {
              "eyeScales": [
                [
                  1.0670553935860059,
                  0.9354057021695508
                ],
                [
                  1.473552686380675,
                  1.0046950134413692
                ]
              ],
              "eyes": [
                [
                  0.162922031385125,
                  0.773376788338494
                ],
                [
                  0.48152269200966397,
                  0.7739189747164884
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -22.855036544951965,
                "scale": 1.109705882352941,
                "targetX": 0.5286897786458333,
                "targetY": 0.3878853352864583
              },
              "head": {
                "angle": -12.855036544951965,
                "scale": 0.184228515625,
                "x": 0.5286897786458333,
                "y": 0.3878853352864583
              }
            },
            {
              "eyeScales": [
                [
                  1.0670553935860059,
                  0.9354057021695508
                ],
                [
                  1.473552686380675,
                  1.0046950134413692
                ]
              ],
              "eyes": [
                [
                  0.162922031385125,
                  0.773376788338494
                ],
                [
                  0.48152269200966397,
                  0.7739189747164884
                ]
              ],
              "hairFit": {
                "fromX": 0.5260416666666666,
                "fromY": 0.1904296875,
                "rotation": -22.855036544951965,
                "scale": 1.109705882352941,
                "targetX": 0.5286897786458333,
                "targetY": 0.3878853352864583
              },
              "head": {
                "angle": -12.855036544951965,
                "scale": 0.184228515625,
                "x": 0.5286897786458333,
                "y": 0.3878853352864583
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "shirt-skirt-0-backward.png",
              "forward": "shirt-skirt-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "shirt-skirt-1-backward.png",
              "forward": "shirt-skirt-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "shirt-skirt-2-backward.png",
              "forward": "shirt-skirt-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "shirt-skirt-3-backward.png",
              "forward": "shirt-skirt-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "shirt-skirt-4-backward.png",
              "forward": "shirt-skirt-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "shirt-skirt-5-backward.png",
              "forward": "shirt-skirt-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "shirt-skirt-6-backward.png",
              "forward": "shirt-skirt-6-forward.png",
              "scale": 768.0
            }
          }
        }
      },
      "outfitFrames": {
        "0": 8,
        "1": 8,
        "2": 8,
        "3": 8
      },
      "palette": "petting-v25",
      "playback": {
        "fps": 2.25,
        "interpolate": true,
        "order": [
          0,
          0,
          1,
          2,
          3,
          4,
          5,
          6,
          7,
          7,
          6,
          5,
          4,
          3,
          2,
          1
        ]
      },
      "posture": "sitting",
      "stabilized": true,
      "station": "meadow",
      "status": "V25 held child and content rabbit, eight painted contact strokes; ear-aware hair depth"
    },
    "width": 1024
  },
  "fishing": {
    "frames": {
      "0": [
        {
          "brows": null,
          "garment": "act-fishing-tee-shorts-a-garment",
          "props": "act-fishing-tee-shorts-a-props",
          "skin": "act-fishing-tee-shorts-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-shorts-b-garment",
          "props": "act-fishing-tee-shorts-b-props",
          "skin": "act-fishing-tee-shorts-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-shorts-c-garment",
          "props": "act-fishing-tee-shorts-c-props",
          "skin": "act-fishing-tee-shorts-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-shorts-d-garment",
          "props": "act-fishing-tee-shorts-d-props",
          "skin": "act-fishing-tee-shorts-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-shorts-e-garment",
          "props": "act-fishing-tee-shorts-e-props",
          "skin": "act-fishing-tee-shorts-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-shorts-f-garment",
          "props": "act-fishing-tee-shorts-f-props",
          "skin": "act-fishing-tee-shorts-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-shorts-g-garment",
          "props": "act-fishing-tee-shorts-g-props",
          "skin": "act-fishing-tee-shorts-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-shorts-h-garment",
          "props": "act-fishing-tee-shorts-h-props",
          "skin": "act-fishing-tee-shorts-h-skin",
          "tongue": null
        }
      ],
      "1": [
        {
          "brows": null,
          "garment": "act-fishing-tee-jeans-a-garment",
          "props": "act-fishing-tee-jeans-a-props",
          "skin": "act-fishing-tee-jeans-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-jeans-b-garment",
          "props": "act-fishing-tee-jeans-b-props",
          "skin": "act-fishing-tee-jeans-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-jeans-c-garment",
          "props": "act-fishing-tee-jeans-c-props",
          "skin": "act-fishing-tee-jeans-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-jeans-d-garment",
          "props": "act-fishing-tee-jeans-d-props",
          "skin": "act-fishing-tee-jeans-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-jeans-e-garment",
          "props": "act-fishing-tee-jeans-e-props",
          "skin": "act-fishing-tee-jeans-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-jeans-f-garment",
          "props": "act-fishing-tee-jeans-f-props",
          "skin": "act-fishing-tee-jeans-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-jeans-g-garment",
          "props": "act-fishing-tee-jeans-g-props",
          "skin": "act-fishing-tee-jeans-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-tee-jeans-h-garment",
          "props": "act-fishing-tee-jeans-h-props",
          "skin": "act-fishing-tee-jeans-h-skin",
          "tongue": null
        }
      ],
      "2": [
        {
          "brows": null,
          "garment": "act-fishing-dress-a-garment",
          "props": "act-fishing-dress-a-props",
          "skin": "act-fishing-dress-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-dress-b-garment",
          "props": "act-fishing-dress-b-props",
          "skin": "act-fishing-dress-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-dress-c-garment",
          "props": "act-fishing-dress-c-props",
          "skin": "act-fishing-dress-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-dress-d-garment",
          "props": "act-fishing-dress-d-props",
          "skin": "act-fishing-dress-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-dress-e-garment",
          "props": "act-fishing-dress-e-props",
          "skin": "act-fishing-dress-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-dress-f-garment",
          "props": "act-fishing-dress-f-props",
          "skin": "act-fishing-dress-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-dress-g-garment",
          "props": "act-fishing-dress-g-props",
          "skin": "act-fishing-dress-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-dress-h-garment",
          "props": "act-fishing-dress-h-props",
          "skin": "act-fishing-dress-h-skin",
          "tongue": null
        }
      ],
      "3": [
        {
          "brows": null,
          "garment": "act-fishing-shirt-skirt-a-garment",
          "props": "act-fishing-shirt-skirt-a-props",
          "skin": "act-fishing-shirt-skirt-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-shirt-skirt-b-garment",
          "props": "act-fishing-shirt-skirt-b-props",
          "skin": "act-fishing-shirt-skirt-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-shirt-skirt-c-garment",
          "props": "act-fishing-shirt-skirt-c-props",
          "skin": "act-fishing-shirt-skirt-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-shirt-skirt-d-garment",
          "props": "act-fishing-shirt-skirt-d-props",
          "skin": "act-fishing-shirt-skirt-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-shirt-skirt-e-garment",
          "props": "act-fishing-shirt-skirt-e-props",
          "skin": "act-fishing-shirt-skirt-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-shirt-skirt-f-garment",
          "props": "act-fishing-shirt-skirt-f-props",
          "skin": "act-fishing-shirt-skirt-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-shirt-skirt-g-garment",
          "props": "act-fishing-shirt-skirt-g-props",
          "skin": "act-fishing-shirt-skirt-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-shirt-skirt-h-garment",
          "props": "act-fishing-shirt-skirt-h-props",
          "skin": "act-fishing-shirt-skirt-h-skin",
          "tongue": null
        }
      ]
    },
    "hair": {
      "0": "act-fishing-hair-0",
      "1": "act-fishing-hair-1",
      "2": "act-fishing-hair-2",
      "3": "act-fishing-hair-3",
      "4": "act-fishing-hair-4",
      "5": "act-fishing-hair-5",
      "6": "act-fishing-hair-6",
      "7": "act-fishing-hair-7",
      "8": "act-fishing-hair-8",
      "9": "act-fishing-hair-9"
    },
    "height": 683,
    "meta": {
      "availableOutfits": [
        0,
        1,
        2,
        3
      ],
      "bounds": [
        125,
        65,
        880,
        860
      ],
      "camera": "side",
      "canvas": [
        1536,
        1024
      ],
      "eyeScale": [
        1.2,
        1.2
      ],
      "eyes": [
        [
          0.21750000000000003,
          0.6174999999999999
        ]
      ],
      "frames": 8,
      "front": [
        "water"
      ],
      "ground": 0.865,
      "hairBounds": {
        "0": [
          738.6233766233767,
          90.42857142857142,
          998.3636363636364,
          328.0909090909091
        ],
        "1": [
          733.4285714285714,
          76.14285714285714,
          993.1688311688312,
          315.1038961038961
        ],
        "2": [
          733.4285714285714,
          68.35064935064935,
          1016.5454545454545,
          325.4935064935065
        ],
        "3": [
          742.5194805194806,
          87.83116883116884,
          1067.1948051948052,
          593.025974025974
        ],
        "4": [
          720.4415584415584,
          68.35064935064935,
          1059.4025974025974,
          390.42857142857144
        ],
        "5": [
          752.9090909090909,
          87.83116883116884,
          980.1818181818182,
          306.012987012987
        ],
        "6": [
          737.3246753246754,
          85.23376623376623,
          1036.025974025974,
          346.27272727272725
        ],
        "7": [
          747.7142857142858,
          87.83116883116884,
          1056.8051948051948,
          498.2207792207792
        ],
        "8": [
          725.6363636363636,
          90.42857142857142,
          1112.6493506493507,
          580.038961038961
        ],
        "9": [
          737.3246753246754,
          77.44155844155844,
          1071.090909090909,
          360.5584415584416
        ]
      },
      "hairFit": {
        "fromX": 0.55078125,
        "fromY": 0.2021484375,
        "scale": 1.2987012987012987
      },
      "hairReady": false,
      "hairStyles": [
        0,
        1,
        2,
        3,
        4,
        5,
        6,
        7,
        8,
        9
      ],
      "head": {
        "angle": 0,
        "scale": 0.13020833333333334,
        "x": 0.5611979166666666,
        "y": 0.2138671875
      },
      "hold": [
        2.4,
        4
      ],
      "id": "fishing",
      "outfitFits": {
        "0": {
          "bounds": [
            125,
            30,
            916,
            895
          ],
          "eyeScales": [
            [
              1.1522727272727273,
              1.3295454545454546
            ]
          ],
          "eyes": [
            [
              0.21750000000000008,
              0.6175
            ]
          ],
          "ground": 0.884765625,
          "hairBounds": {
            "0": [
              732.9410491469315,
              63.478057889822566,
              1014.7507427213309,
              326.99168152109337
            ],
            "1": [
              727.3048552754435,
              47.97852474323062,
              1009.114548849843,
              312.90119684237334
            ],
            "2": [
              727.3048552754435,
              39.52423393599864,
              1034.4774212715388,
              324.1735845853494
            ],
            "3": [
              737.1681945505476,
              60.6599609540786,
              1089.430311518547,
              614.4375689669807
            ],
            "4": [
              713.2143705967235,
              39.52423393599864,
              1080.9760207113147,
              394.62600797894925
            ],
            "5": [
              748.4405822935234,
              60.6599609540786,
              995.0240641711231,
              303.0378575672694
            ],
            "6": [
              731.5320006790596,
              57.841864018334604,
              1055.6131482896187,
              346.7183600713013
            ],
            "7": [
              742.8043884220356,
              60.6599609540786,
              1078.157923775571,
              511.57703081232495
            ],
            "8": [
              718.8505644682116,
              63.478057889822566,
              1138.7470078940669,
              600.3470842882607
            ],
            "9": [
              731.5320006790596,
              49.38757321110262,
              1093.6574569221627,
              362.21789321789333
            ]
          },
          "hairFit": {
            "fromX": 0.55078125,
            "fromY": 0.2021484375,
            "rotation": 0.0,
            "scale": 1.4090484678719972
          },
          "head": {
            "angle": 0.0,
            "scale": 0.14127178649237473,
            "x": 0.5643233421840959,
            "y": 0.20255113172162803
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.1522727272727273,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000008,
                  0.6175
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.4090484678719972
              },
              "head": {
                "angle": 0.0,
                "scale": 0.14127178649237473,
                "x": 0.5643233421840959,
                "y": 0.20255113172162803
              }
            },
            {
              "eyeScales": [
                [
                  1.1522727272727273,
                  1.3899793388429753
                ]
              ],
              "eyes": [
                [
                  0.21750000000000008,
                  0.6175
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.4090484678719972
              },
              "head": {
                "angle": 0.0,
                "scale": 0.14127178649237473,
                "x": 0.5643233421840959,
                "y": 0.20363475053847302
              }
            },
            {
              "eyeScales": [
                [
                  1.1522727272727273,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000008,
                  0.6174999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.4090484678719972
              },
              "head": {
                "angle": 0.0,
                "scale": 0.14127178649237473,
                "x": 0.5643233421840959,
                "y": 0.20374180685531795
              }
            },
            {
              "eyeScales": [
                [
                  1.1522727272727273,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000008,
                  0.6174999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.4090484678719972
              },
              "head": {
                "angle": 0.0,
                "scale": 0.14127178649237473,
                "x": 0.5643233421840959,
                "y": 0.20374180685531795
              }
            },
            {
              "eyeScales": [
                [
                  1.1522727272727273,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000008,
                  0.6174999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.4090484678719972
              },
              "head": {
                "angle": 0.0,
                "scale": 0.14127178649237473,
                "x": 0.5643233421840959,
                "y": 0.20038389408793822
              }
            },
            {
              "eyeScales": [
                [
                  1.1522727272727273,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000008,
                  0.6174999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.4090484678719972
              },
              "head": {
                "angle": 0.0,
                "scale": 0.14127178649237473,
                "x": 0.5643233421840959,
                "y": 0.20038389408793822
              }
            },
            {
              "eyeScales": [
                [
                  1.1522727272727273,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000008,
                  0.6174999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.4090484678719972
              },
              "head": {
                "angle": 0.0,
                "scale": 0.14127178649237473,
                "x": 0.5643233421840959,
                "y": 0.1982166564542484
              }
            },
            {
              "eyeScales": [
                [
                  1.1522727272727273,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000008,
                  0.6175
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.4090484678719972
              },
              "head": {
                "angle": 0.0,
                "scale": 0.14127178649237473,
                "x": 0.5643233421840959,
                "y": 0.19821665645424835
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "tee-shorts-0-backward.png",
              "forward": "tee-shorts-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "tee-shorts-1-backward.png",
              "forward": "tee-shorts-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "tee-shorts-2-backward.png",
              "forward": "tee-shorts-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "tee-shorts-3-backward.png",
              "forward": "tee-shorts-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "tee-shorts-4-backward.png",
              "forward": "tee-shorts-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "tee-shorts-5-backward.png",
              "forward": "tee-shorts-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "tee-shorts-6-backward.png",
              "forward": "tee-shorts-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "1": {
          "bounds": [
            125,
            4,
            904,
            921
          ],
          "eyeScales": [
            [
              1.1673469387755104,
              1.2734693877551024
            ]
          ],
          "eyes": [
            [
              0.2174999999999998,
              0.6174999999999999
            ]
          ],
          "ground": 0.8857421875,
          "hairBounds": {
            "0": [
              717.4715593434345,
              75.72627314814818,
              985.5692024410774,
              352.5485795454547
            ],
            "1": [
              712.1096064814816,
              60.9809027777778,
              980.2072495791246,
              339.14369739057247
            ],
            "2": [
              712.1096064814816,
              52.93797348484853,
              1004.3360374579124,
              349.86760311447824
            ],
            "3": [
              721.493023989899,
              73.04529671717177,
              1056.6150778619528,
              626.0081755050505
            ],
            "4": [
              698.7047243265994,
              52.93797348484853,
              1048.5721485690235,
              416.892013888889
            ],
            "5": [
              732.2169297138047,
              73.04529671717177,
              966.8023674242424,
              329.760279882155
            ],
            "6": [
              716.1310711279461,
              70.36432028619532,
              1024.4433606902355,
              371.3154145622896
            ],
            "7": [
              726.8549768518519,
              73.04529671717177,
              1045.891172138047,
              528.1525357744108
            ],
            "8": [
              704.0666771885523,
              75.72627314814818,
              1103.5321654040404,
              612.6032933501684
            ],
            "9": [
              716.1310711279461,
              62.32139099326602,
              1060.6365425084175,
              386.06078493266006
            ]
          },
          "hairFit": {
            "fromX": 0.55078125,
            "fromY": 0.2021484375,
            "rotation": 0.0,
            "scale": 1.3404882154882152
          },
          "head": {
            "angle": 0.0,
            "scale": 0.13439790702160492,
            "x": 0.5500116795669368,
            "y": 0.20354942039207177
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.1673469387755104,
                  1.2734693877551024
                ]
              ],
              "eyes": [
                [
                  0.2174999999999998,
                  0.6174999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3404882154882152
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13439790702160492,
                "x": 0.5500116795669368,
                "y": 0.20354942039207177
              }
            },
            {
              "eyeScales": [
                [
                  1.0895238095238096,
                  1.326530612244898
                ]
              ],
              "eyes": [
                [
                  0.2174999999999998,
                  0.6174999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3404882154882152
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13439790702160492,
                "x": 0.5500116795669368,
                "y": 0.20445454915364583
              }
            },
            {
              "eyeScales": [
                [
                  1.0895238095238096,
                  1.2204081632653065
                ]
              ],
              "eyes": [
                [
                  0.2174999999999998,
                  0.6174999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3404882154882152
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13439790702160492,
                "x": 0.5500116795669368,
                "y": 0.22074686686197922
              }
            },
            {
              "eyeScales": [
                [
                  1.0895238095238096,
                  1.2734693877551024
                ]
              ],
              "eyes": [
                [
                  0.2174999999999998,
                  0.6174999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3404882154882152
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13439790702160492,
                "x": 0.5500116795669368,
                "y": 0.23432379828559038
              }
            },
            {
              "eyeScales": [
                [
                  1.1673469387755104,
                  1.326530612244898
                ]
              ],
              "eyes": [
                [
                  0.2174999999999998,
                  0.6174999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3404882154882152
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13439790702160492,
                "x": 0.5500116795669368,
                "y": 0.23063184950086815
              }
            },
            {
              "eyeScales": [
                [
                  1.1673469387755104,
                  1.326530612244898
                ]
              ],
              "eyes": [
                [
                  0.2174999999999998,
                  0.6174999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3404882154882152
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13439790702160492,
                "x": 0.5500116795669368,
                "y": 0.2125292742693866
              }
            },
            {
              "eyeScales": [
                [
                  1.1673469387755104,
                  1.326530612244898
                ]
              ],
              "eyes": [
                [
                  0.2174999999999998,
                  0.6174999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3404882154882152
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13439790702160492,
                "x": 0.5500116795669368,
                "y": 0.21712635181568288
              }
            },
            {
              "eyeScales": [
                [
                  1.1673469387755104,
                  1.2734693877551024
                ]
              ],
              "eyes": [
                [
                  0.2174999999999998,
                  0.6174999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3404882154882152
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13439790702160492,
                "x": 0.5500116795669368,
                "y": 0.20898019296151618
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "tee-jeans-0-backward.png",
              "forward": "tee-jeans-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "tee-jeans-1-backward.png",
              "forward": "tee-jeans-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "tee-jeans-2-backward.png",
              "forward": "tee-jeans-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "tee-jeans-3-backward.png",
              "forward": "tee-jeans-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "tee-jeans-4-backward.png",
              "forward": "tee-jeans-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "tee-jeans-5-backward.png",
              "forward": "tee-jeans-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "tee-jeans-6-backward.png",
              "forward": "tee-jeans-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "2": {
          "bounds": [
            125,
            7,
            892,
            918
          ],
          "eyeScales": [
            [
              1.078723404255319,
              1.3012572533849132
            ]
          ],
          "eyes": [
            [
              0.2200000000000008,
              0.6200000000000001
            ]
          ],
          "ground": 0.87890625,
          "hairBounds": {
            "0": [
              739.174739010989,
              62.31601648351648,
              1000.6445192307692,
              316.0181971153847
            ],
            "1": [
              733.9453434065933,
              47.935178571428565,
              995.4151236263735,
              302.9447081043956
            ],
            "2": [
              733.9453434065933,
              40.091085164835164,
              1018.9474038461537,
              313.4034993131869
            ],
            "3": [
              743.0967857142857,
              59.701318681318696,
              1069.934010989011,
              582.7173729395605
            ],
            "4": [
              720.8718543956043,
              40.091085164835164,
              1062.0899175824175,
              378.77094436813195
            ],
            "5": [
              753.5555769230768,
              59.701318681318696,
              982.3416346153847,
              293.79326579670334
            ],
            "6": [
              737.86739010989,
              57.086620879120886,
              1038.5576373626373,
              334.32108173076927
            ],
            "7": [
              748.3261813186814,
              59.701318681318696,
              1059.4752197802197,
              487.2809031593407
            ],
            "8": [
              726.1012499999999,
              62.31601648351648,
              1115.6912225274725,
              569.6438839285715
            ],
            "9": [
              737.86739010989,
              49.242527472527485,
              1073.8560576923076,
              348.7019196428572
            ]
          },
          "hairFit": {
            "fromX": 0.55078125,
            "fromY": 0.2021484375,
            "rotation": 0.0,
            "scale": 1.3073489010989012
          },
          "head": {
            "angle": 0.0,
            "scale": 0.13107534555288464,
            "x": 0.5620917217548076,
            "y": 0.19007326566256005
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.078723404255319,
                  1.3012572533849132
                ]
              ],
              "eyes": [
                [
                  0.2200000000000008,
                  0.6200000000000001
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3073489010989012
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13107534555288464,
                "x": 0.5620917217548076,
                "y": 0.19007326566256005
              }
            },
            {
              "eyeScales": [
                [
                  1.078723404255319,
                  1.3012572533849132
                ]
              ],
              "eyes": [
                [
                  0.2200000000000008,
                  0.6200000000000001
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3073489010989012
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13107534555288464,
                "x": 0.5620917217548076,
                "y": 0.1928616333007812
              }
            },
            {
              "eyeScales": [
                [
                  1.078723404255319,
                  1.3012572533849132
                ]
              ],
              "eyes": [
                [
                  0.2200000000000008,
                  0.6199999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3073489010989012
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13107534555288464,
                "x": 0.5620917217548076,
                "y": 0.19383819580078127
              }
            },
            {
              "eyeScales": [
                [
                  1.078723404255319,
                  1.3012572533849132
                ]
              ],
              "eyes": [
                [
                  0.2200000000000008,
                  0.6200000000000001
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3073489010989012
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13107534555288464,
                "x": 0.5620917217548076,
                "y": 0.20136805607722358
              }
            },
            {
              "eyeScales": [
                [
                  1.2446808510638296,
                  1.3578336557059962
                ]
              ],
              "eyes": [
                [
                  0.2200000000000008,
                  0.6200000000000001
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3073489010989012
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13107534555288464,
                "x": 0.5620917217548076,
                "y": 0.19666189340444712
              }
            },
            {
              "eyeScales": [
                [
                  1.1617021276595745,
                  1.3578336557059962
                ]
              ],
              "eyes": [
                [
                  0.2200000000000008,
                  0.6199999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3073489010989012
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13107534555288464,
                "x": 0.5620917217548076,
                "y": 0.19568533090444712
              }
            },
            {
              "eyeScales": [
                [
                  1.1617021276595745,
                  1.3578336557059962
                ]
              ],
              "eyes": [
                [
                  0.2200000000000008,
                  0.6200000000000001
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3073489010989012
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13107534555288464,
                "x": 0.5620917217548076,
                "y": 0.18724956805889426
              }
            },
            {
              "eyeScales": [
                [
                  1.1617021276595745,
                  1.3578336557059962
                ]
              ],
              "eyes": [
                [
                  0.2200000000000008,
                  0.6199999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3073489010989012
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13107534555288464,
                "x": 0.5620917217548076,
                "y": 0.18724956805889426
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "dress-0-backward.png",
              "forward": "dress-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "dress-1-backward.png",
              "forward": "dress-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "dress-2-backward.png",
              "forward": "dress-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "dress-3-backward.png",
              "forward": "dress-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "dress-4-backward.png",
              "forward": "dress-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "dress-5-backward.png",
              "forward": "dress-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "dress-6-backward.png",
              "forward": "dress-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "3": {
          "bounds": [
            125,
            39,
            911,
            886
          ],
          "eyeScales": [
            [
              1.1522727272727271,
              1.3295454545454546
            ]
          ],
          "eyes": [
            [
              0.21750000000000025,
              0.6200000000000001
            ]
          ],
          "ground": 0.8779296875,
          "hairBounds": {
            "0": [
              716.0206682027649,
              86.73762672811048,
              989.7533870967742,
              341.5143548387097
            ],
            "1": [
              710.5460138248848,
              71.68232718893998,
              984.2787327188939,
              327.8277188940092
            ],
            "2": [
              710.5460138248848,
              63.47034562211971,
              1008.9146774193548,
              338.77702764976965
            ],
            "3": [
              720.1266589861751,
              84.00029953917041,
              1062.2925576036866,
              620.7217281105991
            ],
            "4": [
              696.8593778801842,
              63.47034562211971,
              1054.0805760368662,
              407.21020737327194
            ],
            "5": [
              731.0759677419354,
              84.00029953917041,
              970.5920967741936,
              318.24707373271895
            ],
            "6": [
              714.6520046082949,
              81.26297235023031,
              1029.4446313364053,
              360.67564516129033
            ],
            "7": [
              725.6013133640553,
              84.00029953917041,
              1051.3432488479261,
              520.8092857142858
            ],
            "8": [
              702.3340322580644,
              86.73762672811048,
              1110.1957834101383,
              607.0350921658986
            ],
            "9": [
              714.6520046082949,
              73.05099078341001,
              1066.3985483870968,
              375.7309447004609
            ]
          },
          "hairFit": {
            "fromX": 0.55078125,
            "fromY": 0.2021484375,
            "rotation": 0.0,
            "scale": 1.3686635944700463
          },
          "head": {
            "angle": 0.0,
            "scale": 0.13722278225806453,
            "x": 0.5508097068212365,
            "y": 0.21702668220766125
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.1522727272727271,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000025,
                  0.6200000000000001
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3686635944700463
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13722278225806453,
                "x": 0.5508097068212365,
                "y": 0.21702668220766125
              }
            },
            {
              "eyeScales": [
                [
                  1.1522727272727271,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000025,
                  0.6200000000000001
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3686635944700463
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13722278225806453,
                "x": 0.5508097068212365,
                "y": 0.2170266822076612
              }
            },
            {
              "eyeScales": [
                [
                  1.1522727272727271,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000025,
                  0.6200000000000001
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3686635944700463
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13722278225806453,
                "x": 0.5508097068212365,
                "y": 0.2170266822076612
              }
            },
            {
              "eyeScales": [
                [
                  1.1522727272727271,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000025,
                  0.6200000000000001
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3686635944700463
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13722278225806453,
                "x": 0.5508097068212365,
                "y": 0.2170266822076612
              }
            },
            {
              "eyeScales": [
                [
                  1.240909090909091,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000025,
                  0.6199999999999999
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3686635944700463
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13722278225806453,
                "x": 0.5508097068212365,
                "y": 0.22123692666330647
              }
            },
            {
              "eyeScales": [
                [
                  1.1522727272727271,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000025,
                  0.6200000000000001
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3686635944700463
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13722278225806453,
                "x": 0.5508097068212365,
                "y": 0.22026036416330635
              }
            },
            {
              "eyeScales": [
                [
                  1.240909090909091,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000025,
                  0.6200000000000001
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3686635944700463
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13722278225806453,
                "x": 0.5508097068212365,
                "y": 0.22123692666330635
              }
            },
            {
              "eyeScales": [
                [
                  1.1522727272727271,
                  1.3295454545454546
                ]
              ],
              "eyes": [
                [
                  0.21750000000000025,
                  0.6200000000000001
                ]
              ],
              "hairFit": {
                "fromX": 0.55078125,
                "fromY": 0.2021484375,
                "rotation": 0.0,
                "scale": 1.3686635944700463
              },
              "head": {
                "angle": 0.0,
                "scale": 0.13722278225806453,
                "x": 0.5508097068212365,
                "y": 0.2181552419354838
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "shirt-skirt-0-backward.png",
              "forward": "shirt-skirt-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "shirt-skirt-1-backward.png",
              "forward": "shirt-skirt-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "shirt-skirt-2-backward.png",
              "forward": "shirt-skirt-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "shirt-skirt-3-backward.png",
              "forward": "shirt-skirt-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "shirt-skirt-4-backward.png",
              "forward": "shirt-skirt-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "shirt-skirt-5-backward.png",
              "forward": "shirt-skirt-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "shirt-skirt-6-backward.png",
              "forward": "shirt-skirt-6-forward.png",
              "scale": 768.0
            }
          }
        }
      },
      "outfitFrames": {
        "0": 8,
        "1": 8,
        "2": 8,
        "3": 8
      },
      "palette": "storybook-v21",
      "playback": {
        "fps": 1.4,
        "interpolate": true,
        "order": [
          0,
          0,
          1,
          2,
          3,
          4,
          5,
          6,
          7,
          7,
          6,
          5,
          4,
          3,
          2,
          1
        ]
      },
      "posture": "sitting",
      "station": "river",
      "status": "V22 eight complete animation drawings; extracted cutouts and per-frame fitted overlays"
    },
    "width": 1024
  },
  "fishing-standing": {
    "frames": {
      "0": [
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-shorts-a-garment",
          "props": "act-fishing-standing-tee-shorts-a-props",
          "skin": "act-fishing-standing-tee-shorts-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-shorts-b-garment",
          "props": "act-fishing-standing-tee-shorts-b-props",
          "skin": "act-fishing-standing-tee-shorts-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-shorts-c-garment",
          "props": "act-fishing-standing-tee-shorts-c-props",
          "skin": "act-fishing-standing-tee-shorts-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-shorts-d-garment",
          "props": "act-fishing-standing-tee-shorts-d-props",
          "skin": "act-fishing-standing-tee-shorts-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-shorts-e-garment",
          "props": "act-fishing-standing-tee-shorts-e-props",
          "skin": "act-fishing-standing-tee-shorts-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-shorts-f-garment",
          "props": "act-fishing-standing-tee-shorts-f-props",
          "skin": "act-fishing-standing-tee-shorts-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-shorts-g-garment",
          "props": "act-fishing-standing-tee-shorts-g-props",
          "skin": "act-fishing-standing-tee-shorts-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-shorts-h-garment",
          "props": "act-fishing-standing-tee-shorts-h-props",
          "skin": "act-fishing-standing-tee-shorts-h-skin",
          "tongue": null
        }
      ],
      "1": [
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-jeans-a-garment",
          "props": "act-fishing-standing-tee-jeans-a-props",
          "skin": "act-fishing-standing-tee-jeans-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-jeans-b-garment",
          "props": "act-fishing-standing-tee-jeans-b-props",
          "skin": "act-fishing-standing-tee-jeans-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-jeans-c-garment",
          "props": "act-fishing-standing-tee-jeans-c-props",
          "skin": "act-fishing-standing-tee-jeans-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-jeans-d-garment",
          "props": "act-fishing-standing-tee-jeans-d-props",
          "skin": "act-fishing-standing-tee-jeans-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-jeans-e-garment",
          "props": "act-fishing-standing-tee-jeans-e-props",
          "skin": "act-fishing-standing-tee-jeans-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-jeans-f-garment",
          "props": "act-fishing-standing-tee-jeans-f-props",
          "skin": "act-fishing-standing-tee-jeans-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-jeans-g-garment",
          "props": "act-fishing-standing-tee-jeans-g-props",
          "skin": "act-fishing-standing-tee-jeans-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-tee-jeans-h-garment",
          "props": "act-fishing-standing-tee-jeans-h-props",
          "skin": "act-fishing-standing-tee-jeans-h-skin",
          "tongue": null
        }
      ],
      "2": [
        {
          "brows": null,
          "garment": "act-fishing-standing-dress-a-garment",
          "props": "act-fishing-standing-dress-a-props",
          "skin": "act-fishing-standing-dress-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-dress-b-garment",
          "props": "act-fishing-standing-dress-b-props",
          "skin": "act-fishing-standing-dress-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-dress-c-garment",
          "props": "act-fishing-standing-dress-c-props",
          "skin": "act-fishing-standing-dress-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-dress-d-garment",
          "props": "act-fishing-standing-dress-d-props",
          "skin": "act-fishing-standing-dress-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-dress-e-garment",
          "props": "act-fishing-standing-dress-e-props",
          "skin": "act-fishing-standing-dress-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-dress-f-garment",
          "props": "act-fishing-standing-dress-f-props",
          "skin": "act-fishing-standing-dress-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-dress-g-garment",
          "props": "act-fishing-standing-dress-g-props",
          "skin": "act-fishing-standing-dress-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-dress-h-garment",
          "props": "act-fishing-standing-dress-h-props",
          "skin": "act-fishing-standing-dress-h-skin",
          "tongue": null
        }
      ],
      "3": [
        {
          "brows": null,
          "garment": "act-fishing-standing-shirt-skirt-a-garment",
          "props": "act-fishing-standing-shirt-skirt-a-props",
          "skin": "act-fishing-standing-shirt-skirt-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-shirt-skirt-b-garment",
          "props": "act-fishing-standing-shirt-skirt-b-props",
          "skin": "act-fishing-standing-shirt-skirt-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-shirt-skirt-c-garment",
          "props": "act-fishing-standing-shirt-skirt-c-props",
          "skin": "act-fishing-standing-shirt-skirt-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-shirt-skirt-d-garment",
          "props": "act-fishing-standing-shirt-skirt-d-props",
          "skin": "act-fishing-standing-shirt-skirt-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-shirt-skirt-e-garment",
          "props": "act-fishing-standing-shirt-skirt-e-props",
          "skin": "act-fishing-standing-shirt-skirt-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-shirt-skirt-f-garment",
          "props": "act-fishing-standing-shirt-skirt-f-props",
          "skin": "act-fishing-standing-shirt-skirt-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-shirt-skirt-g-garment",
          "props": "act-fishing-standing-shirt-skirt-g-props",
          "skin": "act-fishing-standing-shirt-skirt-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-fishing-standing-shirt-skirt-h-garment",
          "props": "act-fishing-standing-shirt-skirt-h-props",
          "skin": "act-fishing-standing-shirt-skirt-h-skin",
          "tongue": null
        }
      ]
    },
    "hair": {
      "0": "act-fishing-standing-hair-0",
      "1": "act-fishing-standing-hair-1",
      "2": "act-fishing-standing-hair-2",
      "3": "act-fishing-standing-hair-3",
      "4": "act-fishing-standing-hair-4",
      "5": "act-fishing-standing-hair-5",
      "6": "act-fishing-standing-hair-6",
      "7": "act-fishing-standing-hair-7",
      "8": "act-fishing-standing-hair-8",
      "9": "act-fishing-standing-hair-9"
    },
    "height": 683,
    "meta": {
      "availableOutfits": [
        0,
        1,
        2,
        3
      ],
      "bounds": [
        125,
        60,
        870,
        930
      ],
      "camera": "side",
      "canvas": [
        1536,
        1024
      ],
      "eyeScale": [
        1.2,
        1.2
      ],
      "eyes": [
        [
          0.2292682926829268,
          0.6146341463414634
        ]
      ],
      "frames": 8,
      "front": [
        "water"
      ],
      "ground": 0.936,
      "hairBounds": {
        "0": [
          773.4989381067962,
          81.97866100323623,
          1026.1726334951456,
          316.5116808252427
        ],
        "1": [
          764.4286003236248,
          84.57018608414239,
          1026.1726334951456,
          300.9625303398058
        ],
        "2": [
          765.7243628640778,
          59.95069781553397,
          1043.0175465210357,
          311.32863066343043
        ],
        "3": [
          773.4989381067962,
          92.34476132686083,
          1076.7073725728155,
          554.9319882686084
        ],
        "4": [
          769.611650485437,
          58.6549352750809,
          1092.2565230582525,
          385.1870954692556
        ],
        "5": [
          782.5692758899678,
          76.79561084142395,
          1005.4404328478965,
          286.709142394822
        ],
        "6": [
          765.7243628640778,
          93.6405238673139,
          1063.749747168285,
          324.2862560679612
        ],
        "7": [
          772.2031755663431,
          81.97866100323623,
          1045.6090716019419,
          497.91843648867314
        ],
        "8": [
          752.766737459547,
          74.20408576051778,
          1124.6505865695794,
          557.5235133495146
        ],
        "9": [
          739.8091120550163,
          74.20408576051778,
          1076.7073725728155,
          355.38455703883494
        ]
      },
      "hairFit": {
        "fromX": 0.563,
        "fromY": 0.2,
        "scale": 1.2957625404530744
      },
      "hairReady": false,
      "hairStyles": [
        0,
        1,
        2,
        3,
        4,
        5,
        6,
        7,
        8,
        9
      ],
      "head": {
        "angle": 0,
        "scale": 0.13346354166666666,
        "x": 0.5826822916666666,
        "y": 0.205078125
      },
      "hold": [
        2.4,
        4
      ],
      "id": "fishing-standing",
      "outfitFits": {
        "0": {
          "bounds": [
            125,
            10,
            922,
            980
          ],
          "eyeScales": [
            [
              1.036691410392365,
              1.2958642629904562
            ]
          ],
          "eyes": [
            [
              0.22926829268292692,
              0.6146341463414635
            ]
          ],
          "ground": 0.943359375,
          "hairBounds": {
            "0": [
              767.6812153259364,
              49.98603075203471,
              1042.627060155812,
              329.6518012310094
            ],
            "1": [
              757.8113644858895,
              52.80598813490525,
              1042.627060155812,
              312.73205693378634
            ],
            "2": [
              759.2213431773247,
              26.016392997635307,
              1060.9567831444704,
              324.0118864652684
            ],
            "3": [
              767.6812153259364,
              61.265860283516815,
              1097.616229121787,
              589.0878804550973
            ],
            "4": [
              763.4512792516306,
              24.606414306200065,
              1114.5359734190101,
              404.38067187707816
            ],
            "5": [
              777.5510661659832,
              44.34611598629371,
              1020.0674010928478,
              297.22229132799845
            ],
            "6": [
              759.2213431773247,
              62.67583897495207,
              1083.5164422074347,
              338.11167337962104
            ],
            "7": [
              766.2712366345011,
              49.98603075203471,
              1063.776740527341,
              527.0488180319459
            ],
            "8": [
              745.1215562629721,
              41.52615860342317,
              1149.785440704892,
              591.9078378379679
            ],
            "9": [
              731.0217693486195,
              41.52615860342317,
              1097.616229121787,
              371.95116197406725
            ]
          },
          "hairFit": {
            "fromX": 0.563,
            "fromY": 0.2,
            "rotation": 0.0,
            "scale": 1.4099786914352603
          },
          "head": {
            "angle": 0.0,
            "scale": 0.1452278052178318,
            "x": 0.5858672508232016,
            "y": 0.19560364906832295
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.036691410392365,
                  1.2958642629904562
                ]
              ],
              "eyes": [
                [
                  0.22926829268292692,
                  0.6146341463414635
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.4099786914352603
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1452278052178318,
                "x": 0.5858672508232016,
                "y": 0.19560364906832295
              }
            },
            {
              "eyeScales": [
                [
                  1.1230823612583953,
                  1.2958642629904562
                ]
              ],
              "eyes": [
                [
                  0.22926829268292692,
                  0.6146341463414635
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.4099786914352603
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1452278052178318,
                "x": 0.5858672508232016,
                "y": 0.194627086568323
              }
            },
            {
              "eyeScales": [
                [
                  1.1230823612583953,
                  1.354767184035477
                ]
              ],
              "eyes": [
                [
                  0.22926829268292692,
                  0.6146341463414633
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.4099786914352603
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1452278052178318,
                "x": 0.5858672508232016,
                "y": 0.20559370147515527
              }
            },
            {
              "eyeScales": [
                [
                  1.036691410392365,
                  1.2958642629904562
                ]
              ],
              "eyes": [
                [
                  0.22926829268292692,
                  0.6146341463414633
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.4099786914352603
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1452278052178318,
                "x": 0.5858672508232016,
                "y": 0.20765600737577639
              }
            },
            {
              "eyeScales": [
                [
                  1.1230823612583953,
                  1.354767184035477
                ]
              ],
              "eyes": [
                [
                  0.22926829268292692,
                  0.6146341463414633
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.4099786914352603
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1452278052178318,
                "x": 0.5858672508232016,
                "y": 0.2087417507763975
              }
            },
            {
              "eyeScales": [
                [
                  1.1230823612583953,
                  1.354767184035477
                ]
              ],
              "eyes": [
                [
                  0.22926829268292692,
                  0.6146341463414633
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.4099786914352603
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1452278052178318,
                "x": 0.5858672508232016,
                "y": 0.20439877717391303
              }
            },
            {
              "eyeScales": [
                [
                  1.1230823612583953,
                  1.354767184035477
                ]
              ],
              "eyes": [
                [
                  0.22926829268292692,
                  0.6146341463414635
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.4099786914352603
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1452278052178318,
                "x": 0.5858672508232016,
                "y": 0.20657026397515527
              }
            },
            {
              "eyeScales": [
                [
                  1.1230823612583953,
                  1.354767184035477
                ]
              ],
              "eyes": [
                [
                  0.22926829268292692,
                  0.6146341463414635
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.4099786914352603
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1452278052178318,
                "x": 0.5858672508232016,
                "y": 0.18485539596273287
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "tee-shorts-0-backward.png",
              "forward": "tee-shorts-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "tee-shorts-1-backward.png",
              "forward": "tee-shorts-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "tee-shorts-2-backward.png",
              "forward": "tee-shorts-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "tee-shorts-3-backward.png",
              "forward": "tee-shorts-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "tee-shorts-4-backward.png",
              "forward": "tee-shorts-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "tee-shorts-5-backward.png",
              "forward": "tee-shorts-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "tee-shorts-6-backward.png",
              "forward": "tee-shorts-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "1": {
          "bounds": [
            125,
            7,
            937,
            983
          ],
          "eyeScales": [
            [
              1.19219512195122,
              1.408957871396896
            ]
          ],
          "eyes": [
            [
              0.22682926829268368,
              0.6195121951219512
            ]
          ],
          "ground": 0.9443359375,
          "hairBounds": {
            "0": [
              763.9726314285497,
              53.62671483120286,
              1045.6794396558814,
              320.34846571651826
            ],
            "1": [
              753.8600793383379,
              56.51601542840629,
              1045.6794396558814,
              303.01266213329785
            ],
            "2": [
              755.3047296369396,
              29.06765975497396,
              1064.4598935377035,
              314.5698645221115
            ],
            "3": [
              763.9726314285497,
              65.1839172200165,
              1102.0208013013475,
              586.1641206592312
            ],
            "4": [
              759.6386805327447,
              27.623009456372273,
              1119.3566048845682,
              396.91493154240834
            ],
            "5": [
              774.0851835187617,
              47.848113636796086,
              1022.5650348782542,
              287.1215088486791
            ],
            "6": [
              755.3047296369396,
              66.62856751861818,
              1087.574298315331,
              329.0163675081285
            ],
            "7": [
              762.527981129948,
              53.62671483120286,
              1067.349194134907,
              522.5995075207564
            ],
            "8": [
              740.8582266509225,
              44.958813039592656,
              1155.4728623496108,
              589.0534212564347
            ],
            "9": [
              726.4117236649055,
              44.958813039592656,
              1102.0208013013475,
              363.6879746745693
            ]
          },
          "hairFit": {
            "fromX": 0.563,
            "fromY": 0.2,
            "rotation": 0.0,
            "scale": 1.444650298601701
          },
          "head": {
            "angle": 0.0,
            "scale": 0.1487989807559752,
            "x": 0.5855694014504126,
            "y": 0.1948263598008745
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.19219512195122,
                  1.408957871396896
                ]
              ],
              "eyes": [
                [
                  0.22682926829268368,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.444650298601701
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1487989807559752,
                "x": 0.5855694014504126,
                "y": 0.1948263598008745
              }
            },
            {
              "eyeScales": [
                [
                  1.19219512195122,
                  1.3547671840354771
                ]
              ],
              "eyes": [
                [
                  0.22682926829268368,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.444650298601701
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1487989807559752,
                "x": 0.5855694014504126,
                "y": 0.19584980644900096
              }
            },
            {
              "eyeScales": [
                [
                  1.19219512195122,
                  1.3547671840354771
                ]
              ],
              "eyes": [
                [
                  0.22682926829268368,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.444650298601701
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1487989807559752,
                "x": 0.5855694014504126,
                "y": 0.19584980644900096
              }
            },
            {
              "eyeScales": [
                [
                  1.19219512195122,
                  1.3547671840354771
                ]
              ],
              "eyes": [
                [
                  0.22682926829268368,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.444650298601701
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1487989807559752,
                "x": 0.5855694014504126,
                "y": 0.19584980644900096
              }
            },
            {
              "eyeScales": [
                [
                  1.19219512195122,
                  1.408957871396896
                ]
              ],
              "eyes": [
                [
                  0.22682926829268368,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.444650298601701
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1487989807559752,
                "x": 0.5855694014504126,
                "y": 0.1968732530971274
              }
            },
            {
              "eyeScales": [
                [
                  1.2716747967479676,
                  1.408957871396896
                ]
              ],
              "eyes": [
                [
                  0.22682926829268368,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.444650298601701
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1487989807559752,
                "x": 0.5855694014504126,
                "y": 0.19580292230087448
              }
            },
            {
              "eyeScales": [
                [
                  1.2716747967479676,
                  1.3547671840354771
                ]
              ],
              "eyes": [
                [
                  0.22682926829268368,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.444650298601701
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1487989807559752,
                "x": 0.5855694014504126,
                "y": 0.19584980644900102
              }
            },
            {
              "eyeScales": [
                [
                  1.1127154471544718,
                  1.3547671840354771
                ]
              ],
              "eyes": [
                [
                  0.22682926829268368,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.444650298601701
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1487989807559752,
                "x": 0.5855694014504126,
                "y": 0.19175601985649507
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "tee-jeans-0-backward.png",
              "forward": "tee-jeans-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "tee-jeans-1-backward.png",
              "forward": "tee-jeans-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "tee-jeans-2-backward.png",
              "forward": "tee-jeans-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "tee-jeans-3-backward.png",
              "forward": "tee-jeans-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "tee-jeans-4-backward.png",
              "forward": "tee-jeans-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "tee-jeans-5-backward.png",
              "forward": "tee-jeans-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "tee-jeans-6-backward.png",
              "forward": "tee-jeans-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "2": {
          "bounds": [
            125,
            5,
            974,
            985
          ],
          "eyeScales": [
            [
              1.2352067868504775,
              1.3835920177383592
            ]
          ],
          "eyes": [
            [
              0.22926829268292703,
              0.6170731707317074
            ]
          ],
          "ground": 0.9482421875,
          "hairBounds": {
            "0": [
              755.4303278159302,
              59.35259040906587,
              1016.02334134127,
              336.75040211628607
            ],
            "1": [
              746.0757068175848,
              62.02533926573605,
              1016.02334134127,
              320.7139089762652
            ],
            "2": [
              747.4120812459198,
              36.634225127369604,
              1033.396208909626,
              331.4049044029458
            ],
            "3": [
              755.4303278159302,
              70.0435858357465,
              1068.1419440463378,
              582.6432969299401
            ],
            "4": [
              751.421204530925,
              35.29785069903451,
              1084.178437186359,
              407.57824681804505
            ],
            "5": [
              764.7849488142758,
              54.00709269572559,
              994.6413504879088,
              306.0137902645793
            ],
            "6": [
              747.4120812459198,
              71.37996026408156,
              1054.7781997629872,
              344.76864868629656
            ],
            "7": [
              754.0939533875951,
              59.35259040906587,
              1036.0689577662963,
              523.8428220831968
            ],
            "8": [
              734.048336962569,
              51.334343839055435,
              1117.5877978947358,
              585.3160457866102
            ],
            "9": [
              720.6845926792182,
              51.334343839055435,
              1068.1419440463378,
              376.84163496633835
            ]
          },
          "hairFit": {
            "fromX": 0.563,
            "fromY": 0.2,
            "rotation": 0.0,
            "scale": 1.336374428335076
          },
          "head": {
            "angle": 0.0,
            "scale": 0.1376465661185128,
            "x": 0.5733981023515974,
            "y": 0.1869007655552455
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.2352067868504775,
                  1.3835920177383592
                ]
              ],
              "eyes": [
                [
                  0.22926829268292703,
                  0.6170731707317074
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.336374428335076
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1376465661185128,
                "x": 0.5733981023515974,
                "y": 0.1869007655552455
              }
            },
            {
              "eyeScales": [
                [
                  1.1469777306468716,
                  1.3234358430540831
                ]
              ],
              "eyes": [
                [
                  0.22926829268292703,
                  0.6170731707317074
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.336374428335076
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1376465661185128,
                "x": 0.5733981023515974,
                "y": 0.19733596801757816
              }
            },
            {
              "eyeScales": [
                [
                  1.1469777306468716,
                  1.3835920177383592
                ]
              ],
              "eyes": [
                [
                  0.22926829268292703,
                  0.6170731707317074
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.336374428335076
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1376465661185128,
                "x": 0.5733981023515974,
                "y": 0.2067946079799107
              }
            },
            {
              "eyeScales": [
                [
                  1.1469777306468716,
                  1.3234358430540831
                ]
              ],
              "eyes": [
                [
                  0.22926829268292703,
                  0.6170731707317074
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.336374428335076
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1376465661185128,
                "x": 0.5733981023515974,
                "y": 0.22158244541713168
              }
            },
            {
              "eyeScales": [
                [
                  1.1469777306468716,
                  1.4437481924226359
                ]
              ],
              "eyes": [
                [
                  0.22926829268292703,
                  0.6170731707317074
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.336374428335076
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1376465661185128,
                "x": 0.5733981023515974,
                "y": 0.1973359680175782
              }
            },
            {
              "eyeScales": [
                [
                  1.2352067868504775,
                  1.4437481924226359
                ]
              ],
              "eyes": [
                [
                  0.22926829268292703,
                  0.6170731707317074
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.336374428335076
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1376465661185128,
                "x": 0.5733981023515974,
                "y": 0.19846132550920764
              }
            },
            {
              "eyeScales": [
                [
                  1.1469777306468716,
                  1.3835920177383592
                ]
              ],
              "eyes": [
                [
                  0.22926829268292703,
                  0.6170731707317074
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.336374428335076
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1376465661185128,
                "x": 0.5733981023515974,
                "y": 0.1920811680385045
              }
            },
            {
              "eyeScales": [
                [
                  1.1469777306468716,
                  1.4437481924226359
                ]
              ],
              "eyes": [
                [
                  0.22926829268292703,
                  0.6170731707317074
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.336374428335076
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1376465661185128,
                "x": 0.5733981023515974,
                "y": 0.18892828805106032
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "dress-0-backward.png",
              "forward": "dress-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "dress-1-backward.png",
              "forward": "dress-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "dress-2-backward.png",
              "forward": "dress-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "dress-3-backward.png",
              "forward": "dress-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "dress-4-backward.png",
              "forward": "dress-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "dress-5-backward.png",
              "forward": "dress-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "dress-6-backward.png",
              "forward": "dress-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "3": {
          "bounds": [
            125,
            10,
            955,
            980
          ],
          "eyeScales": [
            [
              1.1220867208672087,
              1.2947154471544715
            ]
          ],
          "eyes": [
            [
              0.2292682926829271,
              0.6195121951219512
            ]
          ],
          "ground": 0.9482421875,
          "hairBounds": {
            "0": [
              769.4674625302974,
              40.63821617887737,
              1028.0041483344235,
              289.01397870293215
            ],
            "1": [
              760.1866584245083,
              43.289874494817155,
              1028.0041483344235,
              273.1040288072936
            ],
            "2": [
              761.5124875824781,
              18.099120493389478,
              1045.239927388032,
              283.71066207105264
            ],
            "3": [
              769.4674625302974,
              51.24484944263641,
              1079.7114854952488,
              532.9665437693897
            ],
            "4": [
              765.4899750563878,
              16.7732913354196,
              1095.6214353908872,
              359.2829240753356
            ],
            "5": [
              778.7482666360867,
              35.33489954699789,
              1006.7908818069055,
              258.51990806962493
            ],
            "6": [
              761.5124875824781,
              52.570678600606286,
              1066.4531939155502,
              296.9689536507514
            ],
            "7": [
              768.1416333723275,
              40.63821617887737,
              1047.8915857039717,
              474.63006081871504
            ],
            "8": [
              748.2541960027794,
              32.683241231058105,
              1128.7671643401343,
              535.6182020853295
            ],
            "9": [
              734.9959044230807,
              32.683241231058105,
              1079.7114854952488,
              328.7888534420285
            ]
          },
          "hairFit": {
            "fromX": 0.563,
            "fromY": 0.2,
            "rotation": 0.0,
            "scale": 1.3258291579698778
          },
          "head": {
            "angle": 0.0,
            "scale": 0.1365604032708974,
            "x": 0.581893106129438,
            "y": 0.1676075556506849
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.1220867208672087,
                  1.2947154471544715
                ]
              ],
              "eyes": [
                [
                  0.2292682926829271,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.3258291579698778
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1365604032708974,
                "x": 0.581893106129438,
                "y": 0.1676075556506849
              }
            },
            {
              "eyeScales": [
                [
                  1.1220867208672087,
                  1.4124168514412418
                ]
              ],
              "eyes": [
                [
                  0.2292682926829271,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.3258291579698778
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1365604032708974,
                "x": 0.581893106129438,
                "y": 0.17381474743150677
              }
            },
            {
              "eyeScales": [
                [
                  1.1220867208672087,
                  1.4124168514412418
                ]
              ],
              "eyes": [
                [
                  0.2292682926829271,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.3258291579698778
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1365604032708974,
                "x": 0.581893106129438,
                "y": 0.17479130993150682
              }
            },
            {
              "eyeScales": [
                [
                  1.1220867208672087,
                  1.3535661492978568
                ]
              ],
              "eyes": [
                [
                  0.2292682926829271,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.3258291579698778
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1365604032708974,
                "x": 0.581893106129438,
                "y": 0.17581134952910957
              }
            },
            {
              "eyeScales": [
                [
                  1.1220867208672087,
                  1.4124168514412418
                ]
              ],
              "eyes": [
                [
                  0.2292682926829271,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.3258291579698778
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1365604032708974,
                "x": 0.581893106129438,
                "y": 0.17275123073630128
              }
            },
            {
              "eyeScales": [
                [
                  1.1220867208672087,
                  1.471267553584627
                ]
              ],
              "eyes": [
                [
                  0.2292682926829271,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.3258291579698778
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1365604032708974,
                "x": 0.581893106129438,
                "y": 0.17173119113869853
              }
            },
            {
              "eyeScales": [
                [
                  1.1220867208672087,
                  1.471267553584627
                ]
              ],
              "eyes": [
                [
                  0.2292682926829271,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.3258291579698778
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1365604032708974,
                "x": 0.581893106129438,
                "y": 0.17173119113869859
              }
            },
            {
              "eyeScales": [
                [
                  1.2084010840108401,
                  1.471267553584627
                ]
              ],
              "eyes": [
                [
                  0.2292682926829271,
                  0.6195121951219512
                ]
              ],
              "hairFit": {
                "fromX": 0.563,
                "fromY": 0.2,
                "rotation": 0.0,
                "scale": 1.3258291579698778
              },
              "head": {
                "angle": 0.0,
                "scale": 0.1365604032708974,
                "x": 0.581893106129438,
                "y": 0.17173119113869856
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "shirt-skirt-0-backward.png",
              "forward": "shirt-skirt-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "shirt-skirt-1-backward.png",
              "forward": "shirt-skirt-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "shirt-skirt-2-backward.png",
              "forward": "shirt-skirt-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "shirt-skirt-3-backward.png",
              "forward": "shirt-skirt-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "shirt-skirt-4-backward.png",
              "forward": "shirt-skirt-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "shirt-skirt-5-backward.png",
              "forward": "shirt-skirt-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "shirt-skirt-6-backward.png",
              "forward": "shirt-skirt-6-forward.png",
              "scale": 768.0
            }
          }
        }
      },
      "outfitFrames": {
        "0": 8,
        "1": 8,
        "2": 8,
        "3": 8
      },
      "palette": "storybook-v21",
      "playback": {
        "fps": 1.25,
        "interpolate": true,
        "order": [
          0,
          0,
          1,
          2,
          3,
          4,
          5,
          6,
          7,
          7,
          6,
          5,
          4,
          3,
          2,
          1
        ]
      },
      "posture": "standing",
      "station": "river",
      "status": "V22 eight complete animation drawings; extracted cutouts and per-frame fitted overlays"
    },
    "width": 1024
  },
  "lying": {
    "frames": {
      "0": [
        {
          "brows": null,
          "garment": "act-lying-tee-shorts-a-garment",
          "props": "act-lying-tee-shorts-a-props",
          "skin": "act-lying-tee-shorts-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-tee-shorts-b-garment",
          "props": "act-lying-tee-shorts-b-props",
          "skin": "act-lying-tee-shorts-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-tee-shorts-c-garment",
          "props": "act-lying-tee-shorts-c-props",
          "skin": "act-lying-tee-shorts-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-tee-shorts-d-garment",
          "props": "act-lying-tee-shorts-d-props",
          "skin": "act-lying-tee-shorts-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-tee-shorts-e-garment",
          "props": "act-lying-tee-shorts-e-props",
          "skin": "act-lying-tee-shorts-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-tee-shorts-f-garment",
          "props": "act-lying-tee-shorts-f-props",
          "skin": "act-lying-tee-shorts-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-tee-shorts-g-garment",
          "props": "act-lying-tee-shorts-g-props",
          "skin": "act-lying-tee-shorts-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-tee-shorts-h-garment",
          "props": "act-lying-tee-shorts-h-props",
          "skin": "act-lying-tee-shorts-h-skin",
          "tongue": null
        }
      ],
      "1": [
        {
          "brows": "act-lying-tee-jeans-a-brows",
          "garment": "act-lying-tee-jeans-a-garment",
          "props": "act-lying-tee-jeans-a-props",
          "skin": "act-lying-tee-jeans-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-tee-jeans-b-garment",
          "props": "act-lying-tee-jeans-b-props",
          "skin": "act-lying-tee-jeans-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-tee-jeans-c-garment",
          "props": "act-lying-tee-jeans-c-props",
          "skin": "act-lying-tee-jeans-c-skin",
          "tongue": null
        },
        {
          "brows": "act-lying-tee-jeans-d-brows",
          "garment": "act-lying-tee-jeans-d-garment",
          "props": "act-lying-tee-jeans-d-props",
          "skin": "act-lying-tee-jeans-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-tee-jeans-e-garment",
          "props": "act-lying-tee-jeans-e-props",
          "skin": "act-lying-tee-jeans-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-tee-jeans-f-garment",
          "props": "act-lying-tee-jeans-f-props",
          "skin": "act-lying-tee-jeans-f-skin",
          "tongue": null
        },
        {
          "brows": "act-lying-tee-jeans-g-brows",
          "garment": "act-lying-tee-jeans-g-garment",
          "props": "act-lying-tee-jeans-g-props",
          "skin": "act-lying-tee-jeans-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-tee-jeans-h-garment",
          "props": "act-lying-tee-jeans-h-props",
          "skin": "act-lying-tee-jeans-h-skin",
          "tongue": null
        }
      ],
      "2": [
        {
          "brows": null,
          "garment": "act-lying-dress-a-garment",
          "props": "act-lying-dress-a-props",
          "skin": "act-lying-dress-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-dress-b-garment",
          "props": "act-lying-dress-b-props",
          "skin": "act-lying-dress-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-dress-c-garment",
          "props": "act-lying-dress-c-props",
          "skin": "act-lying-dress-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-dress-d-garment",
          "props": "act-lying-dress-d-props",
          "skin": "act-lying-dress-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-dress-e-garment",
          "props": "act-lying-dress-e-props",
          "skin": "act-lying-dress-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-dress-f-garment",
          "props": "act-lying-dress-f-props",
          "skin": "act-lying-dress-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-dress-g-garment",
          "props": "act-lying-dress-g-props",
          "skin": "act-lying-dress-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-dress-h-garment",
          "props": "act-lying-dress-h-props",
          "skin": "act-lying-dress-h-skin",
          "tongue": null
        }
      ],
      "3": [
        {
          "brows": null,
          "garment": "act-lying-shirt-skirt-a-garment",
          "props": "act-lying-shirt-skirt-a-props",
          "skin": "act-lying-shirt-skirt-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-shirt-skirt-b-garment",
          "props": "act-lying-shirt-skirt-b-props",
          "skin": "act-lying-shirt-skirt-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-shirt-skirt-c-garment",
          "props": "act-lying-shirt-skirt-c-props",
          "skin": "act-lying-shirt-skirt-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-shirt-skirt-d-garment",
          "props": "act-lying-shirt-skirt-d-props",
          "skin": "act-lying-shirt-skirt-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-shirt-skirt-e-garment",
          "props": "act-lying-shirt-skirt-e-props",
          "skin": "act-lying-shirt-skirt-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-shirt-skirt-f-garment",
          "props": "act-lying-shirt-skirt-f-props",
          "skin": "act-lying-shirt-skirt-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-shirt-skirt-g-garment",
          "props": "act-lying-shirt-skirt-g-props",
          "skin": "act-lying-shirt-skirt-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-lying-shirt-skirt-h-garment",
          "props": "act-lying-shirt-skirt-h-props",
          "skin": "act-lying-shirt-skirt-h-skin",
          "tongue": null
        }
      ]
    },
    "hair": {
      "0": "act-lying-hair-0",
      "1": "act-lying-hair-1",
      "2": "act-lying-hair-2",
      "3": "act-lying-hair-3",
      "4": "act-lying-hair-4",
      "5": "act-lying-hair-5",
      "6": "act-lying-hair-6",
      "7": "act-lying-hair-7",
      "8": "act-lying-hair-8",
      "9": "act-lying-hair-9"
    },
    "height": 683,
    "meta": {
      "availableOutfits": [
        0,
        1,
        2,
        3
      ],
      "bounds": [
        25,
        195,
        1490,
        625
      ],
      "camera": "quarter",
      "canvas": [
        1536,
        1024
      ],
      "eyeScale": [
        1.2,
        1.2
      ],
      "eyes": [
        [
          0.30644831082951335,
          0.5584896096377626
        ],
        [
          0.6947049058417386,
          0.5643953402447828
        ]
      ],
      "frames": 8,
      "front": [
        "grass"
      ],
      "ground": 0.775,
      "hairBounds": {
        "0": [
          110.80851063829789,
          242.0212765957447,
          432.51063829787233,
          536.3191489361702
        ],
        "1": [
          108.42553191489361,
          227.72340425531917,
          365.7872340425532,
          481.51063829787233
        ],
        "2": [
          95.31914893617022,
          219.38297872340425,
          365.7872340425532,
          477.93617021276594
        ],
        "3": [
          59.57446808510639,
          242.0212765957447,
          448.0,
          543.468085106383
        ],
        "4": [
          7.148936170212778,
          231.29787234042553,
          406.29787234042556,
          527.9787234042553
        ],
        "5": [
          122.72340425531917,
          237.25531914893617,
          353.8723404255319,
          464.82978723404256
        ],
        "6": [
          91.74468085106383,
          231.29787234042553,
          397.95744680851067,
          535.1276595744681
        ],
        "7": [
          106.04255319148936,
          224.14893617021278,
          449.1914893617021,
          539.8936170212766
        ],
        "8": [
          -11.914893617021278,
          217.0,
          531.4042553191489,
          744.8297872340426
        ],
        "9": [
          23.829787234042556,
          203.8936170212766,
          458.7234042553191,
          549.4255319148936
        ]
      },
      "hairFit": {
        "fromX": 0.1796875,
        "fromY": 0.3388671875,
        "scale": 1.1914893617021276
      },
      "hairReady": false,
      "hairStyles": [
        0,
        1,
        2,
        3,
        4,
        5,
        6,
        7,
        8,
        9
      ],
      "head": {
        "angle": -35,
        "scale": 0.18229166666666666,
        "x": 0.18229166666666666,
        "y": 0.3759765625
      },
      "hold": [
        2.6,
        4.2
      ],
      "id": "lying",
      "outfitFits": {
        "0": {
          "bounds": [
            25,
            195,
            1490,
            660
          ],
          "eyeScales": [
            [
              1.418500802889023,
              1.013214859206445
            ],
            [
              1.3509531456085933,
              1.059270080079465
            ]
          ],
          "eyes": [
            [
              0.3089284733696382,
              0.5585368037393316
            ],
            [
              0.6912004996652729,
              0.5658109176508727
            ]
          ],
          "ground": 0.8203125,
          "hairBounds": {
            "0": [
              93.72901166858404,
              236.217494127992,
              449.55582936425526,
              568.9637666226176
            ],
            "1": [
              95.18485886429374,
              221.2139863547511,
              381.16371108686667,
              508.3048425806655
            ],
            "2": [
              81.80568619985459,
              211.93897669607009,
              381.7625283896103,
              504.58206790064736
            ],
            "3": [
              39.91212405778933,
              233.89348809916928,
              465.67087595218993,
              577.2395468099475
            ],
            "4": [
              -13.519021271004021,
              218.97292291005698,
              423.05412002226706,
              558.8722863538223
            ],
            "5": [
              111.25792108941289,
              231.7836072649556,
              368.08315877213306,
              490.29325497599626
            ],
            "6": [
              73.98065350470478,
              224.19371379275162,
              414.37678724414843,
              565.8707883453158
            ],
            "7": [
              88.51389980848325,
              217.3833353246847,
              468.1936748549431,
              573.5806360397211
            ],
            "8": [
              -48.922174635180056,
              202.7287700270111,
              554.240655641607,
              791.4263274697266
            ],
            "9": [
              2.2958283638926105,
              191.65932124458297,
              479.5648971937417,
              584.0189464647705
            ]
          },
          "hairFit": {
            "fromX": 0.1796875,
            "fromY": 0.3388671875,
            "rotation": 3.947687131999162,
            "scale": 1.2425671772114406
          },
          "head": {
            "angle": -31.05231286800084,
            "scale": 0.19010630640930246,
            "x": 0.18269476689186856,
            "y": 0.3891344239782346
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.418500802889023,
                  1.013214859206445
                ],
                [
                  1.3509531456085933,
                  1.059270080079465
                ]
              ],
              "eyes": [
                [
                  0.3089284733696382,
                  0.5585368037393316
                ],
                [
                  0.6912004996652729,
                  0.5658109176508727
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 3.947687131999162,
                "scale": 1.2425671772114406
              },
              "head": {
                "angle": -31.05231286800084,
                "scale": 0.19010630640930246,
                "x": 0.18269476689186856,
                "y": 0.3891344239782346
              }
            },
            {
              "eyeScales": [
                [
                  1.418500802889023,
                  1.013214859206445
                ],
                [
                  1.418500802889023,
                  1.013214859206445
                ]
              ],
              "eyes": [
                [
                  0.30962470927448704,
                  0.5585500521565131
                ],
                [
                  0.6905042637604237,
                  0.565797669233691
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 3.1471516941780133,
                "scale": 1.2425671772114406
              },
              "head": {
                "angle": -31.852848305821986,
                "scale": 0.19010630640930246,
                "x": 0.18255397780594018,
                "y": 0.3887082679703805
              }
            },
            {
              "eyeScales": [
                [
                  1.418500802889023,
                  1.013214859206445
                ],
                [
                  1.3509531456085933,
                  1.059270080079465
                ]
              ],
              "eyes": [
                [
                  0.3069530590357622,
                  0.558499214305895
                ],
                [
                  0.6931759139991486,
                  0.5658485070843092
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 2.9461146963433946,
                "scale": 1.2425671772114406
              },
              "head": {
                "angle": -32.05388530365661,
                "scale": 0.19010630640930246,
                "x": 0.1825188122409036,
                "y": 0.38818561478784447
              }
            },
            {
              "eyeScales": [
                [
                  1.418500802889023,
                  1.013214859206445
                ],
                [
                  1.3509531456085933,
                  1.013214859206445
                ]
              ],
              "eyes": [
                [
                  0.3059435626232646,
                  0.5584800049696301
                ],
                [
                  0.6941854104116464,
                  0.5658677164205741
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 2.4530478708616728,
                "scale": 1.2425671772114406
              },
              "head": {
                "angle": -32.546952129138326,
                "scale": 0.19010630640930246,
                "x": 0.1824328929738365,
                "y": 0.38353600024404627
              }
            },
            {
              "eyeScales": [
                [
                  1.4860484601694524,
                  1.059270080079465
                ],
                [
                  1.418500802889023,
                  1.059270080079465
                ]
              ],
              "eyes": [
                [
                  0.30594356262326455,
                  0.55848000496963
                ],
                [
                  0.6941854104116463,
                  0.5658677164205741
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 2.4530478708616537,
                "scale": 1.2425671772114406
              },
              "head": {
                "angle": -32.54695212913835,
                "scale": 0.19010630640930246,
                "x": 0.1824328929738365,
                "y": 0.38882290757163246
              }
            },
            {
              "eyeScales": [
                [
                  1.418500802889023,
                  1.013214859206445
                ],
                [
                  1.3509531456085933,
                  1.059270080079465
                ]
              ],
              "eyes": [
                [
                  0.30491983954482904,
                  0.558460524919367
                ],
                [
                  0.6952091334900816,
                  0.5658871964708373
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 1.965118452213492,
                "scale": 1.2425671772114406
              },
              "head": {
                "angle": -33.03488154778651,
                "scale": 0.19010630640930246,
                "x": 0.18234833323712854,
                "y": 0.3872379167818403
              }
            },
            {
              "eyeScales": [
                [
                  1.4860484601694524,
                  1.059270080079465
                ],
                [
                  1.418500802889023,
                  1.013214859206445
                ]
              ],
              "eyes": [
                [
                  0.3069530590357622,
                  0.558499214305895
                ],
                [
                  0.6931759139991486,
                  0.5658485070843092
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 2.9461146963433946,
                "scale": 1.2425671772114406
              },
              "head": {
                "angle": -32.05388530365661,
                "scale": 0.19010630640930246,
                "x": 0.1825188122409036,
                "y": 0.39374191866715486
              }
            },
            {
              "eyeScales": [
                [
                  1.418500802889023,
                  1.059270080079465
                ],
                [
                  1.3509531456085933,
                  1.059270080079465
                ]
              ],
              "eyes": [
                [
                  0.30594356262326466,
                  0.55848000496963
                ],
                [
                  0.6941854104116465,
                  0.5658677164205741
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 2.453047870861692,
                "scale": 1.2425671772114406
              },
              "head": {
                "angle": -32.546952129138305,
                "scale": 0.19010630640930246,
                "x": 0.1824328929738365,
                "y": 0.39006886662335666
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "tee-shorts-0-backward.png",
              "forward": "tee-shorts-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "tee-shorts-1-backward.png",
              "forward": "tee-shorts-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "tee-shorts-2-backward.png",
              "forward": "tee-shorts-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "tee-shorts-3-backward.png",
              "forward": "tee-shorts-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "tee-shorts-4-backward.png",
              "forward": "tee-shorts-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "tee-shorts-5-backward.png",
              "forward": "tee-shorts-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "tee-shorts-6-backward.png",
              "forward": "tee-shorts-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "1": {
          "bounds": [
            25,
            195,
            1490,
            662
          ],
          "eyeScales": [
            [
              1.419911658788726,
              1.0219061180676434
            ],
            [
              1.2621436967010895,
              1.0756906505975197
            ]
          ],
          "eyes": [
            [
              0.30844708998802006,
              0.560524119611862
            ],
            [
              0.6921204109182082,
              0.5663106169223422
            ]
          ],
          "ground": 0.8203125,
          "hairBounds": {
            "0": [
              119.3960149099087,
              234.12202828873288,
              438.0458690739406,
              528.253598520966
            ],
            "1": [
              116.96928197640051,
              220.92754648252264,
              373.01651302613715,
              474.36933709100924
            ],
            "2": [
              104.05770572615904,
              212.77345005595265,
              373.1339720175823,
              470.94229911689524
            ],
            "3": [
              69.20746585097979,
              233.94003543623342,
              453.1887134693885,
              535.4607417535269
            ],
            "4": [
              17.997252290056394,
              223.94617793022985,
              412.5705357233924,
              520.688852346913
            ],
            "5": [
              131.05972859491573,
              230.38650832909664,
              361.233932336119,
              457.9862547789789
            ],
            "6": [
              100.70308747383737,
              224.04417408157573,
              404.4166964335359,
              526.8778437470911
            ],
            "7": [
              114.59754178966088,
              216.45297221481982,
              454.60524549246475,
              531.983019056652
            ],
            "8": [
              -3.5183808555382825,
              208.49778464306502,
              536.5742192787486,
              733.4923088088008
            ],
            "9": [
              33.98345778877416,
              196.5381710058975,
              464.20917651723903,
              541.4605365696868
            ]
          },
          "hairFit": {
            "fromX": 0.1796875,
            "fromY": 0.3388671875,
            "rotation": 0.25740027280262723,
            "scale": 1.164955037929594
          },
          "head": {
            "angle": -34.74259972719737,
            "scale": 0.17823205332907202,
            "x": 0.18671357909252287,
            "y": 0.36685867177675546
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.419911658788726,
                  1.0219061180676434
                ],
                [
                  1.2621436967010895,
                  1.0756906505975197
                ]
              ],
              "eyes": [
                [
                  0.30844708998802006,
                  0.560524119611862
                ],
                [
                  0.6921204109182082,
                  0.5663106169223422
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.25740027280262723,
                "scale": 1.164955037929594
              },
              "head": {
                "angle": -34.74259972719737,
                "scale": 0.17823205332907202,
                "x": 0.18671357909252287,
                "y": 0.36685867177675546
              }
            },
            {
              "eyeScales": [
                [
                  1.419911658788726,
                  1.0219061180676434
                ],
                [
                  1.3410276777449075,
                  1.0219061180676434
                ]
              ],
              "eyes": [
                [
                  0.3090416823636244,
                  0.5605330871553283
                ],
                [
                  0.6915258185426041,
                  0.5663016493788761
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": -0.6885490790444828,
                "scale": 1.164955037929594
              },
              "head": {
                "angle": -35.688549079044485,
                "scale": 0.17823205332907202,
                "x": 0.18656160260775026,
                "y": 0.3668930352374155
              }
            },
            {
              "eyeScales": [
                [
                  1.3410276777449075,
                  1.0219061180676434
                ],
                [
                  1.2621436967010895,
                  1.0219061180676434
                ]
              ],
              "eyes": [
                [
                  0.31160331903783245,
                  0.5605717213342432
                ],
                [
                  0.688964181868396,
                  0.5662630151999608
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.44587255193250386,
                "scale": 1.164955037929594
              },
              "head": {
                "angle": -34.5541274480675,
                "scale": 0.17823205332907202,
                "x": 0.18674407154369368,
                "y": 0.36889194797839187
              }
            },
            {
              "eyeScales": [
                [
                  1.3410276777449075,
                  1.0219061180676434
                ],
                [
                  1.2621436967010895,
                  1.0219061180676434
                ]
              ],
              "eyes": [
                [
                  0.31160331903783256,
                  0.5605717213342433
                ],
                [
                  0.6889641818683961,
                  0.5662630151999609
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.44587255193251657,
                "scale": 1.164955037929594
              },
              "head": {
                "angle": -34.55412744806748,
                "scale": 0.17823205332907202,
                "x": 0.18674407154369368,
                "y": 0.36889194797839187
              }
            },
            {
              "eyeScales": [
                [
                  1.419911658788726,
                  1.0219061180676434
                ],
                [
                  1.2621436967010895,
                  1.0219061180676434
                ]
              ],
              "eyes": [
                [
                  0.3071695543858658,
                  0.5605048520320679
                ],
                [
                  0.6933979465203628,
                  0.5663298845021363
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": -0.30304923089133845,
                "scale": 1.164955037929594
              },
              "head": {
                "angle": -35.30304923089134,
                "scale": 0.17823205332907202,
                "x": 0.1866233211234688,
                "y": 0.37023634762258734
              }
            },
            {
              "eyeScales": [
                [
                  1.3410276777449075,
                  1.0219061180676434
                ],
                [
                  1.2621436967010895,
                  1.0219061180676434
                ]
              ],
              "eyes": [
                [
                  0.3090416823636244,
                  0.5605330871553282
                ],
                [
                  0.6915258185426042,
                  0.566301649378876
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": -0.6885490790445082,
                "scale": 1.164955037929594
              },
              "head": {
                "angle": -35.688549079044506,
                "scale": 0.17823205332907202,
                "x": 0.18656160260775026,
                "y": 0.36884616023741557
              }
            },
            {
              "eyeScales": [
                [
                  1.419911658788726,
                  1.0219061180676434
                ],
                [
                  1.2621436967010895,
                  1.0219061180676434
                ]
              ],
              "eyes": [
                [
                  0.3097060253260379,
                  0.5605431066655615
                ],
                [
                  0.6908614755801906,
                  0.566291629868643
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.825309253351466,
                "scale": 1.164955037929594
              },
              "head": {
                "angle": -34.17469074664854,
                "scale": 0.17823205332907202,
                "x": 0.18680567107910404,
                "y": 0.3693084331654575
              }
            },
            {
              "eyeScales": [
                [
                  1.3410276777449075,
                  1.0219061180676434
                ],
                [
                  1.2621436967010895,
                  1.0219061180676434
                ]
              ],
              "eyes": [
                [
                  0.31160331903783245,
                  0.5605717213342432
                ],
                [
                  0.688964181868396,
                  0.5662630151999608
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.44587255193250386,
                "scale": 1.164955037929594
              },
              "head": {
                "angle": -34.5541274480675,
                "scale": 0.17823205332907202,
                "x": 0.18674407154369368,
                "y": 0.36889194797839187
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "tee-jeans-0-backward.png",
              "forward": "tee-jeans-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "tee-jeans-1-backward.png",
              "forward": "tee-jeans-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "tee-jeans-2-backward.png",
              "forward": "tee-jeans-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "tee-jeans-3-backward.png",
              "forward": "tee-jeans-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "tee-jeans-4-backward.png",
              "forward": "tee-jeans-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "tee-jeans-5-backward.png",
              "forward": "tee-jeans-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "tee-jeans-6-backward.png",
              "forward": "tee-jeans-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "2": {
          "bounds": [
            25,
            195,
            1490,
            661
          ],
          "eyeScales": [
            [
              1.4209394110322981,
              1.022645788242942
            ],
            [
              1.341998332641615,
              1.0764692507820444
            ]
          ],
          "eyes": [
            [
              0.30413967591805935,
              0.5599425741326135
            ],
            [
              0.6970135407531928,
              0.5629423757499318
            ]
          ],
          "ground": 0.8203125,
          "hairBounds": {
            "0": [
              118.08794009995228,
              258.58812957285716,
              438.80438698588165,
              552.9611379750604
            ],
            "1": [
              116.20572010735359,
              244.4266888317517,
              372.9377591272371,
              498.1769610407515
            ],
            "2": [
              103.2742648373474,
              236.06439808388544,
              373.00997799593705,
              494.6417593460966
            ],
            "3": [
              67.35481392244105,
              258.14449937941447,
              454.1235943293864,
              560.1656621205274
            ],
            "4": [
              15.639309823659005,
              247.08494712076404,
              412.97242738959784,
              544.4853604335228
            ],
            "5": [
              130.49096462337332,
              253.97769712574578,
              361.0712176284444,
              481.5761833199333
            ],
            "6": [
              99.24384804303546,
              247.81745278900667,
              404.72362343540294,
              551.4835449541326
            ],
            "7": [
              113.3433868966838,
              240.87085317461106,
              455.45674961291417,
              556.6407774071153
            ],
            "8": [
              -5.092789800700018,
              232.7790686422586,
              536.8282904774348,
              760.0375462730888
            ],
            "9": [
              31.951212069677382,
              220.1261718658095,
              465.0593428131225,
              566.1505177761379
            ]
          },
          "hairFit": {
            "fromX": 0.1796875,
            "fromY": 0.3388671875,
            "rotation": -0.04861753564854846,
            "scale": 1.178445726973968
          },
          "head": {
            "angle": -35.048617535648546,
            "scale": 0.1802960584888558,
            "x": 0.18658706215423262,
            "y": 0.392038600974873
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.4209394110322981,
                  1.022645788242942
                ],
                [
                  1.341998332641615,
                  1.0764692507820444
                ]
              ],
              "eyes": [
                [
                  0.30413967591805935,
                  0.5599425741326135
                ],
                [
                  0.6970135407531928,
                  0.5629423757499318
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": -0.04861753564854846,
                "scale": 1.178445726973968
              },
              "head": {
                "angle": -35.048617535648546,
                "scale": 0.1802960584888558,
                "x": 0.18658706215423262,
                "y": 0.392038600974873
              }
            },
            {
              "eyeScales": [
                [
                  1.4209394110322981,
                  1.022645788242942
                ],
                [
                  1.341998332641615,
                  1.022645788242942
                ]
              ],
              "eyes": [
                [
                  0.30542406719317206,
                  0.5599523811454215
                ],
                [
                  0.6957291494780797,
                  0.5629325687371238
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.5016158320739746,
                "scale": 1.178445726973968
              },
              "head": {
                "angle": -34.49838416792603,
                "scale": 0.1802960584888558,
                "x": 0.18667387821058712,
                "y": 0.39256219983943624
              }
            },
            {
              "eyeScales": [
                [
                  1.4209394110322981,
                  1.022645788242942
                ],
                [
                  1.4209394110322981,
                  1.022645788242942
                ]
              ],
              "eyes": [
                [
                  0.30730628965794116,
                  0.5599667529183985
                ],
                [
                  0.6938469270133107,
                  0.562918196964147
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.1235353400782022,
                "scale": 1.178445726973968
              },
              "head": {
                "angle": -34.8764646599218,
                "scale": 0.1802960584888558,
                "x": 0.18661416097499642,
                "y": 0.39211520922205023
              }
            },
            {
              "eyeScales": [
                [
                  1.4209394110322981,
                  1.022645788242942
                ],
                [
                  1.341998332641615,
                  1.022645788242942
                ]
              ],
              "eyes": [
                [
                  0.30542406719317206,
                  0.5599523811454215
                ],
                [
                  0.6957291494780797,
                  0.5629325687371238
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.5016158320739746,
                "scale": 1.178445726973968
              },
              "head": {
                "angle": -34.49838416792603,
                "scale": 0.1802960584888558,
                "x": 0.18667387821058712,
                "y": 0.39256219983943624
              }
            },
            {
              "eyeScales": [
                [
                  1.4209394110322981,
                  1.022645788242942
                ],
                [
                  1.4209394110322981,
                  1.022645788242942
                ]
              ],
              "eyes": [
                [
                  0.30730628965794116,
                  0.5599667529183985
                ],
                [
                  0.6938469270133107,
                  0.562918196964147
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.1235353400782022,
                "scale": 1.178445726973968
              },
              "head": {
                "angle": -34.8764646599218,
                "scale": 0.1802960584888558,
                "x": 0.18661416097499642,
                "y": 0.39211520922205023
              }
            },
            {
              "eyeScales": [
                [
                  1.4209394110322981,
                  1.022645788242942
                ],
                [
                  1.341998332641615,
                  1.022645788242942
                ]
              ],
              "eyes": [
                [
                  0.30542406719317206,
                  0.5599523811454215
                ],
                [
                  0.6957291494780797,
                  0.5629325687371238
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.5016158320739746,
                "scale": 1.178445726973968
              },
              "head": {
                "angle": -34.49838416792603,
                "scale": 0.1802960584888558,
                "x": 0.18667387821058712,
                "y": 0.3920519616167382
              }
            },
            {
              "eyeScales": [
                [
                  1.4209394110322981,
                  1.022645788242942
                ],
                [
                  1.4209394110322981,
                  1.022645788242942
                ]
              ],
              "eyes": [
                [
                  0.30730628965794116,
                  0.5599667529183985
                ],
                [
                  0.6938469270133107,
                  0.562918196964147
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.1235353400782022,
                "scale": 1.178445726973968
              },
              "head": {
                "angle": -34.8764646599218,
                "scale": 0.1802960584888558,
                "x": 0.18661416097499642,
                "y": 0.39211520922205023
              }
            },
            {
              "eyeScales": [
                [
                  1.4209394110322981,
                  1.022645788242942
                ],
                [
                  1.341998332641615,
                  1.022645788242942
                ]
              ],
              "eyes": [
                [
                  0.30542406719317206,
                  0.5599523811454215
                ],
                [
                  0.6957291494780797,
                  0.5629325687371238
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.5016158320739746,
                "scale": 1.178445726973968
              },
              "head": {
                "angle": -34.49838416792603,
                "scale": 0.1802960584888558,
                "x": 0.18667387821058712,
                "y": 0.39256219983943624
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "dress-0-backward.png",
              "forward": "dress-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "dress-1-backward.png",
              "forward": "dress-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "dress-2-backward.png",
              "forward": "dress-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "dress-3-backward.png",
              "forward": "dress-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "dress-4-backward.png",
              "forward": "dress-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "dress-5-backward.png",
              "forward": "dress-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "dress-6-backward.png",
              "forward": "dress-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "3": {
          "bounds": [
            24,
            195,
            1491,
            626
          ],
          "eyeScales": [
            [
              1.3531407952364094,
              0.9687257965897023
            ],
            [
              1.2854837554745888,
              1.0148555964273072
            ]
          ],
          "eyes": [
            [
              0.31410000468218063,
              0.5562071602713241
            ],
            [
              0.687491739860389,
              0.5691648047552212
            ]
          ],
          "ground": 0.7861328125,
          "hairBounds": {
            "0": [
              58.914219793857,
              168.4475701500437,
              495.6331188099683,
              588.8771956280327
            ],
            "1": [
              50.849873620490285,
              180.82348480964484,
              407.53766919869713,
              535.4052875080229
            ],
            "2": [
              34.51206743020376,
              172.48388630290216,
              406.11312185480404,
              537.0793925722725
            ],
            "3": [
              8.041848641276772,
              162.27453165984036,
              513.8622326834183,
              616.3941779505501
            ],
            "4": [
              -48.287242941879924,
              168.2465450818513,
              466.2814502318124,
              621.9075198086514
            ],
            "5": [
              68.84560732430506,
              194.89087169807195,
              389.0589976048906,
              513.1440005478895
            ],
            "6": [
              35.711323379822346,
              171.57048888426846,
              460.8489961273181,
              595.2917030632318
            ],
            "7": [
              47.05916947880277,
              144.0535065617511,
              513.6207637384224,
              594.3258272832484
            ],
            "8": [
              -72.91468669980765,
              104.19044725882708,
              676.9272209788666,
              844.8253742420409
            ],
            "9": [
              -42.64567499383253,
              120.14238086345028,
              526.8841838466483,
              636.5550433839668
            ]
          },
          "hairFit": {
            "fromX": 0.1796875,
            "fromY": 0.3388671875,
            "rotation": 0.8167541218262573,
            "scale": 1.2748161356066474
          },
          "head": {
            "angle": -34.18324587817374,
            "scale": 0.1950402290804441,
            "x": 0.1881520924983562,
            "y": 0.37767556379033823
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.3531407952364094,
                  0.9687257965897023
                ],
                [
                  1.2854837554745888,
                  1.0148555964273072
                ]
              ],
              "eyes": [
                [
                  0.31410000468218063,
                  0.5562071602713241
                ],
                [
                  0.687491739860389,
                  0.5691648047552212
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.8167541218262573,
                "scale": 1.2748161356066474
              },
              "head": {
                "angle": -34.18324587817374,
                "scale": 0.1950402290804441,
                "x": 0.1881520924983562,
                "y": 0.37767556379033823
              }
            },
            {
              "eyeScales": [
                [
                  1.42079783499823,
                  1.0148555964273072
                ],
                [
                  1.2854837554745888,
                  1.0148555964273072
                ]
              ],
              "eyes": [
                [
                  0.31678877905094915,
                  0.556300467591989
                ],
                [
                  0.6848029654916207,
                  0.5690714974345563
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 1.0071048484560006,
                "scale": 1.2748161356066474
              },
              "head": {
                "angle": -33.992895151544,
                "scale": 0.1950402290804441,
                "x": 0.18818544296431614,
                "y": 0.3770698009477214
              }
            },
            {
              "eyeScales": [
                [
                  1.3531407952364094,
                  1.0148555964273072
                ],
                [
                  1.3531407952364094,
                  1.0609853962649118
                ]
              ],
              "eyes": [
                [
                  0.31244658475385456,
                  0.5561497823874682
                ],
                [
                  0.6891451597887152,
                  0.5692221826390772
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 1.1328912095167494,
                "scale": 1.2748161356066474
              },
              "head": {
                "angle": -33.86710879048325,
                "scale": 0.1950402290804441,
                "x": 0.18820752361117846,
                "y": 0.37647592927228485
              }
            },
            {
              "eyeScales": [
                [
                  1.3531407952364094,
                  1.0148555964273072
                ],
                [
                  1.2854837554745888,
                  0.5074277982136536
                ]
              ],
              "eyes": [
                [
                  0.28583659173305853,
                  0.5552263478365725
                ],
                [
                  0.7157551528095113,
                  0.570145617189973
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": -20.845470707088865,
                "scale": 1.2748161356066474
              },
              "head": {
                "angle": -55.845470707088865,
                "scale": 0.1950402290804441,
                "x": 0.18494501461741766,
                "y": 0.36205731330160335
              }
            },
            {
              "eyeScales": [
                [
                  1.3531407952364094,
                  1.0148555964273072
                ],
                [
                  1.3531407952364094,
                  1.0609853962649118
                ]
              ],
              "eyes": [
                [
                  0.31244658475385456,
                  0.5561497823874682
                ],
                [
                  0.6891451597887152,
                  0.5692221826390772
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 1.1328912095167494,
                "scale": 1.2748161356066474
              },
              "head": {
                "angle": -33.86710879048325,
                "scale": 0.1950402290804441,
                "x": 0.18820752361117846,
                "y": 0.37549936677228485
              }
            },
            {
              "eyeScales": [
                [
                  1.3531407952364094,
                  1.0609853962649118
                ],
                [
                  1.3531407952364094,
                  1.0148555964273072
                ]
              ],
              "eyes": [
                [
                  0.3124465847538545,
                  0.5561497823874682
                ],
                [
                  0.6891451597887149,
                  0.5692221826390772
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 1.132891209516775,
                "scale": 1.2748161356066474
              },
              "head": {
                "angle": -33.86710879048322,
                "scale": 0.1950402290804441,
                "x": 0.1882075236111785,
                "y": 0.3776178773569623
              }
            },
            {
              "eyeScales": [
                [
                  1.3531407952364094,
                  1.0148555964273072
                ],
                [
                  1.3531407952364094,
                  1.0609853962649118
                ]
              ],
              "eyes": [
                [
                  0.31035754508188496,
                  0.556077287388699
                ],
                [
                  0.6912341994606848,
                  0.5692946776378462
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": 0.1362263043288859,
                "scale": 1.2748161356066474
              },
              "head": {
                "angle": -34.863773695671114,
                "scale": 0.1950402290804441,
                "x": 0.1880334946135157,
                "y": 0.378777855565116
              }
            },
            {
              "eyeScales": [
                [
                  1.42079783499823,
                  1.0148555964273072
                ],
                [
                  1.2854837554745888,
                  0.5074277982136536
                ]
              ],
              "eyes": [
                [
                  0.2838021064081724,
                  0.5551557460126328
                ],
                [
                  0.7177896381343974,
                  0.5702162190139128
                ]
              ],
              "hairFit": {
                "fromX": 0.1796875,
                "fromY": 0.3388671875,
                "rotation": -21.868918063810703,
                "scale": 1.2748161356066474
              },
              "head": {
                "angle": -56.8689180638107,
                "scale": 0.1950402290804441,
                "x": 0.184826332156891,
                "y": 0.3624977194242976
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "shirt-skirt-0-backward.png",
              "forward": "shirt-skirt-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "shirt-skirt-1-backward.png",
              "forward": "shirt-skirt-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "shirt-skirt-2-backward.png",
              "forward": "shirt-skirt-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "shirt-skirt-3-backward.png",
              "forward": "shirt-skirt-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "shirt-skirt-4-backward.png",
              "forward": "shirt-skirt-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "shirt-skirt-5-backward.png",
              "forward": "shirt-skirt-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "shirt-skirt-6-backward.png",
              "forward": "shirt-skirt-6-forward.png",
              "scale": 768.0
            }
          }
        }
      },
      "outfitFrames": {
        "0": 8,
        "1": 8,
        "2": 8,
        "3": 8
      },
      "palette": "storybook-v21",
      "playback": {
        "fps": 1.6,
        "interpolate": true,
        "order": [
          0,
          0,
          1,
          2,
          3,
          4,
          5,
          6,
          7,
          7,
          6,
          5,
          4,
          3,
          2,
          1
        ]
      },
      "posture": "lying",
      "station": "meadow",
      "status": "V22 eight complete animation drawings; extracted cutouts and per-frame fitted overlays"
    },
    "width": 1024
  },
  "standing": {
    "frames": {
      "0": [
        {
          "brows": null,
          "garment": "act-standing-tee-shorts-a-garment",
          "props": "act-standing-tee-shorts-a-props",
          "skin": "act-standing-tee-shorts-a-skin",
          "tongue": null
        },
        {
          "brows": "act-standing-tee-shorts-b-brows",
          "garment": "act-standing-tee-shorts-b-garment",
          "props": "act-standing-tee-shorts-b-props",
          "skin": "act-standing-tee-shorts-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-tee-shorts-c-garment",
          "props": "act-standing-tee-shorts-c-props",
          "skin": "act-standing-tee-shorts-c-skin",
          "tongue": null
        },
        {
          "brows": "act-standing-tee-shorts-d-brows",
          "garment": "act-standing-tee-shorts-d-garment",
          "props": "act-standing-tee-shorts-d-props",
          "skin": "act-standing-tee-shorts-d-skin",
          "tongue": null
        },
        {
          "brows": "act-standing-tee-shorts-e-brows",
          "garment": "act-standing-tee-shorts-e-garment",
          "props": "act-standing-tee-shorts-e-props",
          "skin": "act-standing-tee-shorts-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-tee-shorts-f-garment",
          "props": "act-standing-tee-shorts-f-props",
          "skin": "act-standing-tee-shorts-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-tee-shorts-g-garment",
          "props": "act-standing-tee-shorts-g-props",
          "skin": "act-standing-tee-shorts-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-tee-shorts-h-garment",
          "props": "act-standing-tee-shorts-h-props",
          "skin": "act-standing-tee-shorts-h-skin",
          "tongue": null
        }
      ],
      "1": [
        {
          "brows": null,
          "garment": "act-standing-tee-jeans-a-garment",
          "props": "act-standing-tee-jeans-a-props",
          "skin": "act-standing-tee-jeans-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-tee-jeans-b-garment",
          "props": "act-standing-tee-jeans-b-props",
          "skin": "act-standing-tee-jeans-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-tee-jeans-c-garment",
          "props": "act-standing-tee-jeans-c-props",
          "skin": "act-standing-tee-jeans-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-tee-jeans-d-garment",
          "props": "act-standing-tee-jeans-d-props",
          "skin": "act-standing-tee-jeans-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-tee-jeans-e-garment",
          "props": "act-standing-tee-jeans-e-props",
          "skin": "act-standing-tee-jeans-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-tee-jeans-f-garment",
          "props": "act-standing-tee-jeans-f-props",
          "skin": "act-standing-tee-jeans-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-tee-jeans-g-garment",
          "props": "act-standing-tee-jeans-g-props",
          "skin": "act-standing-tee-jeans-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-tee-jeans-h-garment",
          "props": "act-standing-tee-jeans-h-props",
          "skin": "act-standing-tee-jeans-h-skin",
          "tongue": null
        }
      ],
      "2": [
        {
          "brows": "act-standing-dress-a-brows",
          "garment": "act-standing-dress-a-garment",
          "props": "act-standing-dress-a-props",
          "skin": "act-standing-dress-a-skin",
          "tongue": null
        },
        {
          "brows": "act-standing-dress-b-brows",
          "garment": "act-standing-dress-b-garment",
          "props": "act-standing-dress-b-props",
          "skin": "act-standing-dress-b-skin",
          "tongue": null
        },
        {
          "brows": "act-standing-dress-c-brows",
          "garment": "act-standing-dress-c-garment",
          "props": "act-standing-dress-c-props",
          "skin": "act-standing-dress-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-dress-d-garment",
          "props": "act-standing-dress-d-props",
          "skin": "act-standing-dress-d-skin",
          "tongue": null
        },
        {
          "brows": "act-standing-dress-e-brows",
          "garment": "act-standing-dress-e-garment",
          "props": "act-standing-dress-e-props",
          "skin": "act-standing-dress-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-dress-f-garment",
          "props": "act-standing-dress-f-props",
          "skin": "act-standing-dress-f-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-dress-g-garment",
          "props": "act-standing-dress-g-props",
          "skin": "act-standing-dress-g-skin",
          "tongue": null
        },
        {
          "brows": "act-standing-dress-h-brows",
          "garment": "act-standing-dress-h-garment",
          "props": "act-standing-dress-h-props",
          "skin": "act-standing-dress-h-skin",
          "tongue": null
        }
      ],
      "3": [
        {
          "brows": null,
          "garment": "act-standing-shirt-skirt-a-garment",
          "props": "act-standing-shirt-skirt-a-props",
          "skin": "act-standing-shirt-skirt-a-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-shirt-skirt-b-garment",
          "props": "act-standing-shirt-skirt-b-props",
          "skin": "act-standing-shirt-skirt-b-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-shirt-skirt-c-garment",
          "props": "act-standing-shirt-skirt-c-props",
          "skin": "act-standing-shirt-skirt-c-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-shirt-skirt-d-garment",
          "props": "act-standing-shirt-skirt-d-props",
          "skin": "act-standing-shirt-skirt-d-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-shirt-skirt-e-garment",
          "props": "act-standing-shirt-skirt-e-props",
          "skin": "act-standing-shirt-skirt-e-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-shirt-skirt-f-garment",
          "props": "act-standing-shirt-skirt-f-props",
          "skin": "act-standing-shirt-skirt-f-skin",
          "tongue": null
        },
        {
          "brows": "act-standing-shirt-skirt-g-brows",
          "garment": "act-standing-shirt-skirt-g-garment",
          "props": "act-standing-shirt-skirt-g-props",
          "skin": "act-standing-shirt-skirt-g-skin",
          "tongue": null
        },
        {
          "brows": null,
          "garment": "act-standing-shirt-skirt-h-garment",
          "props": "act-standing-shirt-skirt-h-props",
          "skin": "act-standing-shirt-skirt-h-skin",
          "tongue": null
        }
      ]
    },
    "hair": {
      "0": "act-standing-hair-0",
      "1": "act-standing-hair-1",
      "2": "act-standing-hair-2",
      "3": "act-standing-hair-3",
      "4": "act-standing-hair-4",
      "5": "act-standing-hair-5",
      "6": "act-standing-hair-6",
      "7": "act-standing-hair-7",
      "8": "act-standing-hair-8",
      "9": "act-standing-hair-9"
    },
    "height": 683,
    "meta": {
      "availableOutfits": [
        0,
        1,
        2,
        3
      ],
      "bounds": [
        550,
        35,
        430,
        925
      ],
      "camera": "front",
      "canvas": [
        1536,
        1024
      ],
      "expression": "calm",
      "eyeScale": [
        1.5,
        1.15
      ],
      "eyes": [
        [
          0.2923728813559322,
          0.614406779661017
        ],
        [
          0.6885593220338984,
          0.6122881355932204
        ]
      ],
      "frames": 8,
      "ground": 0.925,
      "hairBounds": {
        "0": [
          625.9871031746031,
          45.85317460317461,
          895.2331349206349,
          312.17261904761904
        ],
        "1": [
          623.0605158730159,
          18.05059523809524,
          906.9394841269841,
          236.0813492063492
        ],
        "2": [
          636.2301587301588,
          22.440476190476176,
          893.7698412698412,
          200.96230158730157
        ],
        "3": [
          601.1111111111111,
          37.07341269841268,
          914.2559523809524,
          458.5019841269841
        ],
        "4": [
          646.4732142857143,
          4.88095238095238,
          974.250992063492,
          306.3194444444444
        ],
        "5": [
          655.2529761904761,
          50.24305555555554,
          871.8204365079365,
          211.2053571428571
        ],
        "6": [
          650.8630952380952,
          -22.921626984126988,
          874.7470238095239,
          211.2053571428571
        ],
        "7": [
          649.3998015873016,
          45.85317460317461,
          876.2103174603175,
          467.281746031746
        ],
        "8": [
          561.6021825396825,
          34.14682539682539,
          965.4712301587301,
          492.1577380952381
        ],
        "9": [
          555.749007936508,
          23.90376984126985,
          965.4712301587301,
          348.7549603174603
        ]
      },
      "hairFit": {
        "fromX": 0.5,
        "fromY": 0.2,
        "scale": 1.4632936507936507
      },
      "hairStyles": [
        0,
        1,
        2,
        3,
        4,
        5,
        6,
        7,
        8,
        9
      ],
      "head": {
        "angle": 0,
        "scale": 0.15364583333333334,
        "x": 0.498046875,
        "y": 0.18310546875
      },
      "hold": [
        3.3,
        5.7
      ],
      "id": "standing",
      "label": "Standing portrait",
      "outfitFits": {
        "0": {
          "bounds": [
            550,
            35,
            430,
            941
          ],
          "eyeScales": [
            [
              1.562778849101984,
              1.1049951458296858
            ],
            [
              1.562778849101984,
              1.1444592581807462
            ]
          ],
          "eyes": [
            [
              0.2945127464722713,
              0.6165251955454959
            ],
            [
              0.6885381009853555,
              0.612288363776538
            ]
          ],
          "ground": 0.939453125,
          "hairBounds": {
            "0": [
              623.2380139990164,
              170.87185665531214,
              900.5173744731292,
              445.34322659260926
            ],
            "1": [
              620.8817537032243,
              143.10898247679762,
              913.1468618645205,
              370.0580240894177
            ],
            "2": [
              634.0536182078913,
              147.94209061274503,
              899.8897821181265,
              334.6540512381403
            ],
            "3": [
              593.3141517115241,
              161.25740057764432,
              919.7527566527431,
              591.5916553810366
            ],
            "4": [
              643.8256312528113,
              130.835346420717,
              980.5774548937626,
              442.3152552303289
            ],
            "5": [
              653.3884048322126,
              176.25465163043074,
              877.0700064882918,
              344.0679365631784
            ],
            "6": [
              647.9662438469231,
              103.33127321333754,
              882.5695271249122,
              344.1714569516325
            ],
            "7": [
              641.0435208131092,
              171.70001976294435,
              881.5925534588772,
              598.9804231069429
            ],
            "8": [
              552.8182697531644,
              156.94836440824545,
              970.8077182334138,
              626.8856378195816
            ],
            "9": [
              552.0677469368726,
              146.55103539289408,
              971.1700395930029,
              484.22160248137493
            ]
          },
          "hairFit": {
            "fromX": 0.5,
            "fromY": 0.2,
            "rotation": 0.1305140783910799,
            "scale": 1.456675353226438
          },
          "head": {
            "angle": 0.1305140783910799,
            "scale": 0.152950912088776,
            "x": 0.49808084255582213,
            "y": 0.3078804489771095
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.562778849101984,
                  1.1049951458296858
                ],
                [
                  1.562778849101984,
                  1.1444592581807462
                ]
              ],
              "eyes": [
                [
                  0.2945127464722713,
                  0.6165251955454959
                ],
                [
                  0.6885381009853555,
                  0.612288363776538
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.1305140783910799,
                "scale": 1.456675353226438
              },
              "head": {
                "angle": 0.1305140783910799,
                "scale": 0.152950912088776,
                "x": 0.49808084255582213,
                "y": 0.3078804489771095
              }
            },
            {
              "eyeScales": [
                [
                  1.562778849101984,
                  1.1049951458296858
                ],
                [
                  1.562778849101984,
                  1.1049951458296858
                ]
              ],
              "eyes": [
                [
                  0.29449152542372903,
                  0.6165254237288137
                ],
                [
                  0.6885593220338984,
                  0.6122881355932205
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.35496202267993454,
                "scale": 1.456675353226438
              },
              "head": {
                "angle": -0.35496202267993454,
                "scale": 0.152950912088776,
                "x": 0.4979325532168633,
                "y": 0.30803702313013476
              }
            },
            {
              "eyeScales": [
                [
                  1.5048981509870956,
                  1.1049951458296858
                ],
                [
                  1.562778849101984,
                  1.1444592581807462
                ]
              ],
              "eyes": [
                [
                  0.2961822250203323,
                  0.6165072441632587
                ],
                [
                  0.6868686224372945,
                  0.6123063151587753
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.12636431527002484,
                "scale": 1.456675353226438
              },
              "head": {
                "angle": 0.12636431527002484,
                "scale": 0.152950912088776,
                "x": 0.4980795753962916,
                "y": 0.30579239908429723
              }
            },
            {
              "eyeScales": [
                [
                  1.562778849101984,
                  1.1444592581807462
                ],
                [
                  1.562778849101984,
                  1.1444592581807462
                ]
              ],
              "eyes": [
                [
                  0.2944915254237285,
                  0.6165254237288136
                ],
                [
                  0.6885593220338979,
                  0.6122881355932204
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.3549620226799697,
                "scale": 1.456675353226438
              },
              "head": {
                "angle": -0.3549620226799697,
                "scale": 0.152950912088776,
                "x": 0.4979325532168633,
                "y": 0.30824746880204756
              }
            },
            {
              "eyeScales": [
                [
                  1.5048981509870956,
                  1.1444592581807462
                ],
                [
                  1.562778849101984,
                  1.1444592581807462
                ]
              ],
              "eyes": [
                [
                  0.29282222415610115,
                  0.6165433732048096
                ],
                [
                  0.6902286233015257,
                  0.6122701861172244
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.34680371723694625,
                "scale": 1.456675353226438
              },
              "head": {
                "angle": -0.34680371723694625,
                "scale": 0.152950912088776,
                "x": 0.4979350459167002,
                "y": 0.3118678612235596
              }
            },
            {
              "eyeScales": [
                [
                  1.562778849101984,
                  1.1444592581807462
                ],
                [
                  1.6206595472168723,
                  1.1049951458296858
                ]
              ],
              "eyes": [
                [
                  0.28944912288125724,
                  0.6165796431109907
                ],
                [
                  0.6936017245763693,
                  0.6122339162110432
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 2.036325454798256,
                "scale": 1.456675353226438
              },
              "head": {
                "angle": 2.036325454798256,
                "scale": 0.152950912088776,
                "x": 0.49866194758949245,
                "y": 0.3092834022362533
              }
            },
            {
              "eyeScales": [
                [
                  1.562778849101984,
                  1.1049951458296858
                ],
                [
                  1.6206595472168723,
                  1.1444592581807462
                ]
              ],
              "eyes": [
                [
                  0.29278715785395343,
                  0.6165437502618217
                ],
                [
                  0.6902636896036732,
                  0.6122698090602119
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.8280656225365958,
                "scale": 1.456675353226438
              },
              "head": {
                "angle": -0.8280656225365958,
                "scale": 0.152950912088776,
                "x": 0.49778796094633543,
                "y": 0.3091723823362604
              }
            },
            {
              "eyeScales": [
                [
                  1.562778849101984,
                  1.1444592581807462
                ],
                [
                  1.562778849101984,
                  1.1444592581807462
                ]
              ],
              "eyes": [
                [
                  0.2944915254237286,
                  0.6165254237288136
                ],
                [
                  0.688559322033898,
                  0.6122881355932204
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.35496202267989935,
                "scale": 1.456675353226438
              },
              "head": {
                "angle": -0.35496202267989935,
                "scale": 0.152950912088776,
                "x": 0.4979325532168633,
                "y": 0.308803139958222
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "tee-shorts-0-backward.png",
              "forward": "tee-shorts-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "tee-shorts-1-backward.png",
              "forward": "tee-shorts-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "tee-shorts-2-backward.png",
              "forward": "tee-shorts-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "tee-shorts-3-backward.png",
              "forward": "tee-shorts-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "tee-shorts-4-backward.png",
              "forward": "tee-shorts-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "tee-shorts-5-backward.png",
              "forward": "tee-shorts-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "tee-shorts-6-backward.png",
              "forward": "tee-shorts-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "1": {
          "bounds": [
            550,
            35,
            430,
            939
          ],
          "eyeScales": [
            [
              1.503539362271836,
              1.1390449714180577
            ],
            [
              1.5592260053189408,
              1.1010768057041223
            ]
          ],
          "eyes": [
            [
              0.29503517940269586,
              0.6143981149258122
            ],
            [
              0.6837783799193383,
              0.6122968003284249
            ]
          ],
          "ground": 0.939453125,
          "hairBounds": {
            "0": [
              628.0495952569154,
              171.69194005634668,
              894.0004342904147,
              435.6058318885883
            ],
            "1": [
              624.8914170076523,
              144.44368597415547,
              905.4307118058264,
              361.257222350077
            ],
            "2": [
              637.8071599392704,
              148.86746312739507,
              892.5383218574716,
              326.90475027087876
            ],
            "3": [
              603.6479553388488,
              162.95313421953566,
              914.1594124140605,
              578.6631877544767
            ],
            "4": [
              647.627563855083,
              130.85242128286674,
              971.2761973993714,
              429.83289276312996
            ],
            "5": [
              656.6936435853969,
              176.13625913326746,
              870.9427957689209,
              336.86481240365504
            ],
            "6": [
              651.6186153316669,
              104.61750362240087,
              874.1890810077152,
              336.8762189105971
            ],
            "7": [
              650.927130379626,
              171.8156080654436,
              877.0776766898423,
              587.117279317707
            ],
            "8": [
              565.0106952025458,
              159.5435990364416,
              964.5653460873718,
              611.6541716853949
            ],
            "9": [
              559.181342763317,
              149.53467742025575,
              963.0257848676573,
              471.53651574796527
            ]
          },
          "hairFit": {
            "fromX": 0.5,
            "fromY": 0.2,
            "rotation": -0.1638041587760929,
            "scale": 1.4299322448970304
          },
          "head": {
            "angle": -0.1638041587760929,
            "scale": 0.1501428857141882,
            "x": 0.4984493882887504,
            "y": 0.30389807418324444
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.503539362271836,
                  1.1390449714180577
                ],
                [
                  1.5592260053189408,
                  1.1010768057041223
                ]
              ],
              "eyes": [
                [
                  0.29503517940269586,
                  0.6143981149258122
                ],
                [
                  0.6837783799193383,
                  0.6122968003284249
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.1638041587760929,
                "scale": 1.4299322448970304
              },
              "head": {
                "angle": -0.1638041587760929,
                "scale": 0.1501428857141882,
                "x": 0.4984493882887504,
                "y": 0.30389807418324444
              }
            },
            {
              "eyeScales": [
                [
                  1.5592260053189408,
                  1.1010768057041223
                ],
                [
                  1.5592260053189408,
                  1.1010768057041223
                ]
              ],
              "eyes": [
                [
                  0.29343549497799815,
                  0.6144067618686484
                ],
                [
                  0.6853780643440361,
                  0.6122881533855887
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.30970389995883885,
                "scale": 1.4299322448970304
              },
              "head": {
                "angle": 0.30970389995883885,
                "scale": 0.1501428857141882,
                "x": 0.4985900150632911,
                "y": 0.30355628715048366
              }
            },
            {
              "eyeScales": [
                [
                  1.503539362271836,
                  1.1390449714180577
                ],
                [
                  1.503539362271836,
                  1.1010768057041223
                ]
              ],
              "eyes": [
                [
                  0.29342891180166286,
                  0.6144067974533853
                ],
                [
                  0.6853846475203714,
                  0.6122881178008518
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.15992311973081333,
                "scale": 1.4299322448970304
              },
              "head": {
                "angle": -0.15992311973081333,
                "scale": 0.1501428857141882,
                "x": 0.49845054135653255,
                "y": 0.30389823090065327
              }
            },
            {
              "eyeScales": [
                [
                  1.503539362271836,
                  1.1390449714180577
                ],
                [
                  1.503539362271836,
                  1.1010768057041223
                ]
              ],
              "eyes": [
                [
                  0.2934289118016633,
                  0.6144067974533853
                ],
                [
                  0.6853846475203714,
                  0.6122881178008518
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.15992311973081394,
                "scale": 1.4299322448970304
              },
              "head": {
                "angle": -0.15992311973081394,
                "scale": 0.1501428857141882,
                "x": 0.49845054135653255,
                "y": 0.30389823090065327
              }
            },
            {
              "eyeScales": [
                [
                  1.5592260053189408,
                  1.1010768057041223
                ],
                [
                  1.5592260053189408,
                  1.1390449714180577
                ]
              ],
              "eyes": [
                [
                  0.2902163739672485,
                  0.6144241625227607
                ],
                [
                  0.6885971853547856,
                  0.6122707527314768
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.15234882147193113,
                "scale": 1.4299322448970304
              },
              "head": {
                "angle": -0.15234882147193113,
                "scale": 0.1501428857141882,
                "x": 0.498452791681777,
                "y": 0.30606916919258076
              }
            },
            {
              "eyeScales": [
                [
                  1.5592260053189408,
                  1.1010768057041223
                ],
                [
                  1.5592260053189408,
                  1.1010768057041223
                ]
              ],
              "eyes": [
                [
                  0.29340916359935276,
                  0.614406904200425
                ],
                [
                  0.6854043957226807,
                  0.6122880110538125
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.6294870457767516,
                "scale": 1.4299322448970304
              },
              "head": {
                "angle": -0.6294870457767516,
                "scale": 0.1501428857141882,
                "x": 0.49831098274589,
                "y": 0.3042418924180896
              }
            },
            {
              "eyeScales": [
                [
                  1.5592260053189408,
                  1.1010768057041223
                ],
                [
                  1.5592260053189408,
                  1.1010768057041223
                ]
              ],
              "eyes": [
                [
                  0.29343549497799815,
                  0.6144067618686484
                ],
                [
                  0.6853780643440361,
                  0.6122881533855887
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.30970389995883885,
                "scale": 1.4299322448970304
              },
              "head": {
                "angle": 0.30970389995883885,
                "scale": 0.1501428857141882,
                "x": 0.4985900150632911,
                "y": 0.3050033752186655
              }
            },
            {
              "eyeScales": [
                [
                  1.5592260053189408,
                  1.1010768057041223
                ],
                [
                  1.5592260053189408,
                  1.1010768057041223
                ]
              ],
              "eyes": [
                [
                  0.2934354949779986,
                  0.6144067618686484
                ],
                [
                  0.6853780643440361,
                  0.6122881533855887
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.30970389995883885,
                "scale": 1.4299322448970304
              },
              "head": {
                "angle": 0.30970389995883885,
                "scale": 0.1501428857141882,
                "x": 0.4985900150632911,
                "y": 0.3050033752186655
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "tee-jeans-0-backward.png",
              "forward": "tee-jeans-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "tee-jeans-1-backward.png",
              "forward": "tee-jeans-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "tee-jeans-2-backward.png",
              "forward": "tee-jeans-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "tee-jeans-3-backward.png",
              "forward": "tee-jeans-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "tee-jeans-4-backward.png",
              "forward": "tee-jeans-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "tee-jeans-5-backward.png",
              "forward": "tee-jeans-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "tee-jeans-6-backward.png",
              "forward": "tee-jeans-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "2": {
          "bounds": [
            550,
            35,
            430,
            925
          ],
          "eyeScales": [
            [
              1.575089172188769,
              1.1168814130065816
            ],
            [
              1.575089172188769,
              1.1168814130065816
            ]
          ],
          "eyes": [
            [
              0.2931180416696841,
              0.6175557968480435
            ],
            [
              0.6856955176523492,
              0.6112577624739901
            ]
          ],
          "ground": 0.9228515625,
          "hairBounds": {
            "0": [
              628.9371363233461,
              130.5564448495205,
              888.4172634684129,
              396.94988349974494
            ],
            "1": [
              626.2850994567926,
              103.9150409095883,
              899.8069428906961,
              324.1971582083132
            ],
            "2": [
              638.901783806115,
              108.15027319790448,
              887.17243953887,
              290.49541397768894
            ],
            "3": [
              604.1536007403704,
              122.10452454697685,
              906.6833271306417,
              537.1353031296892
            ],
            "4": [
              648.5843180090169,
              91.13887580310208,
              964.321636503583,
              391.8732348573599
            ],
            "5": [
              657.1802799591297,
              134.81763912156106,
              865.9786398585941,
              300.1535048493611
            ],
            "6": [
              652.7929846232852,
              64.77934482932005,
              869.2665816962317,
              300.17297543420386
            ],
            "7": [
              650.3146130592363,
              130.60465996215146,
              870.2096115607122,
              545.285717176442
            ],
            "8": [
              566.1137965909932,
              119.17347956505088,
              955.7233990054477,
              569.6895763549843
            ],
            "9": [
              561.4655008151481,
              109.36915684348611,
              955.7915460523973,
              432.431892743087
            ]
          },
          "hairFit": {
            "fromX": 0.5,
            "fromY": 0.2,
            "rotation": -0.1418069065499818,
            "scale": 1.4006224421932227
          },
          "head": {
            "angle": -0.1418069065499818,
            "scale": 0.1470653564302884,
            "x": 0.4964706755698744,
            "y": 0.2602213989640654
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.575089172188769,
                  1.1168814130065816
                ],
                [
                  1.575089172188769,
                  1.1168814130065816
                ]
              ],
              "eyes": [
                [
                  0.2931180416696841,
                  0.6175557968480435
                ],
                [
                  0.6856955176523492,
                  0.6112577624739901
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.1418069065499818,
                "scale": 1.4006224421932227
              },
              "head": {
                "angle": -0.1418069065499818,
                "scale": 0.1470653564302884,
                "x": 0.4964706755698744,
                "y": 0.2602213989640654
              }
            },
            {
              "eyeScales": [
                [
                  1.5120856053012182,
                  1.1168814130065816
                ],
                [
                  1.575089172188769,
                  1.1168814130065816
                ]
              ],
              "eyes": [
                [
                  0.2949349040062521,
                  0.6175266493239279
                ],
                [
                  0.6838786553157816,
                  0.6112869099981065
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.15171967107275355,
                "scale": 1.4006224421932227
              },
              "head": {
                "angle": -0.15171967107275355,
                "scale": 0.1470653564302884,
                "x": 0.49646776394202113,
                "y": 0.26022100585178304
              }
            },
            {
              "eyeScales": [
                [
                  1.575089172188769,
                  1.1168814130065816
                ],
                [
                  1.575089172188769,
                  1.1168814130065816
                ]
              ],
              "eyes": [
                [
                  0.2931180416696841,
                  0.6175557968480435
                ],
                [
                  0.6856955176523492,
                  0.6112577624739901
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.1418069065499818,
                "scale": 1.4006224421932227
              },
              "head": {
                "angle": -0.1418069065499818,
                "scale": 0.1470653564302884,
                "x": 0.4964706755698744,
                "y": 0.2611979614640654
              }
            },
            {
              "eyeScales": [
                [
                  1.575089172188769,
                  1.1168814130065816
                ],
                [
                  1.575089172188769,
                  1.1168814130065816
                ]
              ],
              "eyes": [
                [
                  0.29311804166968447,
                  0.617555796848044
                ],
                [
                  0.6856955176523496,
                  0.6112577624739904
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.14180690655001857,
                "scale": 1.4006224421932227
              },
              "head": {
                "angle": -0.14180690655001857,
                "scale": 0.1470653564302884,
                "x": 0.4964706755698744,
                "y": 0.26084850527533987
              }
            },
            {
              "eyeScales": [
                [
                  1.575089172188769,
                  1.1168814130065816
                ],
                [
                  1.575089172188769,
                  1.1168814130065816
                ]
              ],
              "eyes": [
                [
                  0.2894842998508117,
                  0.617614092171341
                ],
                [
                  0.6893292594712219,
                  0.6111994671506927
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.1225218922957273,
                "scale": 1.4006224421932227
              },
              "head": {
                "angle": -0.1225218922957273,
                "scale": 0.1470653564302884,
                "x": 0.4964763399326991,
                "y": 0.26984417878591554
              }
            },
            {
              "eyeScales": [
                [
                  1.575089172188769,
                  1.1168814130065816
                ],
                [
                  1.575089172188769,
                  1.0739244355832516
                ]
              ],
              "eyes": [
                [
                  0.28950907697438377,
                  0.6176136946773803
                ],
                [
                  0.6893044823476504,
                  0.611199864644654
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.3982484092122824,
                "scale": 1.4006224421932227
              },
              "head": {
                "angle": 0.3982484092122824,
                "scale": 0.1470653564302884,
                "x": 0.49662923243794893,
                "y": 0.2702668877425684
              }
            },
            {
              "eyeScales": [
                [
                  1.575089172188769,
                  1.1168814130065816
                ],
                [
                  1.575089172188769,
                  1.0739244355832516
                ]
              ],
              "eyes": [
                [
                  0.28950907697438377,
                  0.6176136946773803
                ],
                [
                  0.6893044823476504,
                  0.611199864644654
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.3982484092122824,
                "scale": 1.4006224421932227
              },
              "head": {
                "angle": 0.3982484092122824,
                "scale": 0.1470653564302884,
                "x": 0.49662923243794893,
                "y": 0.2702668877425684
              }
            },
            {
              "eyeScales": [
                [
                  1.575089172188769,
                  1.1168814130065816
                ],
                [
                  1.575089172188769,
                  1.1168814130065816
                ]
              ],
              "eyes": [
                [
                  0.28948429985081225,
                  0.617614092171341
                ],
                [
                  0.6893292594712219,
                  0.6111994671506927
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -0.1225218922957287,
                "scale": 1.4006224421932227
              },
              "head": {
                "angle": -0.1225218922957287,
                "scale": 0.1470653564302884,
                "x": 0.4964763399326991,
                "y": 0.27047128509719004
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "dress-0-backward.png",
              "forward": "dress-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "dress-1-backward.png",
              "forward": "dress-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "dress-2-backward.png",
              "forward": "dress-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "dress-3-backward.png",
              "forward": "dress-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "dress-4-backward.png",
              "forward": "dress-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "dress-5-backward.png",
              "forward": "dress-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "dress-6-backward.png",
              "forward": "dress-6-forward.png",
              "scale": 768.0
            }
          }
        },
        "3": {
          "bounds": [
            550,
            35,
            430,
            925
          ],
          "eyeScales": [
            [
              1.5179527066397365,
              1.0732998935836522
            ],
            [
              1.5179527066397365,
              1.0732998935836522
            ]
          ],
          "eyes": [
            [
              0.2991113309487913,
              0.6154050358071845
            ],
            [
              0.6818208724410402,
              0.6112898794470529
            ]
          ],
          "ground": 0.923828125,
          "hairBounds": {
            "0": [
              616.3570150090741,
              114.82520433716383,
              899.5632133984368,
              397.06432993427734
            ],
            "1": [
              612.309271491331,
              86.60035169985196,
              909.8532717975901,
              320.07099195988536
            ],
            "2": [
              625.8256103399822,
              91.19546620496547,
              896.450854491009,
              283.9704700970434
            ],
            "3": [
              590.8071688728089,
              105.65093058563508,
              924.5351836691252,
              546.3156632341253
            ],
            "4": [
              635.5220120699782,
              72.11522137071222,
              979.4083112953592,
              390.3356812933198
            ],
            "5": [
              646.1845010291349,
              119.59582668795878,
              873.8901186915975,
              293.6099202272674
            ],
            "6": [
              638.888426357421,
              45.35600796471837,
              877.6555597443988,
              293.78077502658357
            ],
            "7": [
              640.0823199856699,
              115.08048847633682,
              886.3232726807895,
              553.3332498078712
            ],
            "8": [
              550.6568135252596,
              102.11356219008911,
              977.7441751001853,
              581.9584823318269
            ],
            "9": [
              544.3268260827064,
              91.73374126282852,
              972.162918322525,
              436.86879574926627
            ]
          },
          "hairFit": {
            "fromX": 0.5,
            "fromY": 0.2,
            "rotation": 0.6160599083992302,
            "scale": 1.4839248374242913
          },
          "head": {
            "angle": 0.6160599083992302,
            "scale": 0.1558121079295506,
            "x": 0.49744353132998925,
            "y": 0.25388337384685256
          },
          "poseFits": [
            {
              "eyeScales": [
                [
                  1.5179527066397365,
                  1.0732998935836522
                ],
                [
                  1.5179527066397365,
                  1.0732998935836522
                ]
              ],
              "eyes": [
                [
                  0.2991113309487913,
                  0.6154050358071845
                ],
                [
                  0.6818208724410402,
                  0.6112898794470529
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.6160599083992302,
                "scale": 1.4839248374242913
              },
              "head": {
                "angle": 0.6160599083992302,
                "scale": 0.1558121079295506,
                "x": 0.49744353132998925,
                "y": 0.25388337384685256
              }
            },
            {
              "eyeScales": [
                [
                  1.574173177256023,
                  1.0732998935836522
                ],
                [
                  1.5179527066397365,
                  1.1116320326402112
                ]
              ],
              "eyes": [
                [
                  0.2942396782290436,
                  0.6154574191697625
                ],
                [
                  0.6866925251607866,
                  0.6112374960844749
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.14255184966432913,
                "scale": 1.4839248374242913
              },
              "head": {
                "angle": 0.14255184966432913,
                "scale": 0.1558121079295506,
                "x": 0.4972976616348595,
                "y": 0.2534844790210872
              }
            },
            {
              "eyeScales": [
                [
                  1.5179527066397365,
                  1.0732998935836522
                ],
                [
                  1.5179527066397365,
                  1.0732998935836522
                ]
              ],
              "eyes": [
                [
                  0.2958680297497046,
                  0.6154399100136264
                ],
                [
                  0.6850641736401268,
                  0.611255005240611
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.6160599083992302,
                "scale": 1.4839248374242913
              },
              "head": {
                "angle": 0.6160599083992302,
                "scale": 0.1558121079295506,
                "x": 0.49744353132998925,
                "y": 0.25388337384685256
              }
            },
            {
              "eyeScales": [
                [
                  1.5179527066397365,
                  1.0732998935836522
                ],
                [
                  1.574173177256023,
                  1.0732998935836522
                ]
              ],
              "eyes": [
                [
                  0.2942463791501605,
                  0.6154573471168473
                ],
                [
                  0.68668582423967,
                  0.6112375681373902
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.6160599083992302,
                "scale": 1.4839248374242913
              },
              "head": {
                "angle": 0.6160599083992302,
                "scale": 0.1558121079295506,
                "x": 0.49744353132998925,
                "y": 0.25485993634685256
              }
            },
            {
              "eyeScales": [
                [
                  1.574173177256023,
                  1.0732998935836522
                ],
                [
                  1.574173177256023,
                  1.0732998935836522
                ]
              ],
              "eyes": [
                [
                  0.29238561312616884,
                  0.6154773553536642
                ],
                [
                  0.688546590263662,
                  0.6112175599005729
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": -2.1994967758120207,
                "scale": 1.4839248374242913
              },
              "head": {
                "angle": -2.1994967758120207,
                "scale": 0.1558121079295506,
                "x": 0.4965748230244434,
                "y": 0.25909831929254007
              }
            },
            {
              "eyeScales": [
                [
                  1.574173177256023,
                  1.0732998935836522
                ],
                [
                  1.574173177256023,
                  1.0732998935836522
                ]
              ],
              "eyes": [
                [
                  0.2926247285506174,
                  0.6154747842200682
                ],
                [
                  0.6883074748392135,
                  0.6112201310341693
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.6160599083992302,
                "scale": 1.4839248374242913
              },
              "head": {
                "angle": 0.6160599083992302,
                "scale": 0.1558121079295506,
                "x": 0.49744353132998925,
                "y": 0.25843174713452377
              }
            },
            {
              "eyeScales": [
                [
                  1.574173177256023,
                  1.0732998935836522
                ],
                [
                  1.574173177256023,
                  1.0732998935836522
                ]
              ],
              "eyes": [
                [
                  0.2926247285506174,
                  0.6154747842200682
                ],
                [
                  0.6883074748392135,
                  0.6112201310341693
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.6160599083992302,
                "scale": 1.4839248374242913
              },
              "head": {
                "angle": 0.6160599083992302,
                "scale": 0.1558121079295506,
                "x": 0.49744353132998925,
                "y": 0.25442293577607633
              }
            },
            {
              "eyeScales": [
                [
                  1.5179527066397365,
                  1.0732998935836522
                ],
                [
                  1.5179527066397365,
                  1.0349677545270932
                ]
              ],
              "eyes": [
                [
                  0.29261808255323085,
                  0.6154748556824055
                ],
                [
                  0.6883141208365999,
                  0.6112200595718316
                ]
              ],
              "hairFit": {
                "fromX": 0.5,
                "fromY": 0.2,
                "rotation": 0.14643288870960758,
                "scale": 1.4839248374242913
              },
              "head": {
                "angle": 0.14643288870960758,
                "scale": 0.1558121079295506,
                "x": 0.4972988576725137,
                "y": 0.2565168833388403
              }
            }
          ],
          "transitions": {
            "0": {
              "backward": "shirt-skirt-0-backward.png",
              "forward": "shirt-skirt-0-forward.png",
              "scale": 768.0
            },
            "1": {
              "backward": "shirt-skirt-1-backward.png",
              "forward": "shirt-skirt-1-forward.png",
              "scale": 768.0
            },
            "2": {
              "backward": "shirt-skirt-2-backward.png",
              "forward": "shirt-skirt-2-forward.png",
              "scale": 768.0
            },
            "3": {
              "backward": "shirt-skirt-3-backward.png",
              "forward": "shirt-skirt-3-forward.png",
              "scale": 768.0
            },
            "4": {
              "backward": "shirt-skirt-4-backward.png",
              "forward": "shirt-skirt-4-forward.png",
              "scale": 768.0
            },
            "5": {
              "backward": "shirt-skirt-5-backward.png",
              "forward": "shirt-skirt-5-forward.png",
              "scale": 768.0
            },
            "6": {
              "backward": "shirt-skirt-6-backward.png",
              "forward": "shirt-skirt-6-forward.png",
              "scale": 768.0
            }
          }
        }
      },
      "outfitFrames": {
        "0": 8,
        "1": 8,
        "2": 8,
        "3": 8
      },
      "palette": "storybook-v21",
      "playback": {
        "fps": 1.2,
        "interpolate": true,
        "order": [
          0,
          0,
          1,
          2,
          3,
          4,
          5,
          6,
          7,
          7,
          6,
          5,
          4,
          3,
          2,
          1
        ]
      },
      "posture": "standing",
      "status": "V22 eight complete animation drawings; extracted cutouts and per-frame fitted overlays"
    },
    "width": 1024
  }
};

/**
 * What each file is actually called on the server: its name plus a hash
 * of its contents, so that a repainted file is a new URL and a year of
 * immutable caching can never serve the old one. `assetUrl` in sprites.ts
 * is the only thing that should read this.
 */
export const ASSET_SRC: Readonly<Record<string, string>> = {
  "act-animals-dress-a-brows": "act-animals-dress-a-brows.361d2a5dec",
  "act-animals-dress-a-garment": "act-animals-dress-a-garment.24d6f1fa05",
  "act-animals-dress-a-props": "act-animals-dress-a-props.00b17b8799",
  "act-animals-dress-a-skin": "act-animals-dress-a-skin.e036bfd950",
  "act-animals-dress-b-brows": "act-animals-dress-b-brows.de7b939e85",
  "act-animals-dress-b-garment": "act-animals-dress-b-garment.a2582af215",
  "act-animals-dress-b-props": "act-animals-dress-b-props.9233b45144",
  "act-animals-dress-b-skin": "act-animals-dress-b-skin.ea3b6709ab",
  "act-animals-dress-c-brows": "act-animals-dress-c-brows.21f669b3b2",
  "act-animals-dress-c-garment": "act-animals-dress-c-garment.723b1e4bae",
  "act-animals-dress-c-props": "act-animals-dress-c-props.59e21a6ca9",
  "act-animals-dress-c-skin": "act-animals-dress-c-skin.bc015c2c44",
  "act-animals-dress-d-brows": "act-animals-dress-d-brows.b6f3119354",
  "act-animals-dress-d-garment": "act-animals-dress-d-garment.bd029ad2f2",
  "act-animals-dress-d-props": "act-animals-dress-d-props.7ddce3609b",
  "act-animals-dress-d-skin": "act-animals-dress-d-skin.2b9bbe8882",
  "act-animals-dress-e-brows": "act-animals-dress-e-brows.7839e22edc",
  "act-animals-dress-e-garment": "act-animals-dress-e-garment.426dab32ff",
  "act-animals-dress-e-props": "act-animals-dress-e-props.c635d4426c",
  "act-animals-dress-e-skin": "act-animals-dress-e-skin.54316540b1",
  "act-animals-dress-f-brows": "act-animals-dress-f-brows.6c3bf6cb4c",
  "act-animals-dress-f-garment": "act-animals-dress-f-garment.b47e0725fc",
  "act-animals-dress-f-props": "act-animals-dress-f-props.2c906c959e",
  "act-animals-dress-f-skin": "act-animals-dress-f-skin.51b121d91a",
  "act-animals-dress-g-brows": "act-animals-dress-g-brows.64622cf567",
  "act-animals-dress-g-garment": "act-animals-dress-g-garment.7df620b7a9",
  "act-animals-dress-g-props": "act-animals-dress-g-props.fbac6ce5a4",
  "act-animals-dress-g-skin": "act-animals-dress-g-skin.bea31695be",
  "act-animals-dress-h-brows": "act-animals-dress-h-brows.c41288f356",
  "act-animals-dress-h-garment": "act-animals-dress-h-garment.2eb57fdba7",
  "act-animals-dress-h-props": "act-animals-dress-h-props.8a324dabc4",
  "act-animals-dress-h-skin": "act-animals-dress-h-skin.dfc589801b",
  "act-animals-hair-0": "act-animals-hair-0.de72930cbd",
  "act-animals-hair-1": "act-animals-hair-1.13a86b6757",
  "act-animals-hair-2": "act-animals-hair-2.6ee672a9bf",
  "act-animals-hair-3": "act-animals-hair-3.6fb30d4b76",
  "act-animals-hair-4": "act-animals-hair-4.6cffad49b3",
  "act-animals-hair-5": "act-animals-hair-5.8f7f9d7aed",
  "act-animals-hair-6": "act-animals-hair-6.43d53a65ec",
  "act-animals-hair-7": "act-animals-hair-7.4995d370dd",
  "act-animals-hair-8": "act-animals-hair-8.bcb3c846a9",
  "act-animals-hair-9": "act-animals-hair-9.47ef741e8f",
  "act-animals-shirt-skirt-a-brows": "act-animals-shirt-skirt-a-brows.db8386b5de",
  "act-animals-shirt-skirt-a-garment": "act-animals-shirt-skirt-a-garment.27fe538909",
  "act-animals-shirt-skirt-a-props": "act-animals-shirt-skirt-a-props.481b242b6c",
  "act-animals-shirt-skirt-a-skin": "act-animals-shirt-skirt-a-skin.4d1ea1aafd",
  "act-animals-shirt-skirt-b-brows": "act-animals-shirt-skirt-b-brows.740f800de4",
  "act-animals-shirt-skirt-b-garment": "act-animals-shirt-skirt-b-garment.1cc98b9e02",
  "act-animals-shirt-skirt-b-props": "act-animals-shirt-skirt-b-props.590dc87d31",
  "act-animals-shirt-skirt-b-skin": "act-animals-shirt-skirt-b-skin.dce453c344",
  "act-animals-shirt-skirt-c-brows": "act-animals-shirt-skirt-c-brows.5e3ce907fb",
  "act-animals-shirt-skirt-c-garment": "act-animals-shirt-skirt-c-garment.9291aa767b",
  "act-animals-shirt-skirt-c-props": "act-animals-shirt-skirt-c-props.0df3f36bb4",
  "act-animals-shirt-skirt-c-skin": "act-animals-shirt-skirt-c-skin.e7175f2c0f",
  "act-animals-shirt-skirt-d-brows": "act-animals-shirt-skirt-d-brows.bdb1e7b2f6",
  "act-animals-shirt-skirt-d-garment": "act-animals-shirt-skirt-d-garment.f3432194dd",
  "act-animals-shirt-skirt-d-props": "act-animals-shirt-skirt-d-props.01fcf74f0c",
  "act-animals-shirt-skirt-d-skin": "act-animals-shirt-skirt-d-skin.e9fdf00559",
  "act-animals-shirt-skirt-e-brows": "act-animals-shirt-skirt-e-brows.180cba8791",
  "act-animals-shirt-skirt-e-garment": "act-animals-shirt-skirt-e-garment.81b18ed6af",
  "act-animals-shirt-skirt-e-props": "act-animals-shirt-skirt-e-props.77dd002e23",
  "act-animals-shirt-skirt-e-skin": "act-animals-shirt-skirt-e-skin.845b3f5d81",
  "act-animals-shirt-skirt-f-brows": "act-animals-shirt-skirt-f-brows.0e41381de1",
  "act-animals-shirt-skirt-f-garment": "act-animals-shirt-skirt-f-garment.339839d66f",
  "act-animals-shirt-skirt-f-props": "act-animals-shirt-skirt-f-props.5ff0dbb66f",
  "act-animals-shirt-skirt-f-skin": "act-animals-shirt-skirt-f-skin.7d4ac0c5f3",
  "act-animals-shirt-skirt-g-brows": "act-animals-shirt-skirt-g-brows.9731555940",
  "act-animals-shirt-skirt-g-garment": "act-animals-shirt-skirt-g-garment.76d6df6487",
  "act-animals-shirt-skirt-g-props": "act-animals-shirt-skirt-g-props.0b0dd957a3",
  "act-animals-shirt-skirt-g-skin": "act-animals-shirt-skirt-g-skin.9bd61e967f",
  "act-animals-shirt-skirt-h-brows": "act-animals-shirt-skirt-h-brows.523a534285",
  "act-animals-shirt-skirt-h-garment": "act-animals-shirt-skirt-h-garment.0a7f2251e2",
  "act-animals-shirt-skirt-h-props": "act-animals-shirt-skirt-h-props.347fdfc394",
  "act-animals-shirt-skirt-h-skin": "act-animals-shirt-skirt-h-skin.44641e6f67",
  "act-animals-tee-jeans-a-brows": "act-animals-tee-jeans-a-brows.15374c666f",
  "act-animals-tee-jeans-a-garment": "act-animals-tee-jeans-a-garment.f55d149a71",
  "act-animals-tee-jeans-a-props": "act-animals-tee-jeans-a-props.2c1fa1c3c8",
  "act-animals-tee-jeans-a-skin": "act-animals-tee-jeans-a-skin.d14f32f114",
  "act-animals-tee-jeans-b-brows": "act-animals-tee-jeans-b-brows.c443d3625d",
  "act-animals-tee-jeans-b-garment": "act-animals-tee-jeans-b-garment.92795883ce",
  "act-animals-tee-jeans-b-props": "act-animals-tee-jeans-b-props.32f48bc2ca",
  "act-animals-tee-jeans-b-skin": "act-animals-tee-jeans-b-skin.4d0c374bc5",
  "act-animals-tee-jeans-c-brows": "act-animals-tee-jeans-c-brows.0628fff999",
  "act-animals-tee-jeans-c-garment": "act-animals-tee-jeans-c-garment.55c98232ee",
  "act-animals-tee-jeans-c-props": "act-animals-tee-jeans-c-props.e5c6101641",
  "act-animals-tee-jeans-c-skin": "act-animals-tee-jeans-c-skin.fb902293db",
  "act-animals-tee-jeans-d-brows": "act-animals-tee-jeans-d-brows.53a40b8a2c",
  "act-animals-tee-jeans-d-garment": "act-animals-tee-jeans-d-garment.b2f8ee3694",
  "act-animals-tee-jeans-d-props": "act-animals-tee-jeans-d-props.a46d59bb48",
  "act-animals-tee-jeans-d-skin": "act-animals-tee-jeans-d-skin.227a9aaaf1",
  "act-animals-tee-jeans-e-brows": "act-animals-tee-jeans-e-brows.c862e1cb5b",
  "act-animals-tee-jeans-e-garment": "act-animals-tee-jeans-e-garment.9b672d046d",
  "act-animals-tee-jeans-e-props": "act-animals-tee-jeans-e-props.f5844f6e9a",
  "act-animals-tee-jeans-e-skin": "act-animals-tee-jeans-e-skin.2f1cbda27a",
  "act-animals-tee-jeans-f-brows": "act-animals-tee-jeans-f-brows.0e38c516fb",
  "act-animals-tee-jeans-f-garment": "act-animals-tee-jeans-f-garment.b3a735e766",
  "act-animals-tee-jeans-f-props": "act-animals-tee-jeans-f-props.0205c73792",
  "act-animals-tee-jeans-f-skin": "act-animals-tee-jeans-f-skin.a453bc3480",
  "act-animals-tee-jeans-g-brows": "act-animals-tee-jeans-g-brows.fbf7bd9f11",
  "act-animals-tee-jeans-g-garment": "act-animals-tee-jeans-g-garment.17e9e14008",
  "act-animals-tee-jeans-g-props": "act-animals-tee-jeans-g-props.fe5e6c2b91",
  "act-animals-tee-jeans-g-skin": "act-animals-tee-jeans-g-skin.66caac612a",
  "act-animals-tee-jeans-h-brows": "act-animals-tee-jeans-h-brows.dd8fbe9dfc",
  "act-animals-tee-jeans-h-garment": "act-animals-tee-jeans-h-garment.ae6b0dc31e",
  "act-animals-tee-jeans-h-props": "act-animals-tee-jeans-h-props.a884181da1",
  "act-animals-tee-jeans-h-skin": "act-animals-tee-jeans-h-skin.7936f4c4ac",
  "act-animals-tee-shorts-a-brows": "act-animals-tee-shorts-a-brows.b84f6c5575",
  "act-animals-tee-shorts-a-garment": "act-animals-tee-shorts-a-garment.2b3ad5debf",
  "act-animals-tee-shorts-a-props": "act-animals-tee-shorts-a-props.477882e050",
  "act-animals-tee-shorts-a-skin": "act-animals-tee-shorts-a-skin.94af477059",
  "act-animals-tee-shorts-b-brows": "act-animals-tee-shorts-b-brows.c41a3b7b2a",
  "act-animals-tee-shorts-b-garment": "act-animals-tee-shorts-b-garment.1e37241bf2",
  "act-animals-tee-shorts-b-props": "act-animals-tee-shorts-b-props.7f6c11f895",
  "act-animals-tee-shorts-b-skin": "act-animals-tee-shorts-b-skin.cfa64e1cf7",
  "act-animals-tee-shorts-c-brows": "act-animals-tee-shorts-c-brows.d6d04be1ba",
  "act-animals-tee-shorts-c-garment": "act-animals-tee-shorts-c-garment.699639aaed",
  "act-animals-tee-shorts-c-props": "act-animals-tee-shorts-c-props.74fd010eea",
  "act-animals-tee-shorts-c-skin": "act-animals-tee-shorts-c-skin.608338539a",
  "act-animals-tee-shorts-d-brows": "act-animals-tee-shorts-d-brows.c80fe81160",
  "act-animals-tee-shorts-d-garment": "act-animals-tee-shorts-d-garment.760db46ee3",
  "act-animals-tee-shorts-d-props": "act-animals-tee-shorts-d-props.61374d5650",
  "act-animals-tee-shorts-d-skin": "act-animals-tee-shorts-d-skin.ee30300344",
  "act-animals-tee-shorts-e-brows": "act-animals-tee-shorts-e-brows.5183e95573",
  "act-animals-tee-shorts-e-garment": "act-animals-tee-shorts-e-garment.4458369f23",
  "act-animals-tee-shorts-e-props": "act-animals-tee-shorts-e-props.5d9210b926",
  "act-animals-tee-shorts-e-skin": "act-animals-tee-shorts-e-skin.49671269e8",
  "act-animals-tee-shorts-f-brows": "act-animals-tee-shorts-f-brows.0b877ed300",
  "act-animals-tee-shorts-f-garment": "act-animals-tee-shorts-f-garment.f75e181817",
  "act-animals-tee-shorts-f-props": "act-animals-tee-shorts-f-props.62203cceeb",
  "act-animals-tee-shorts-f-skin": "act-animals-tee-shorts-f-skin.613baa1c45",
  "act-animals-tee-shorts-g-brows": "act-animals-tee-shorts-g-brows.9435b62c39",
  "act-animals-tee-shorts-g-garment": "act-animals-tee-shorts-g-garment.b100be7e48",
  "act-animals-tee-shorts-g-props": "act-animals-tee-shorts-g-props.e78e0508a1",
  "act-animals-tee-shorts-g-skin": "act-animals-tee-shorts-g-skin.9b3f11ec93",
  "act-animals-tee-shorts-h-brows": "act-animals-tee-shorts-h-brows.a40fd73dfb",
  "act-animals-tee-shorts-h-garment": "act-animals-tee-shorts-h-garment.fc983bd03d",
  "act-animals-tee-shorts-h-props": "act-animals-tee-shorts-h-props.fa040788c2",
  "act-animals-tee-shorts-h-skin": "act-animals-tee-shorts-h-skin.13105d9170",
  "act-fishing-dress-a-garment": "act-fishing-dress-a-garment.5ed0f63b67",
  "act-fishing-dress-a-props": "act-fishing-dress-a-props.64b8bebdb4",
  "act-fishing-dress-a-skin": "act-fishing-dress-a-skin.b09c7e1521",
  "act-fishing-dress-b-garment": "act-fishing-dress-b-garment.d02ad2e273",
  "act-fishing-dress-b-props": "act-fishing-dress-b-props.8edd372fe7",
  "act-fishing-dress-b-skin": "act-fishing-dress-b-skin.0142523672",
  "act-fishing-dress-c-garment": "act-fishing-dress-c-garment.690184595e",
  "act-fishing-dress-c-props": "act-fishing-dress-c-props.6cd8840aaf",
  "act-fishing-dress-c-skin": "act-fishing-dress-c-skin.f23fc35a4f",
  "act-fishing-dress-d-garment": "act-fishing-dress-d-garment.598322aed3",
  "act-fishing-dress-d-props": "act-fishing-dress-d-props.8618e25a6c",
  "act-fishing-dress-d-skin": "act-fishing-dress-d-skin.80ee838498",
  "act-fishing-dress-e-garment": "act-fishing-dress-e-garment.0a36d4dcb4",
  "act-fishing-dress-e-props": "act-fishing-dress-e-props.ed0107c304",
  "act-fishing-dress-e-skin": "act-fishing-dress-e-skin.fc75fc8f89",
  "act-fishing-dress-f-garment": "act-fishing-dress-f-garment.8f83d66c9f",
  "act-fishing-dress-f-props": "act-fishing-dress-f-props.11a93b282b",
  "act-fishing-dress-f-skin": "act-fishing-dress-f-skin.9330f80e02",
  "act-fishing-dress-g-garment": "act-fishing-dress-g-garment.127646ad0d",
  "act-fishing-dress-g-props": "act-fishing-dress-g-props.be4fb33021",
  "act-fishing-dress-g-skin": "act-fishing-dress-g-skin.74f0abefd8",
  "act-fishing-dress-h-garment": "act-fishing-dress-h-garment.c30c3dc6cb",
  "act-fishing-dress-h-props": "act-fishing-dress-h-props.b4e088c829",
  "act-fishing-dress-h-skin": "act-fishing-dress-h-skin.8f13d7009b",
  "act-fishing-hair-0": "act-fishing-hair-0.07b20db764",
  "act-fishing-hair-1": "act-fishing-hair-1.240a75a289",
  "act-fishing-hair-2": "act-fishing-hair-2.50623c13d4",
  "act-fishing-hair-3": "act-fishing-hair-3.634df7b4af",
  "act-fishing-hair-4": "act-fishing-hair-4.b54fa38079",
  "act-fishing-hair-5": "act-fishing-hair-5.8130f000ef",
  "act-fishing-hair-6": "act-fishing-hair-6.cb1de265fb",
  "act-fishing-hair-7": "act-fishing-hair-7.3767d10dce",
  "act-fishing-hair-8": "act-fishing-hair-8.133001a07d",
  "act-fishing-hair-9": "act-fishing-hair-9.d69392a7f5",
  "act-fishing-shirt-skirt-a-garment": "act-fishing-shirt-skirt-a-garment.62e68b6348",
  "act-fishing-shirt-skirt-a-props": "act-fishing-shirt-skirt-a-props.6eed3922e8",
  "act-fishing-shirt-skirt-a-skin": "act-fishing-shirt-skirt-a-skin.cbb1ceaf4a",
  "act-fishing-shirt-skirt-b-garment": "act-fishing-shirt-skirt-b-garment.2f32d63c0b",
  "act-fishing-shirt-skirt-b-props": "act-fishing-shirt-skirt-b-props.cf700d9990",
  "act-fishing-shirt-skirt-b-skin": "act-fishing-shirt-skirt-b-skin.d216c42ee0",
  "act-fishing-shirt-skirt-c-garment": "act-fishing-shirt-skirt-c-garment.8bb42ecefe",
  "act-fishing-shirt-skirt-c-props": "act-fishing-shirt-skirt-c-props.d2b5ee1672",
  "act-fishing-shirt-skirt-c-skin": "act-fishing-shirt-skirt-c-skin.6426341cd7",
  "act-fishing-shirt-skirt-d-garment": "act-fishing-shirt-skirt-d-garment.809cfe8d25",
  "act-fishing-shirt-skirt-d-props": "act-fishing-shirt-skirt-d-props.7521a71a70",
  "act-fishing-shirt-skirt-d-skin": "act-fishing-shirt-skirt-d-skin.1a17807bfa",
  "act-fishing-shirt-skirt-e-garment": "act-fishing-shirt-skirt-e-garment.82a9b5daa0",
  "act-fishing-shirt-skirt-e-props": "act-fishing-shirt-skirt-e-props.580dbccac7",
  "act-fishing-shirt-skirt-e-skin": "act-fishing-shirt-skirt-e-skin.f05cbc1f77",
  "act-fishing-shirt-skirt-f-garment": "act-fishing-shirt-skirt-f-garment.ca38754617",
  "act-fishing-shirt-skirt-f-props": "act-fishing-shirt-skirt-f-props.c945c993d1",
  "act-fishing-shirt-skirt-f-skin": "act-fishing-shirt-skirt-f-skin.7e6a3e163e",
  "act-fishing-shirt-skirt-g-garment": "act-fishing-shirt-skirt-g-garment.b5cbdc1bb9",
  "act-fishing-shirt-skirt-g-props": "act-fishing-shirt-skirt-g-props.c2df9d1804",
  "act-fishing-shirt-skirt-g-skin": "act-fishing-shirt-skirt-g-skin.bd56e1c03f",
  "act-fishing-shirt-skirt-h-garment": "act-fishing-shirt-skirt-h-garment.3d3d0e60cc",
  "act-fishing-shirt-skirt-h-props": "act-fishing-shirt-skirt-h-props.f8a4d4fa10",
  "act-fishing-shirt-skirt-h-skin": "act-fishing-shirt-skirt-h-skin.7f3830532c",
  "act-fishing-standing-dress-a-garment": "act-fishing-standing-dress-a-garment.e70e7861fe",
  "act-fishing-standing-dress-a-props": "act-fishing-standing-dress-a-props.230a0d02a5",
  "act-fishing-standing-dress-a-skin": "act-fishing-standing-dress-a-skin.16c4a5cb08",
  "act-fishing-standing-dress-b-garment": "act-fishing-standing-dress-b-garment.f3a5776230",
  "act-fishing-standing-dress-b-props": "act-fishing-standing-dress-b-props.47206838c8",
  "act-fishing-standing-dress-b-skin": "act-fishing-standing-dress-b-skin.262d7a8634",
  "act-fishing-standing-dress-c-garment": "act-fishing-standing-dress-c-garment.cd482e5cd0",
  "act-fishing-standing-dress-c-props": "act-fishing-standing-dress-c-props.1576aad3fe",
  "act-fishing-standing-dress-c-skin": "act-fishing-standing-dress-c-skin.8e38a8675f",
  "act-fishing-standing-dress-d-garment": "act-fishing-standing-dress-d-garment.0ed4780c6e",
  "act-fishing-standing-dress-d-props": "act-fishing-standing-dress-d-props.213e3b6f0e",
  "act-fishing-standing-dress-d-skin": "act-fishing-standing-dress-d-skin.ff36f5f40f",
  "act-fishing-standing-dress-e-garment": "act-fishing-standing-dress-e-garment.7ad9d14852",
  "act-fishing-standing-dress-e-props": "act-fishing-standing-dress-e-props.29e0ea96d1",
  "act-fishing-standing-dress-e-skin": "act-fishing-standing-dress-e-skin.dac5660e87",
  "act-fishing-standing-dress-f-garment": "act-fishing-standing-dress-f-garment.1eff163661",
  "act-fishing-standing-dress-f-props": "act-fishing-standing-dress-f-props.452ae8dd58",
  "act-fishing-standing-dress-f-skin": "act-fishing-standing-dress-f-skin.b6bc2e7f6e",
  "act-fishing-standing-dress-g-garment": "act-fishing-standing-dress-g-garment.49c8dbee6c",
  "act-fishing-standing-dress-g-props": "act-fishing-standing-dress-g-props.b5eade463d",
  "act-fishing-standing-dress-g-skin": "act-fishing-standing-dress-g-skin.6394a7e2a7",
  "act-fishing-standing-dress-h-garment": "act-fishing-standing-dress-h-garment.5a55f393df",
  "act-fishing-standing-dress-h-props": "act-fishing-standing-dress-h-props.2a68bdd170",
  "act-fishing-standing-dress-h-skin": "act-fishing-standing-dress-h-skin.4c8d885dee",
  "act-fishing-standing-hair-0": "act-fishing-standing-hair-0.8ceb420596",
  "act-fishing-standing-hair-1": "act-fishing-standing-hair-1.9eb8a8a41e",
  "act-fishing-standing-hair-2": "act-fishing-standing-hair-2.1da55b1ce4",
  "act-fishing-standing-hair-3": "act-fishing-standing-hair-3.34fcccd9b1",
  "act-fishing-standing-hair-4": "act-fishing-standing-hair-4.f26bb67722",
  "act-fishing-standing-hair-5": "act-fishing-standing-hair-5.ebe04156df",
  "act-fishing-standing-hair-6": "act-fishing-standing-hair-6.f6aecde5d2",
  "act-fishing-standing-hair-7": "act-fishing-standing-hair-7.2a4e9016e3",
  "act-fishing-standing-hair-8": "act-fishing-standing-hair-8.39aca6440a",
  "act-fishing-standing-hair-9": "act-fishing-standing-hair-9.3532745076",
  "act-fishing-standing-shirt-skirt-a-garment": "act-fishing-standing-shirt-skirt-a-garment.ffd5e00097",
  "act-fishing-standing-shirt-skirt-a-props": "act-fishing-standing-shirt-skirt-a-props.189ab980af",
  "act-fishing-standing-shirt-skirt-a-skin": "act-fishing-standing-shirt-skirt-a-skin.c844e96218",
  "act-fishing-standing-shirt-skirt-b-garment": "act-fishing-standing-shirt-skirt-b-garment.e166629d24",
  "act-fishing-standing-shirt-skirt-b-props": "act-fishing-standing-shirt-skirt-b-props.81ddc2ffa0",
  "act-fishing-standing-shirt-skirt-b-skin": "act-fishing-standing-shirt-skirt-b-skin.7f4865679e",
  "act-fishing-standing-shirt-skirt-c-garment": "act-fishing-standing-shirt-skirt-c-garment.32669d1b5c",
  "act-fishing-standing-shirt-skirt-c-props": "act-fishing-standing-shirt-skirt-c-props.05b3e68880",
  "act-fishing-standing-shirt-skirt-c-skin": "act-fishing-standing-shirt-skirt-c-skin.7fe7a0efa6",
  "act-fishing-standing-shirt-skirt-d-garment": "act-fishing-standing-shirt-skirt-d-garment.cbfe10cf94",
  "act-fishing-standing-shirt-skirt-d-props": "act-fishing-standing-shirt-skirt-d-props.d5481b30b5",
  "act-fishing-standing-shirt-skirt-d-skin": "act-fishing-standing-shirt-skirt-d-skin.c42eba506c",
  "act-fishing-standing-shirt-skirt-e-garment": "act-fishing-standing-shirt-skirt-e-garment.932310ea76",
  "act-fishing-standing-shirt-skirt-e-props": "act-fishing-standing-shirt-skirt-e-props.0d93286c89",
  "act-fishing-standing-shirt-skirt-e-skin": "act-fishing-standing-shirt-skirt-e-skin.f2ffb41069",
  "act-fishing-standing-shirt-skirt-f-garment": "act-fishing-standing-shirt-skirt-f-garment.b4c7dcc89f",
  "act-fishing-standing-shirt-skirt-f-props": "act-fishing-standing-shirt-skirt-f-props.96068dd79f",
  "act-fishing-standing-shirt-skirt-f-skin": "act-fishing-standing-shirt-skirt-f-skin.de094f565b",
  "act-fishing-standing-shirt-skirt-g-garment": "act-fishing-standing-shirt-skirt-g-garment.aca75eefbe",
  "act-fishing-standing-shirt-skirt-g-props": "act-fishing-standing-shirt-skirt-g-props.9d737a8b50",
  "act-fishing-standing-shirt-skirt-g-skin": "act-fishing-standing-shirt-skirt-g-skin.cf0c9f0151",
  "act-fishing-standing-shirt-skirt-h-garment": "act-fishing-standing-shirt-skirt-h-garment.9572bf2bfc",
  "act-fishing-standing-shirt-skirt-h-props": "act-fishing-standing-shirt-skirt-h-props.58c4a9f99e",
  "act-fishing-standing-shirt-skirt-h-skin": "act-fishing-standing-shirt-skirt-h-skin.07b992ed8a",
  "act-fishing-standing-tee-jeans-a-garment": "act-fishing-standing-tee-jeans-a-garment.2b8b262d91",
  "act-fishing-standing-tee-jeans-a-props": "act-fishing-standing-tee-jeans-a-props.848f9fb9f8",
  "act-fishing-standing-tee-jeans-a-skin": "act-fishing-standing-tee-jeans-a-skin.2dd14445b0",
  "act-fishing-standing-tee-jeans-b-garment": "act-fishing-standing-tee-jeans-b-garment.509262147f",
  "act-fishing-standing-tee-jeans-b-props": "act-fishing-standing-tee-jeans-b-props.de7d96d1b1",
  "act-fishing-standing-tee-jeans-b-skin": "act-fishing-standing-tee-jeans-b-skin.5003a319c2",
  "act-fishing-standing-tee-jeans-c-garment": "act-fishing-standing-tee-jeans-c-garment.8f7896071b",
  "act-fishing-standing-tee-jeans-c-props": "act-fishing-standing-tee-jeans-c-props.1a3d6f6b4c",
  "act-fishing-standing-tee-jeans-c-skin": "act-fishing-standing-tee-jeans-c-skin.117cd55326",
  "act-fishing-standing-tee-jeans-d-garment": "act-fishing-standing-tee-jeans-d-garment.8ea15b1790",
  "act-fishing-standing-tee-jeans-d-props": "act-fishing-standing-tee-jeans-d-props.eff4bd7e05",
  "act-fishing-standing-tee-jeans-d-skin": "act-fishing-standing-tee-jeans-d-skin.49fb856b00",
  "act-fishing-standing-tee-jeans-e-garment": "act-fishing-standing-tee-jeans-e-garment.91e82062cb",
  "act-fishing-standing-tee-jeans-e-props": "act-fishing-standing-tee-jeans-e-props.e38f86d9c8",
  "act-fishing-standing-tee-jeans-e-skin": "act-fishing-standing-tee-jeans-e-skin.25c40dceca",
  "act-fishing-standing-tee-jeans-f-garment": "act-fishing-standing-tee-jeans-f-garment.625bcb5aed",
  "act-fishing-standing-tee-jeans-f-props": "act-fishing-standing-tee-jeans-f-props.93c7370131",
  "act-fishing-standing-tee-jeans-f-skin": "act-fishing-standing-tee-jeans-f-skin.52ea573e49",
  "act-fishing-standing-tee-jeans-g-garment": "act-fishing-standing-tee-jeans-g-garment.f09e154adb",
  "act-fishing-standing-tee-jeans-g-props": "act-fishing-standing-tee-jeans-g-props.8c66bb420c",
  "act-fishing-standing-tee-jeans-g-skin": "act-fishing-standing-tee-jeans-g-skin.564451bf0c",
  "act-fishing-standing-tee-jeans-h-garment": "act-fishing-standing-tee-jeans-h-garment.b296258b19",
  "act-fishing-standing-tee-jeans-h-props": "act-fishing-standing-tee-jeans-h-props.b3fd5ed19f",
  "act-fishing-standing-tee-jeans-h-skin": "act-fishing-standing-tee-jeans-h-skin.273211a7c4",
  "act-fishing-standing-tee-shorts-a-garment": "act-fishing-standing-tee-shorts-a-garment.70f663abd2",
  "act-fishing-standing-tee-shorts-a-props": "act-fishing-standing-tee-shorts-a-props.cdca81a0b7",
  "act-fishing-standing-tee-shorts-a-skin": "act-fishing-standing-tee-shorts-a-skin.a24a033271",
  "act-fishing-standing-tee-shorts-b-garment": "act-fishing-standing-tee-shorts-b-garment.9431f4b458",
  "act-fishing-standing-tee-shorts-b-props": "act-fishing-standing-tee-shorts-b-props.91d3192d87",
  "act-fishing-standing-tee-shorts-b-skin": "act-fishing-standing-tee-shorts-b-skin.2e560b2765",
  "act-fishing-standing-tee-shorts-c-garment": "act-fishing-standing-tee-shorts-c-garment.8140dd4689",
  "act-fishing-standing-tee-shorts-c-props": "act-fishing-standing-tee-shorts-c-props.938571eb7e",
  "act-fishing-standing-tee-shorts-c-skin": "act-fishing-standing-tee-shorts-c-skin.d063bce822",
  "act-fishing-standing-tee-shorts-d-garment": "act-fishing-standing-tee-shorts-d-garment.f5439ff1f6",
  "act-fishing-standing-tee-shorts-d-props": "act-fishing-standing-tee-shorts-d-props.caf6b17f2b",
  "act-fishing-standing-tee-shorts-d-skin": "act-fishing-standing-tee-shorts-d-skin.d052fc0d57",
  "act-fishing-standing-tee-shorts-e-garment": "act-fishing-standing-tee-shorts-e-garment.73b1cf1ae0",
  "act-fishing-standing-tee-shorts-e-props": "act-fishing-standing-tee-shorts-e-props.825fe3afe4",
  "act-fishing-standing-tee-shorts-e-skin": "act-fishing-standing-tee-shorts-e-skin.0c84403203",
  "act-fishing-standing-tee-shorts-f-garment": "act-fishing-standing-tee-shorts-f-garment.e457524cc4",
  "act-fishing-standing-tee-shorts-f-props": "act-fishing-standing-tee-shorts-f-props.d22f77dea2",
  "act-fishing-standing-tee-shorts-f-skin": "act-fishing-standing-tee-shorts-f-skin.9cac060a6b",
  "act-fishing-standing-tee-shorts-g-garment": "act-fishing-standing-tee-shorts-g-garment.26124b737e",
  "act-fishing-standing-tee-shorts-g-props": "act-fishing-standing-tee-shorts-g-props.40194f77b6",
  "act-fishing-standing-tee-shorts-g-skin": "act-fishing-standing-tee-shorts-g-skin.ac690e7638",
  "act-fishing-standing-tee-shorts-h-garment": "act-fishing-standing-tee-shorts-h-garment.5f25d6cc3c",
  "act-fishing-standing-tee-shorts-h-props": "act-fishing-standing-tee-shorts-h-props.3b517f5440",
  "act-fishing-standing-tee-shorts-h-skin": "act-fishing-standing-tee-shorts-h-skin.1099c617f0",
  "act-fishing-tee-jeans-a-garment": "act-fishing-tee-jeans-a-garment.65b3ceff2e",
  "act-fishing-tee-jeans-a-props": "act-fishing-tee-jeans-a-props.0b448298ce",
  "act-fishing-tee-jeans-a-skin": "act-fishing-tee-jeans-a-skin.09432838f6",
  "act-fishing-tee-jeans-b-garment": "act-fishing-tee-jeans-b-garment.534880da62",
  "act-fishing-tee-jeans-b-props": "act-fishing-tee-jeans-b-props.f185cbb100",
  "act-fishing-tee-jeans-b-skin": "act-fishing-tee-jeans-b-skin.6edd668223",
  "act-fishing-tee-jeans-c-garment": "act-fishing-tee-jeans-c-garment.3a8b5347a0",
  "act-fishing-tee-jeans-c-props": "act-fishing-tee-jeans-c-props.9790615536",
  "act-fishing-tee-jeans-c-skin": "act-fishing-tee-jeans-c-skin.e600caa0a3",
  "act-fishing-tee-jeans-d-garment": "act-fishing-tee-jeans-d-garment.5371bd5d5a",
  "act-fishing-tee-jeans-d-props": "act-fishing-tee-jeans-d-props.9c2eda80ec",
  "act-fishing-tee-jeans-d-skin": "act-fishing-tee-jeans-d-skin.b1e80000f6",
  "act-fishing-tee-jeans-e-garment": "act-fishing-tee-jeans-e-garment.58c1649e6f",
  "act-fishing-tee-jeans-e-props": "act-fishing-tee-jeans-e-props.c2621945a0",
  "act-fishing-tee-jeans-e-skin": "act-fishing-tee-jeans-e-skin.5e9059952e",
  "act-fishing-tee-jeans-f-garment": "act-fishing-tee-jeans-f-garment.92a6058fdf",
  "act-fishing-tee-jeans-f-props": "act-fishing-tee-jeans-f-props.4542d64ce3",
  "act-fishing-tee-jeans-f-skin": "act-fishing-tee-jeans-f-skin.f53368f7a5",
  "act-fishing-tee-jeans-g-garment": "act-fishing-tee-jeans-g-garment.0b6a3cee68",
  "act-fishing-tee-jeans-g-props": "act-fishing-tee-jeans-g-props.0585b276de",
  "act-fishing-tee-jeans-g-skin": "act-fishing-tee-jeans-g-skin.901d45c65e",
  "act-fishing-tee-jeans-h-garment": "act-fishing-tee-jeans-h-garment.95c9638a00",
  "act-fishing-tee-jeans-h-props": "act-fishing-tee-jeans-h-props.f54d7957df",
  "act-fishing-tee-jeans-h-skin": "act-fishing-tee-jeans-h-skin.1e93161273",
  "act-fishing-tee-shorts-a-garment": "act-fishing-tee-shorts-a-garment.43fbd17e0f",
  "act-fishing-tee-shorts-a-props": "act-fishing-tee-shorts-a-props.10e73a7a49",
  "act-fishing-tee-shorts-a-skin": "act-fishing-tee-shorts-a-skin.20e95505e8",
  "act-fishing-tee-shorts-b-garment": "act-fishing-tee-shorts-b-garment.c210467c55",
  "act-fishing-tee-shorts-b-props": "act-fishing-tee-shorts-b-props.a18d6d948c",
  "act-fishing-tee-shorts-b-skin": "act-fishing-tee-shorts-b-skin.d04820b253",
  "act-fishing-tee-shorts-c-garment": "act-fishing-tee-shorts-c-garment.04915178ff",
  "act-fishing-tee-shorts-c-props": "act-fishing-tee-shorts-c-props.57745681c9",
  "act-fishing-tee-shorts-c-skin": "act-fishing-tee-shorts-c-skin.7a2dee57a0",
  "act-fishing-tee-shorts-d-garment": "act-fishing-tee-shorts-d-garment.647417af47",
  "act-fishing-tee-shorts-d-props": "act-fishing-tee-shorts-d-props.065f4ab351",
  "act-fishing-tee-shorts-d-skin": "act-fishing-tee-shorts-d-skin.c21b74f6dc",
  "act-fishing-tee-shorts-e-garment": "act-fishing-tee-shorts-e-garment.f8e9dde75d",
  "act-fishing-tee-shorts-e-props": "act-fishing-tee-shorts-e-props.b8ad402a3a",
  "act-fishing-tee-shorts-e-skin": "act-fishing-tee-shorts-e-skin.e3f1637594",
  "act-fishing-tee-shorts-f-garment": "act-fishing-tee-shorts-f-garment.254b63c89b",
  "act-fishing-tee-shorts-f-props": "act-fishing-tee-shorts-f-props.cf2cb83e16",
  "act-fishing-tee-shorts-f-skin": "act-fishing-tee-shorts-f-skin.c54d1d17a1",
  "act-fishing-tee-shorts-g-garment": "act-fishing-tee-shorts-g-garment.b947abe906",
  "act-fishing-tee-shorts-g-props": "act-fishing-tee-shorts-g-props.20949312d4",
  "act-fishing-tee-shorts-g-skin": "act-fishing-tee-shorts-g-skin.487a6ab1f9",
  "act-fishing-tee-shorts-h-garment": "act-fishing-tee-shorts-h-garment.fe30878617",
  "act-fishing-tee-shorts-h-props": "act-fishing-tee-shorts-h-props.9960baf77f",
  "act-fishing-tee-shorts-h-skin": "act-fishing-tee-shorts-h-skin.5106b6746d",
  "act-lying-dress-a-garment": "act-lying-dress-a-garment.b5c52beb43",
  "act-lying-dress-a-props": "act-lying-dress-a-props.36bd2067a3",
  "act-lying-dress-a-skin": "act-lying-dress-a-skin.ba29e3ca20",
  "act-lying-dress-b-garment": "act-lying-dress-b-garment.c558c35db5",
  "act-lying-dress-b-props": "act-lying-dress-b-props.0913bd049c",
  "act-lying-dress-b-skin": "act-lying-dress-b-skin.bfd44fd2e9",
  "act-lying-dress-c-garment": "act-lying-dress-c-garment.f733e74086",
  "act-lying-dress-c-props": "act-lying-dress-c-props.0ac9292716",
  "act-lying-dress-c-skin": "act-lying-dress-c-skin.f6bece870b",
  "act-lying-dress-d-garment": "act-lying-dress-d-garment.83caaa2e3c",
  "act-lying-dress-d-props": "act-lying-dress-d-props.d159f50a03",
  "act-lying-dress-d-skin": "act-lying-dress-d-skin.725d9d1016",
  "act-lying-dress-e-garment": "act-lying-dress-e-garment.82b6edb799",
  "act-lying-dress-e-props": "act-lying-dress-e-props.20be1c3ba9",
  "act-lying-dress-e-skin": "act-lying-dress-e-skin.ff13bdcfe0",
  "act-lying-dress-f-garment": "act-lying-dress-f-garment.9e903c16d2",
  "act-lying-dress-f-props": "act-lying-dress-f-props.073aee1198",
  "act-lying-dress-f-skin": "act-lying-dress-f-skin.846ac01363",
  "act-lying-dress-g-garment": "act-lying-dress-g-garment.623eb90877",
  "act-lying-dress-g-props": "act-lying-dress-g-props.cb7a49a367",
  "act-lying-dress-g-skin": "act-lying-dress-g-skin.1f2785b7db",
  "act-lying-dress-h-garment": "act-lying-dress-h-garment.1910f2d63f",
  "act-lying-dress-h-props": "act-lying-dress-h-props.d6ba3c967a",
  "act-lying-dress-h-skin": "act-lying-dress-h-skin.6a96bf0174",
  "act-lying-hair-0": "act-lying-hair-0.cd0da541e7",
  "act-lying-hair-1": "act-lying-hair-1.c98ae02902",
  "act-lying-hair-2": "act-lying-hair-2.c549cbd487",
  "act-lying-hair-3": "act-lying-hair-3.39cf2e319b",
  "act-lying-hair-4": "act-lying-hair-4.d552adbdb0",
  "act-lying-hair-5": "act-lying-hair-5.bfc6a9a5f6",
  "act-lying-hair-6": "act-lying-hair-6.140ab6a1ad",
  "act-lying-hair-7": "act-lying-hair-7.037cfd6633",
  "act-lying-hair-8": "act-lying-hair-8.c3f2d53bea",
  "act-lying-hair-9": "act-lying-hair-9.68b6120505",
  "act-lying-shirt-skirt-a-garment": "act-lying-shirt-skirt-a-garment.0898e54552",
  "act-lying-shirt-skirt-a-props": "act-lying-shirt-skirt-a-props.767f91202c",
  "act-lying-shirt-skirt-a-skin": "act-lying-shirt-skirt-a-skin.0376a72b23",
  "act-lying-shirt-skirt-b-garment": "act-lying-shirt-skirt-b-garment.5fb7ad58d2",
  "act-lying-shirt-skirt-b-props": "act-lying-shirt-skirt-b-props.17296ed000",
  "act-lying-shirt-skirt-b-skin": "act-lying-shirt-skirt-b-skin.b45b213616",
  "act-lying-shirt-skirt-c-garment": "act-lying-shirt-skirt-c-garment.d99f73441e",
  "act-lying-shirt-skirt-c-props": "act-lying-shirt-skirt-c-props.3a1e23db85",
  "act-lying-shirt-skirt-c-skin": "act-lying-shirt-skirt-c-skin.45a0063334",
  "act-lying-shirt-skirt-d-garment": "act-lying-shirt-skirt-d-garment.8a2cfc6c8c",
  "act-lying-shirt-skirt-d-props": "act-lying-shirt-skirt-d-props.1389242018",
  "act-lying-shirt-skirt-d-skin": "act-lying-shirt-skirt-d-skin.f9002d5c37",
  "act-lying-shirt-skirt-e-garment": "act-lying-shirt-skirt-e-garment.7aadd865ea",
  "act-lying-shirt-skirt-e-props": "act-lying-shirt-skirt-e-props.667e87bc4f",
  "act-lying-shirt-skirt-e-skin": "act-lying-shirt-skirt-e-skin.d2599d1543",
  "act-lying-shirt-skirt-f-garment": "act-lying-shirt-skirt-f-garment.6e14a6c93b",
  "act-lying-shirt-skirt-f-props": "act-lying-shirt-skirt-f-props.59f7ca459d",
  "act-lying-shirt-skirt-f-skin": "act-lying-shirt-skirt-f-skin.fe8c835b36",
  "act-lying-shirt-skirt-g-garment": "act-lying-shirt-skirt-g-garment.77eb449a12",
  "act-lying-shirt-skirt-g-props": "act-lying-shirt-skirt-g-props.6b40644a4c",
  "act-lying-shirt-skirt-g-skin": "act-lying-shirt-skirt-g-skin.532b2c660d",
  "act-lying-shirt-skirt-h-garment": "act-lying-shirt-skirt-h-garment.64985a91f2",
  "act-lying-shirt-skirt-h-props": "act-lying-shirt-skirt-h-props.c1818cd43b",
  "act-lying-shirt-skirt-h-skin": "act-lying-shirt-skirt-h-skin.a8501678b5",
  "act-lying-tee-jeans-a-brows": "act-lying-tee-jeans-a-brows.91505eb8a9",
  "act-lying-tee-jeans-a-garment": "act-lying-tee-jeans-a-garment.b99ce470f4",
  "act-lying-tee-jeans-a-props": "act-lying-tee-jeans-a-props.9a672809a5",
  "act-lying-tee-jeans-a-skin": "act-lying-tee-jeans-a-skin.c929b7b1b0",
  "act-lying-tee-jeans-b-garment": "act-lying-tee-jeans-b-garment.b771afa2ea",
  "act-lying-tee-jeans-b-props": "act-lying-tee-jeans-b-props.5659e8212a",
  "act-lying-tee-jeans-b-skin": "act-lying-tee-jeans-b-skin.b26c211101",
  "act-lying-tee-jeans-c-garment": "act-lying-tee-jeans-c-garment.8f6804f080",
  "act-lying-tee-jeans-c-props": "act-lying-tee-jeans-c-props.2446ac291a",
  "act-lying-tee-jeans-c-skin": "act-lying-tee-jeans-c-skin.9d2d7caedd",
  "act-lying-tee-jeans-d-brows": "act-lying-tee-jeans-d-brows.b627afd193",
  "act-lying-tee-jeans-d-garment": "act-lying-tee-jeans-d-garment.939dadc921",
  "act-lying-tee-jeans-d-props": "act-lying-tee-jeans-d-props.83875d670f",
  "act-lying-tee-jeans-d-skin": "act-lying-tee-jeans-d-skin.f646f1ff50",
  "act-lying-tee-jeans-e-garment": "act-lying-tee-jeans-e-garment.b40fb42d1e",
  "act-lying-tee-jeans-e-props": "act-lying-tee-jeans-e-props.1a41d70995",
  "act-lying-tee-jeans-e-skin": "act-lying-tee-jeans-e-skin.c911f0b77b",
  "act-lying-tee-jeans-f-garment": "act-lying-tee-jeans-f-garment.e8fdbee10d",
  "act-lying-tee-jeans-f-props": "act-lying-tee-jeans-f-props.5de3fa13d2",
  "act-lying-tee-jeans-f-skin": "act-lying-tee-jeans-f-skin.b99b357d1e",
  "act-lying-tee-jeans-g-brows": "act-lying-tee-jeans-g-brows.b96a67c179",
  "act-lying-tee-jeans-g-garment": "act-lying-tee-jeans-g-garment.b7eb94eb41",
  "act-lying-tee-jeans-g-props": "act-lying-tee-jeans-g-props.8c7de18ca8",
  "act-lying-tee-jeans-g-skin": "act-lying-tee-jeans-g-skin.9742a68f8d",
  "act-lying-tee-jeans-h-garment": "act-lying-tee-jeans-h-garment.269ed5b297",
  "act-lying-tee-jeans-h-props": "act-lying-tee-jeans-h-props.383dc26b2a",
  "act-lying-tee-jeans-h-skin": "act-lying-tee-jeans-h-skin.c2b780f5e9",
  "act-lying-tee-shorts-a-garment": "act-lying-tee-shorts-a-garment.70bc09ea93",
  "act-lying-tee-shorts-a-props": "act-lying-tee-shorts-a-props.bbaa341607",
  "act-lying-tee-shorts-a-skin": "act-lying-tee-shorts-a-skin.c26cece988",
  "act-lying-tee-shorts-b-garment": "act-lying-tee-shorts-b-garment.3fa4b102be",
  "act-lying-tee-shorts-b-props": "act-lying-tee-shorts-b-props.f7097d4606",
  "act-lying-tee-shorts-b-skin": "act-lying-tee-shorts-b-skin.2f9f226c56",
  "act-lying-tee-shorts-c-garment": "act-lying-tee-shorts-c-garment.c85cc2ba5a",
  "act-lying-tee-shorts-c-props": "act-lying-tee-shorts-c-props.82a123e76f",
  "act-lying-tee-shorts-c-skin": "act-lying-tee-shorts-c-skin.3e0181c8fe",
  "act-lying-tee-shorts-d-garment": "act-lying-tee-shorts-d-garment.88d11091a3",
  "act-lying-tee-shorts-d-props": "act-lying-tee-shorts-d-props.9d99efb3d6",
  "act-lying-tee-shorts-d-skin": "act-lying-tee-shorts-d-skin.ac5fb94263",
  "act-lying-tee-shorts-e-garment": "act-lying-tee-shorts-e-garment.bec93dd583",
  "act-lying-tee-shorts-e-props": "act-lying-tee-shorts-e-props.036ba95454",
  "act-lying-tee-shorts-e-skin": "act-lying-tee-shorts-e-skin.52b859c602",
  "act-lying-tee-shorts-f-garment": "act-lying-tee-shorts-f-garment.631d9fd6c1",
  "act-lying-tee-shorts-f-props": "act-lying-tee-shorts-f-props.b1694e653f",
  "act-lying-tee-shorts-f-skin": "act-lying-tee-shorts-f-skin.cb6d1014a6",
  "act-lying-tee-shorts-g-garment": "act-lying-tee-shorts-g-garment.2fae93e90f",
  "act-lying-tee-shorts-g-props": "act-lying-tee-shorts-g-props.46ff9de1c8",
  "act-lying-tee-shorts-g-skin": "act-lying-tee-shorts-g-skin.0d514a7960",
  "act-lying-tee-shorts-h-garment": "act-lying-tee-shorts-h-garment.04cffcaed6",
  "act-lying-tee-shorts-h-props": "act-lying-tee-shorts-h-props.078affba90",
  "act-lying-tee-shorts-h-skin": "act-lying-tee-shorts-h-skin.f560427998",
  "act-standing-dress-a-brows": "act-standing-dress-a-brows.6fbf1a46a7",
  "act-standing-dress-a-garment": "act-standing-dress-a-garment.fdf0eea864",
  "act-standing-dress-a-props": "act-standing-dress-a-props.ccdbdf5d0d",
  "act-standing-dress-a-skin": "act-standing-dress-a-skin.10a337905c",
  "act-standing-dress-b-brows": "act-standing-dress-b-brows.3f4f986639",
  "act-standing-dress-b-garment": "act-standing-dress-b-garment.ed99676cc9",
  "act-standing-dress-b-props": "act-standing-dress-b-props.23cfb6f7a1",
  "act-standing-dress-b-skin": "act-standing-dress-b-skin.9aebe20317",
  "act-standing-dress-c-brows": "act-standing-dress-c-brows.ed4b01e883",
  "act-standing-dress-c-garment": "act-standing-dress-c-garment.a8ab3d8aba",
  "act-standing-dress-c-props": "act-standing-dress-c-props.bd1c6148cb",
  "act-standing-dress-c-skin": "act-standing-dress-c-skin.f1498b1510",
  "act-standing-dress-d-garment": "act-standing-dress-d-garment.949b9c8194",
  "act-standing-dress-d-props": "act-standing-dress-d-props.5ec5a3c411",
  "act-standing-dress-d-skin": "act-standing-dress-d-skin.ad8b822aaa",
  "act-standing-dress-e-brows": "act-standing-dress-e-brows.05e1ba14f3",
  "act-standing-dress-e-garment": "act-standing-dress-e-garment.58a2a990a7",
  "act-standing-dress-e-props": "act-standing-dress-e-props.66aa3095d9",
  "act-standing-dress-e-skin": "act-standing-dress-e-skin.d2c8120f2e",
  "act-standing-dress-f-garment": "act-standing-dress-f-garment.1e8a009647",
  "act-standing-dress-f-props": "act-standing-dress-f-props.0423f7b5b7",
  "act-standing-dress-f-skin": "act-standing-dress-f-skin.ad600d85f1",
  "act-standing-dress-g-garment": "act-standing-dress-g-garment.fd5babc2e5",
  "act-standing-dress-g-props": "act-standing-dress-g-props.183e9cdd72",
  "act-standing-dress-g-skin": "act-standing-dress-g-skin.f33049244d",
  "act-standing-dress-h-brows": "act-standing-dress-h-brows.6865967bad",
  "act-standing-dress-h-garment": "act-standing-dress-h-garment.96eccf4da5",
  "act-standing-dress-h-props": "act-standing-dress-h-props.f104a0bb25",
  "act-standing-dress-h-skin": "act-standing-dress-h-skin.e778a07961",
  "act-standing-hair-0": "act-standing-hair-0.36e2282c2b",
  "act-standing-hair-1": "act-standing-hair-1.4f1ac35af7",
  "act-standing-hair-2": "act-standing-hair-2.bf31c8db4a",
  "act-standing-hair-3": "act-standing-hair-3.9761bc05ba",
  "act-standing-hair-4": "act-standing-hair-4.f51d9e106d",
  "act-standing-hair-5": "act-standing-hair-5.9eaa90356b",
  "act-standing-hair-6": "act-standing-hair-6.488e9eaee8",
  "act-standing-hair-7": "act-standing-hair-7.0a2bed261e",
  "act-standing-hair-8": "act-standing-hair-8.750d818200",
  "act-standing-hair-9": "act-standing-hair-9.9086640e52",
  "act-standing-shirt-skirt-a-garment": "act-standing-shirt-skirt-a-garment.f6eff2839d",
  "act-standing-shirt-skirt-a-props": "act-standing-shirt-skirt-a-props.4fadf944d1",
  "act-standing-shirt-skirt-a-skin": "act-standing-shirt-skirt-a-skin.39c6b29267",
  "act-standing-shirt-skirt-b-garment": "act-standing-shirt-skirt-b-garment.4f19983028",
  "act-standing-shirt-skirt-b-props": "act-standing-shirt-skirt-b-props.d2b0b848d1",
  "act-standing-shirt-skirt-b-skin": "act-standing-shirt-skirt-b-skin.6a3732dede",
  "act-standing-shirt-skirt-c-garment": "act-standing-shirt-skirt-c-garment.2ebef055a6",
  "act-standing-shirt-skirt-c-props": "act-standing-shirt-skirt-c-props.39f97c6dbe",
  "act-standing-shirt-skirt-c-skin": "act-standing-shirt-skirt-c-skin.da43a854a6",
  "act-standing-shirt-skirt-d-garment": "act-standing-shirt-skirt-d-garment.60543ba421",
  "act-standing-shirt-skirt-d-props": "act-standing-shirt-skirt-d-props.81860a4e3b",
  "act-standing-shirt-skirt-d-skin": "act-standing-shirt-skirt-d-skin.0c4464e7c1",
  "act-standing-shirt-skirt-e-garment": "act-standing-shirt-skirt-e-garment.bf4fc182f5",
  "act-standing-shirt-skirt-e-props": "act-standing-shirt-skirt-e-props.fba8be1a0d",
  "act-standing-shirt-skirt-e-skin": "act-standing-shirt-skirt-e-skin.d5c100b226",
  "act-standing-shirt-skirt-f-garment": "act-standing-shirt-skirt-f-garment.b35bb7f011",
  "act-standing-shirt-skirt-f-props": "act-standing-shirt-skirt-f-props.d670270178",
  "act-standing-shirt-skirt-f-skin": "act-standing-shirt-skirt-f-skin.a0f34d775f",
  "act-standing-shirt-skirt-g-brows": "act-standing-shirt-skirt-g-brows.5cce0f7751",
  "act-standing-shirt-skirt-g-garment": "act-standing-shirt-skirt-g-garment.7325152d0b",
  "act-standing-shirt-skirt-g-props": "act-standing-shirt-skirt-g-props.413334fd9b",
  "act-standing-shirt-skirt-g-skin": "act-standing-shirt-skirt-g-skin.4c7f5ff86e",
  "act-standing-shirt-skirt-h-garment": "act-standing-shirt-skirt-h-garment.ded747f3bf",
  "act-standing-shirt-skirt-h-props": "act-standing-shirt-skirt-h-props.96cabc906c",
  "act-standing-shirt-skirt-h-skin": "act-standing-shirt-skirt-h-skin.5266d55c1e",
  "act-standing-tee-jeans-a-garment": "act-standing-tee-jeans-a-garment.add1ead65f",
  "act-standing-tee-jeans-a-props": "act-standing-tee-jeans-a-props.5330460ed0",
  "act-standing-tee-jeans-a-skin": "act-standing-tee-jeans-a-skin.c918186a3e",
  "act-standing-tee-jeans-b-garment": "act-standing-tee-jeans-b-garment.18e60ab974",
  "act-standing-tee-jeans-b-props": "act-standing-tee-jeans-b-props.ec198337fe",
  "act-standing-tee-jeans-b-skin": "act-standing-tee-jeans-b-skin.12808cd2bc",
  "act-standing-tee-jeans-c-garment": "act-standing-tee-jeans-c-garment.35d03b0ed7",
  "act-standing-tee-jeans-c-props": "act-standing-tee-jeans-c-props.5d523a700c",
  "act-standing-tee-jeans-c-skin": "act-standing-tee-jeans-c-skin.aac1931e32",
  "act-standing-tee-jeans-d-garment": "act-standing-tee-jeans-d-garment.5a55cada0f",
  "act-standing-tee-jeans-d-props": "act-standing-tee-jeans-d-props.eebce40940",
  "act-standing-tee-jeans-d-skin": "act-standing-tee-jeans-d-skin.c0a1bf2138",
  "act-standing-tee-jeans-e-garment": "act-standing-tee-jeans-e-garment.32d94b577d",
  "act-standing-tee-jeans-e-props": "act-standing-tee-jeans-e-props.109f8d99b1",
  "act-standing-tee-jeans-e-skin": "act-standing-tee-jeans-e-skin.c35fe7d8ec",
  "act-standing-tee-jeans-f-garment": "act-standing-tee-jeans-f-garment.04f3fe8a6a",
  "act-standing-tee-jeans-f-props": "act-standing-tee-jeans-f-props.1b58b549a0",
  "act-standing-tee-jeans-f-skin": "act-standing-tee-jeans-f-skin.3f167541eb",
  "act-standing-tee-jeans-g-garment": "act-standing-tee-jeans-g-garment.38192d8bb1",
  "act-standing-tee-jeans-g-props": "act-standing-tee-jeans-g-props.6730d83307",
  "act-standing-tee-jeans-g-skin": "act-standing-tee-jeans-g-skin.fc01ce5fd0",
  "act-standing-tee-jeans-h-garment": "act-standing-tee-jeans-h-garment.5d61311c77",
  "act-standing-tee-jeans-h-props": "act-standing-tee-jeans-h-props.38b1ebc751",
  "act-standing-tee-jeans-h-skin": "act-standing-tee-jeans-h-skin.f47b240869",
  "act-standing-tee-shorts-a-garment": "act-standing-tee-shorts-a-garment.74f5ca414c",
  "act-standing-tee-shorts-a-props": "act-standing-tee-shorts-a-props.9089c322c8",
  "act-standing-tee-shorts-a-skin": "act-standing-tee-shorts-a-skin.1c74cc676f",
  "act-standing-tee-shorts-b-brows": "act-standing-tee-shorts-b-brows.c88a531805",
  "act-standing-tee-shorts-b-garment": "act-standing-tee-shorts-b-garment.64a16fd4bd",
  "act-standing-tee-shorts-b-props": "act-standing-tee-shorts-b-props.b9aee8396d",
  "act-standing-tee-shorts-b-skin": "act-standing-tee-shorts-b-skin.13a4158dda",
  "act-standing-tee-shorts-c-garment": "act-standing-tee-shorts-c-garment.b4001ad9ed",
  "act-standing-tee-shorts-c-props": "act-standing-tee-shorts-c-props.ef681b1302",
  "act-standing-tee-shorts-c-skin": "act-standing-tee-shorts-c-skin.59c0ff01d8",
  "act-standing-tee-shorts-d-brows": "act-standing-tee-shorts-d-brows.1c447e4502",
  "act-standing-tee-shorts-d-garment": "act-standing-tee-shorts-d-garment.49dd832535",
  "act-standing-tee-shorts-d-props": "act-standing-tee-shorts-d-props.89c695e8b7",
  "act-standing-tee-shorts-d-skin": "act-standing-tee-shorts-d-skin.ef4c721cfb",
  "act-standing-tee-shorts-e-brows": "act-standing-tee-shorts-e-brows.7d5961455b",
  "act-standing-tee-shorts-e-garment": "act-standing-tee-shorts-e-garment.9f75363bb9",
  "act-standing-tee-shorts-e-props": "act-standing-tee-shorts-e-props.f0d412b056",
  "act-standing-tee-shorts-e-skin": "act-standing-tee-shorts-e-skin.8b60398461",
  "act-standing-tee-shorts-f-garment": "act-standing-tee-shorts-f-garment.ebe53359ca",
  "act-standing-tee-shorts-f-props": "act-standing-tee-shorts-f-props.88ed3b20b5",
  "act-standing-tee-shorts-f-skin": "act-standing-tee-shorts-f-skin.e31f0ae443",
  "act-standing-tee-shorts-g-garment": "act-standing-tee-shorts-g-garment.6f29b612b5",
  "act-standing-tee-shorts-g-props": "act-standing-tee-shorts-g-props.28d19d5306",
  "act-standing-tee-shorts-g-skin": "act-standing-tee-shorts-g-skin.9774ffd86c",
  "act-standing-tee-shorts-h-garment": "act-standing-tee-shorts-h-garment.5b1cf8c8bb",
  "act-standing-tee-shorts-h-props": "act-standing-tee-shorts-h-props.77dd758a65",
  "act-standing-tee-shorts-h-skin": "act-standing-tee-shorts-h-skin.154e03aa4f",
  "builder-grove-v6": "builder-grove-v6.f583276216",
  "celestial-v21": "celestial-v21.1869bff49b",
  "cottage-barren-v2": "cottage-barren-v2.34ff7a60f0",
  "cottage-growing-v2": "cottage-growing-v2.2ba5c0ad17",
  "cottage-night-barren-v2": "cottage-night-barren-v2.fd5ee3c173",
  "cottage-night-growing-v2": "cottage-night-growing-v2.518296a860",
  "cottage-night-plentiful-v2": "cottage-night-plentiful-v2.9185c202c3",
  "cottage-night-recovering-v2": "cottage-night-recovering-v2.e112c6337a",
  "cottage-night-thriving-v2": "cottage-night-thriving-v2.d17fdb12b5",
  "cottage-plentiful-v2": "cottage-plentiful-v2.18995f5386",
  "cottage-portrait-barren-v2": "cottage-portrait-barren-v2.609275ec00",
  "cottage-portrait-growing-v2": "cottage-portrait-growing-v2.410a511d3d",
  "cottage-portrait-night-barren-v2": "cottage-portrait-night-barren-v2.a68d8e740d",
  "cottage-portrait-night-growing-v2": "cottage-portrait-night-growing-v2.3d10f6060e",
  "cottage-portrait-night-plentiful-v2": "cottage-portrait-night-plentiful-v2.5cf5a2e4d9",
  "cottage-portrait-night-recovering-v2": "cottage-portrait-night-recovering-v2.669f6bb230",
  "cottage-portrait-night-thriving-v2": "cottage-portrait-night-thriving-v2.293e3d7727",
  "cottage-portrait-plentiful-v2": "cottage-portrait-plentiful-v2.db076ef4f4",
  "cottage-portrait-recovering-v2": "cottage-portrait-recovering-v2.566d72aed6",
  "cottage-portrait-thriving-v2": "cottage-portrait-thriving-v2.66dfd2a678",
  "cottage-recovering-v2": "cottage-recovering-v2.33b6abcf65",
  "cottage-thriving-v2": "cottage-thriving-v2.7db7366b18",
  "habitat-actions-v13": "habitat-actions-v13.c05d203dbe",
  "idle-bat-v21": "idle-bat-v21.4a0bcd0749",
  "idle-bird-v26": "idle-bird-v26.23703ab306",
  "idle-butterfly-v26": "idle-butterfly-v26.e75d229dd3",
  "idle-deer-v21": "idle-deer-v21.0adb78bf5f",
  "idle-duck-v21": "idle-duck-v21.5f94e09836",
  "idle-fox-v21": "idle-fox-v21.e222a0e492",
  "idle-frog-v21": "idle-frog-v21.a1d2a78dc1",
  "idle-habitat-v6": "idle-habitat-v6.55ac70766e",
  "idle-hedgehog-v21": "idle-hedgehog-v21.3f54ecce9f",
  "idle-moth-v26": "idle-moth-v26.585e223f2d",
  "idle-night-v6": "idle-night-v6.f2ac1e254e",
  "idle-owl-v26": "idle-owl-v26.55b2ac521f",
  "idle-rabbit-v21": "idle-rabbit-v21.92e35528d4",
  "idle-raccoon-v21": "idle-raccoon-v21.996a141160",
  "idle-squirrel-v21": "idle-squirrel-v21.5d8fa4f293",
  "idle-woodland-v6": "idle-woodland-v6.19a90ce423",
  "meadow-flight-v13": "meadow-flight-v13.b4a6ae45f2",
  "night-moves-v13": "night-moves-v13.a0d7ce43d1",
  "travel-deer-v20": "travel-deer-v20.014478ceea",
  "travel-hedgehog-v20": "travel-hedgehog-v20.aeae44beae",
  "travel-raccoon-v20": "travel-raccoon-v20.2ab0a82bf0",
  "travel-squirrel-v19": "travel-squirrel-v19.a0de10f097",
  "turn-bat-v26": "turn-bat-v26.a2c12cd966",
  "turn-bird-v26": "turn-bird-v26.c6f5da8ccc",
  "turn-butterfly-v26": "turn-butterfly-v26.c8e5b22aba",
  "turn-deer-v21": "turn-deer-v21.f21261355e",
  "turn-duck-v21": "turn-duck-v21.503fb38494",
  "turn-fox-v21": "turn-fox-v21.9a39b4ad87",
  "turn-frog-v21": "turn-frog-v21.6e7ec04c18",
  "turn-habitat-v13": "turn-habitat-v13.4111e70c4e",
  "turn-hedgehog-v21": "turn-hedgehog-v21.09171f977d",
  "turn-meadow-v13": "turn-meadow-v13.cf4d4959d6",
  "turn-moth-v26": "turn-moth-v26.4169faf4fc",
  "turn-night-v13": "turn-night-v13.0f041fc8e8",
  "turn-owl-v26": "turn-owl-v26.0fa327611d",
  "turn-rabbit-v21": "turn-rabbit-v21.c338a3055b",
  "turn-raccoon-v21": "turn-raccoon-v21.f78a88721e",
  "turn-squirrel-v21": "turn-squirrel-v21.a7f180d826",
  "turn-woodland-v13": "turn-woodland-v13.406ebd75d5",
  "valley-barren-v13": "valley-barren-v13.4587c18d46",
  "valley-growing-v13": "valley-growing-v13.c63f3c2a26",
  "valley-night-barren-v13": "valley-night-barren-v13.f2f055b961",
  "valley-night-growing-v13": "valley-night-growing-v13.f3158bd088",
  "valley-night-plentiful-v13": "valley-night-plentiful-v13.5ab9f81ac6",
  "valley-night-recovering-v13": "valley-night-recovering-v13.64dc8bc60c",
  "valley-night-thriving-v13": "valley-night-thriving-v13.bb0c21621f",
  "valley-plentiful-v18": "valley-plentiful-v18.c74e1a962b",
  "valley-portrait-barren-v13": "valley-portrait-barren-v13.c902e2dc9b",
  "valley-portrait-growing-v13": "valley-portrait-growing-v13.86b9cc5541",
  "valley-portrait-night-barren-v13": "valley-portrait-night-barren-v13.98f528d0d0",
  "valley-portrait-night-growing-v13": "valley-portrait-night-growing-v13.7ceb62bed7",
  "valley-portrait-night-plentiful-v13": "valley-portrait-night-plentiful-v13.a4b2fbed4a",
  "valley-portrait-night-recovering-v13": "valley-portrait-night-recovering-v13.1785a71b9f",
  "valley-portrait-night-thriving-v13": "valley-portrait-night-thriving-v13.11b642f9c1",
  "valley-portrait-plentiful-v18": "valley-portrait-plentiful-v18.c19d210fcc",
  "valley-portrait-recovering-v13": "valley-portrait-recovering-v13.9d004b9b33",
  "valley-portrait-thriving-v13": "valley-portrait-thriving-v13.b845d5ebe2",
  "valley-recovering-v13": "valley-recovering-v13.5171bd820c",
  "valley-thriving-v13": "valley-thriving-v13.989ead4d30",
  "woodland-actions-v13": "woodland-actions-v13.75f6d80556",
};
